import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n";
import { api, API } from "../api";
import TwoFactorWizard from "../components/TwoFactorWizard";
import { CloudCopytradingTab } from "./admin/CloudCopytradingTab";
import useIdleLogout from "../hooks/useIdleLogout";
import IdleLogoutWarning from "../components/IdleLogoutWarning";
import {
  LogOut, Upload, RefreshCw, Loader2, Download, Search,
  TrendingUp, ShoppingBag, CheckCircle2, XCircle, Send, KeyRound, Lock, Save, Clock,
  ChevronDown, ChevronRight, FileSpreadsheet, FileCode, Mail, Loader, Copy,
  ShieldCheck, ShieldOff, Smartphone, Tag, Wallet, Users, Eye, EyeOff, BookOpen,
  MessageCircle, Trash2, Activity, Sparkles, Paperclip, FileText, BellRing, UserRound,
  Settings, LifeBuoy, Cloud,
} from "lucide-react";

const TOKEN_KEY = "dtsp_admin_token";

const STATUSES = [
  "pending_payment", "pending_form", "pending_compilation",
  "compiled", "delivered", "failed", "submitted",
];

export default function Admin() {
  const { t } = useI18n();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPwd, setLoginPwd] = useState("");
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // filter state
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // settings state
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdMsg, setPwdMsg] = useState({ kind: "", text: "" });
  const [tgMsg, setTgMsg] = useState({ kind: "", text: "" });

  // Mon alias chat (visible aux visiteurs)
  const [aliasDraft, setAliasDraft] = useState("");
  const [aliasBusy, setAliasBusy] = useState(false);
  const [aliasMsg, setAliasMsg] = useState({ kind: "", text: "" });
  const saveAlias = async () => {
    setAliasMsg({ kind: "", text: "" });
    setAliasBusy(true);
    try {
      const v = (aliasDraft || "").trim();
      const { data } = await api.put("/admin/me/chat-alias", { chat_alias: v }, headers);
      setMe((m) => m ? { ...m, chat_alias: data.chat_alias } : m);
      setAliasMsg({ kind: "ok", text: data.chat_alias ? `✓ Alias enregistré : ${data.chat_alias}` : "✓ Alias effacé (le nom complet sera utilisé)" });
    } catch (e) {
      setAliasMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setAliasBusy(false); }
  };

  // expanded rows for timeline
  const [expanded, setExpanded] = useState(() => new Set());
  const [sendingId, setSendingId] = useState(null);

  // Admin user management (superadmin)
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [newInviteRole, setNewInviteRole] = useState("assistant");
  const [lastInviteUrl, setLastInviteUrl] = useState("");

  // ─── 2FA state ──────────────────────────────────────────
  // login flow: { stage: "2fa"|"enroll", challenge_token?, enroll_token?, method?, email_masked? }
  const [twofaChallenge, setTwofaChallenge] = useState(null);
  const [twofaCode, setTwofaCode] = useState("");
  const [twofaBusy, setTwofaBusy] = useState(false);
  const [twofaErr, setTwofaErr] = useState("");
  const [twofaResendMsg, setTwofaResendMsg] = useState("");
  // Settings tab: show wizard (voluntary config)
  const [showTwofaWizard, setShowTwofaWizard] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [twofaSettingsMsg, setTwofaSettingsMsg] = useState({ kind: "", text: "" });
  // Password visibility toggle on login form
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  // Passwordless (email code) login flow
  //   stage: null | "request" | "verify"  |  ok / err / cooldown message
  const [plessStage, setPlessStage] = useState(null);
  const [plessEmail, setPlessEmail] = useState("");
  const [plessCode, setPlessCode] = useState("");
  const [plessChallenge, setPlessChallenge] = useState("");
  const [plessMsg, setPlessMsg] = useState({ kind: "", text: "" });
  const [plessBusy, setPlessBusy] = useState(false);
  const [plessResendCooldown, setPlessResendCooldown] = useState(0);
  // Password reset flow (mot de passe oublié)
  //   stage: null | "request" | "verify" | "done"
  const [pwrStage, setPwrStage] = useState(null);
  const [pwrEmail, setPwrEmail] = useState("");
  const [pwrCode, setPwrCode] = useState("");
  const [pwrChallenge, setPwrChallenge] = useState("");
  const [pwrNew, setPwrNew] = useState("");
  const [pwrConfirm2, setPwrConfirm2] = useState("");
  const [pwrShow, setPwrShow] = useState(false);
  const [pwrMsg, setPwrMsg] = useState({ kind: "", text: "" });
  const [pwrBusy, setPwrBusy] = useState(false);

  // Affiliate management
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [affStatusFilter, setAffStatusFilter] = useState("pending");

  // Team performance (superadmin only)
  const [teamStats, setTeamStats] = useState(null);
  const [teamPeriod, setTeamPeriod] = useState("month");
  const [teamCustomStart, setTeamCustomStart] = useState("");
  const [teamCustomEnd, setTeamCustomEnd] = useState("");
  const [teamBusy, setTeamBusy] = useState(false);

  async function loadTeamStats(period = teamPeriod, start = teamCustomStart, end = teamCustomEnd) {
    if (me?.role !== "superadmin") return;
    setTeamBusy(true);
    try {
      const params = { period };
      if (period === "custom") { params.start = start; params.end = end; }
      const { data } = await api.get("/admin/analytics/team", { params, ...headers });
      setTeamStats(data);
    } catch (e) {
      // best-effort: leave previous data, surface in console
      console.warn("team analytics failed", e);
    } finally { setTeamBusy(false); }
  }

  // License number change
  const [licLookupNum, setLicLookupNum] = useState("");
  const [licOrder, setLicOrder] = useState(null);
  const [licOldNum, setLicOldNum] = useState("");
  const [licNewNum, setLicNewNum] = useState("");
  const [licNewEmail, setLicNewEmail] = useState("");
  const [licReason, setLicReason] = useState("");
  const [licForce, setLicForce] = useState(false);
  const [licBusy, setLicBusy] = useState(false);
  const [licMsg, setLicMsg] = useState({ kind: "", text: "" });
  // License reissue (lost .ex5)
  const [licReissueEmail, setLicReissueEmail] = useState("");
  const [licReissueReason, setLicReissueReason] = useState("");
  const [licReissueBusy, setLicReissueBusy] = useState(false);
  const [licReissueMsg, setLicReissueMsg] = useState({ kind: "", text: "" });
  // Active action selector (after lookup)
  const [licAction, setLicAction] = useState("");

  async function lookupLicense() {
    setLicMsg({ kind: "", text: "" });
    setLicOrder(null);
    const q = licLookupNum.trim();
    if (!q) return;
    setLicBusy(true);
    try {
      const { data } = await api.get(`/admin/license/lookup`, {
        params: { account: q }, ...headers,
      });
      setLicOrder(data);
      setLicNewEmail(data.email || "");
      setLicReissueEmail(data.email || "");
      setLicNewNum("");
      setLicOldNum(q);  // default: the searched account
      setLicReason("");
      setLicForce(false);
      setLicReissueReason("");
      setLicReissueMsg({ kind: "", text: "" });
      setLicMsg({ kind: "", text: "" });
      setLicAction("");
    } catch (e) {
      setLicMsg({
        kind: "err",
        text: e?.response?.data?.detail || "Aucune commande trouvée.",
      });
    } finally { setLicBusy(false); }
  }

  async function submitLicenseReissue() {
    if (!licOrder) return;
    if (licOrder.is_expired) {
      setLicReissueMsg({
        kind: "err",
        text: "Licence expirée : impossible de réémettre. Le client doit renouveler son abonnement.",
      });
      return;
    }
    if (!window.confirm(
      `Confirmer la réémission de la licence ${licOrder.order_id?.slice(0, 8)} ?\n\n`
      + `Comptes MT5 préservés : ${(licOrder.mt5_accounts || []).join(", ")}\n`
      + `Date d'expiration verrouillée : ${(licOrder.license_expiry || "").slice(0, 10) || "à confirmer"}\n\n`
      + `La commande passera en "pending_compilation" et un email sera envoyé au client.`
    )) return;
    setLicReissueBusy(true);
    setLicReissueMsg({ kind: "", text: "" });
    try {
      const payload = {
        order_id: licOrder.order_id,
        new_email: (licReissueEmail.trim() !== (licOrder.email || "")) ? licReissueEmail.trim() : null,
        reason: licReissueReason.trim() || null,
      };
      await api.post(`/admin/license/reissue`, payload, headers);
      setLicReissueMsg({
        kind: "ok",
        text: `✓ Réémission enregistrée. La date d'expiration (${(licOrder.license_expiry || "").slice(0,10)}) est verrouillée. Uploadez le nouveau .ex5 depuis l'onglet Commandes.`,
      });
      // re-fetch
      const { data } = await api.get(`/admin/license/lookup`, {
        params: { account: licLookupNum }, ...headers,
      });
      setLicOrder(data);
      setLicReissueReason("");
    } catch (e) {
      setLicReissueMsg({
        kind: "err",
        text: e?.response?.data?.detail || "Erreur lors de la réémission.",
      });
    } finally { setLicReissueBusy(false); }
  }
  async function submitLicenseChange() {
    if (!licOrder) return;
    if (!licOldNum.trim()) {
      setLicMsg({ kind: "err", text: "Sélectionnez l'ancien numéro à remplacer." });
      return;
    }
    if (!licNewNum.trim() || licNewNum.trim() === licOldNum.trim()) {
      setLicMsg({ kind: "err", text: "Saisissez un nouveau numéro différent." });
      return;
    }
    setLicBusy(true);
    setLicMsg({ kind: "", text: "" });
    try {
      const payload = {
        order_id: licOrder.order_id,
        old_account: licOldNum.trim(),
        new_account: licNewNum.trim(),
        new_email: (licNewEmail.trim() !== (licOrder.email || "")) ? licNewEmail.trim() : null,
        force: !!licForce,
        reason: licReason.trim() || null,
      };
      await api.post(`/admin/license/change`, payload, headers);
      setLicMsg({
        kind: "ok",
        text: `✓ Changement enregistré (${licOldNum.trim()} → ${licNewNum.trim()}). Commande passée à « pending_compilation ». Uploadez le nouveau .ex5 depuis l'onglet Commandes pour déclencher l'envoi.`,
      });
      // re-fetch with the new account
      setLicLookupNum(licNewNum.trim());
      const { data } = await api.get(`/admin/license/lookup`, {
        params: { account: licNewNum.trim() }, ...headers,
      });
      setLicOrder(data);
      setLicOldNum(licNewNum.trim());
      setLicNewNum("");
    } catch (e) {
      setLicMsg({
        kind: "err",
        text: e?.response?.data?.detail || "Erreur lors du changement.",
      });
    } finally { setLicBusy(false); }
  }

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const headers = { headers: { "x-admin-token": token } };

  const loadAll = async () => {
    if (!token) return;
    setBusy(true); setErr("");
    try {
      const meRes = await api.get("/admin/me", headers);
      setMe(meRes.data);
      setAliasDraft((meRes.data?.chat_alias || "").trim());
      const promises = [
        api.get("/admin/orders", headers),
        api.get("/admin/contacts", headers),
        api.get("/admin/analytics", headers),
    api.get("/admin/tickets-stats?days=30", headers).catch(() => null),
        api.get("/admin/affiliates", headers),
      ];
      if (meRes.data?.role === "superadmin") {
        promises.push(api.get("/admin/users", headers));
        promises.push(api.get("/admin/invitations", headers));
      }
      const results = await Promise.all(promises);
      setOrders(results[0].data);
      setContacts(results[1].data); setAnalytics(results[2].data);
      setTicketStats(results[3]?.data || null);
      setAffiliates(results[4].data || []);
      if (meRes.data?.role === "superadmin") {
        setUsers(results[5].data || []);
        setInvites(results[6].data || []);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(""); setMe(null);
        setErr("Session expirée");
      } else if (!e.response) {
        setErr("Impossible de joindre le serveur. Vérifiez votre connexion.");
      } else {
        setErr(`Erreur ${e.response.status} lors du chargement — réessayez.`);
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadAll(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Contacts: auto-refresh + reply modal state ────────────────────────
  const reloadContacts = async () => {
    if (!token) return;
    try {
      const r = await api.get("/admin/contacts", headers);
      setContacts(r.data || []);
    } catch { /* ignore network blip */ }
  };
  useEffect(() => {
    if (tab !== "contacts" || !token) return undefined;
    // Immediate refresh on tab open + every 15s thereafter
    reloadContacts();
    const id = setInterval(reloadContacts, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [tab, token]);

  const [contactReplyOpen, setContactReplyOpen] = useState(null); // contact id
  const [contactReplyBody, setContactReplyBody] = useState("");
  const [contactReplyAttachments, setContactReplyAttachments] = useState([]); // [{filename, mime_type, base64, size}]
  const [contactReplyBusy, setContactReplyBusy] = useState(false);
  const [contactReplyMsg, setContactReplyMsg] = useState({ kind: "", text: "" });
  const openContactReply = (contactId) => {
    setContactReplyOpen(contactId);
    setContactReplyBody("");
    setContactReplyAttachments([]);
    setContactReplyMsg({ kind: "", text: "" });
  };
  const closeContactReply = () => {
    if (contactReplyBusy) return;
    setContactReplyOpen(null);
    setContactReplyBody("");
    setContactReplyAttachments([]);
    setContactReplyMsg({ kind: "", text: "" });
  };
  const onContactAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const limit = 20 * 1024 * 1024; // 20MB total
    let total = contactReplyAttachments.reduce((s, a) => s + (a.size || 0), 0);
    const newOnes = [];
    for (const f of files) {
      total += f.size;
      if (total > limit) {
        setContactReplyMsg({ kind: "err", text: "Taille totale > 20 Mo. Pièce jointe ignorée." });
        break;
      }
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => {
          const s = String(r.result || "");
          res(s.split(",")[1] || "");
        };
        r.readAsDataURL(f);
      });
      newOnes.push({ filename: f.name, mime_type: f.type || "application/octet-stream", base64: b64, size: f.size });
    }
    setContactReplyAttachments((arr) => [...arr, ...newOnes]);
  };
  const sendContactReply = async () => {
    if (!contactReplyOpen) return;
    if (!contactReplyBody.trim()) {
      setContactReplyMsg({ kind: "err", text: "Le message ne peut pas être vide." });
      return;
    }
    setContactReplyBusy(true); setContactReplyMsg({ kind: "", text: "" });
    try {
      await api.post(
        `/admin/contacts/${contactReplyOpen}/reply`,
        { body: contactReplyBody.trim(), attachments: contactReplyAttachments.map((a) => ({ filename: a.filename, mime_type: a.mime_type, base64: a.base64 })) },
        headers,
      );
      setContactReplyMsg({ kind: "ok", text: "✓ Réponse envoyée par email." });
      await reloadContacts();
      // Auto-close after a short delay
      setTimeout(() => { setContactReplyOpen(null); setContactReplyBody(""); setContactReplyAttachments([]); setContactReplyMsg({ kind: "", text: "" }); }, 1200);
    } catch (e) {
      setContactReplyMsg({ kind: "err", text: e.response?.data?.detail || "Erreur lors de l'envoi." });
    } finally { setContactReplyBusy(false); }
  };

  const login = async (e) => {
    e.preventDefault();
    setErr("");
    const fd = new FormData();
    fd.append("email", loginEmail.trim());
    fd.append("password", loginPwd);
    try {
      const { data } = await api.post("/admin/login", fd);
      if (data.requires_2fa) {
        setTwofaChallenge({
          stage: "2fa",
          challenge_token: data.challenge_token,
          method: data.method,
          email_masked: data.email_masked,
        });
        setLoginPwd("");
        return;
      }
      if (data.requires_enrollment) {
        setTwofaChallenge({ stage: "enroll", enroll_token: data.enroll_token, role: data.role });
        setLoginPwd("");
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMe({ email: data.email, role: data.role });
      setLoginEmail(""); setLoginPwd("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Identifiants invalides");
    }
  };

  const verify2FA = async (e) => {
    e.preventDefault();
    setTwofaErr(""); setTwofaBusy(true);
    try {
      const { data } = await api.post("/admin/login/verify-2fa", {
        challenge_token: twofaChallenge.challenge_token,
        code: twofaCode.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMe({ email: data.email, role: data.role });
      setTwofaChallenge(null); setTwofaCode(""); setLoginEmail("");
    } catch (e2) {
      setTwofaErr(e2.response?.data?.detail || "Code invalide");
    } finally {
      setTwofaBusy(false);
    }
  };

  const resendEmailCode = async () => {
    setTwofaResendMsg(""); setTwofaErr("");
    try {
      await api.post("/admin/login/resend-email-code", { challenge_token: twofaChallenge.challenge_token });
      setTwofaResendMsg("Nouveau code envoyé ✓");
    } catch (e2) {
      setTwofaErr(e2.response?.data?.detail || "Échec du renvoi");
    }
  };

  const cancelTwoFactor = () => {
    setTwofaChallenge(null); setTwofaCode(""); setTwofaErr(""); setTwofaResendMsg("");
  };

  // ─── Passwordless (email code) login ─────────────────────
  const PLESS_RESEND_COOLDOWN_SEC = 60;

  useEffect(() => {
    if (plessResendCooldown <= 0) return;
    const tid = setInterval(() => {
      setPlessResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(tid);
  }, [plessResendCooldown]);

  const requestPasswordless = async (e) => {
    if (e) e.preventDefault();
    setPlessMsg({ kind: "", text: "" });
    if (!plessEmail.trim()) {
      setPlessMsg({ kind: "err", text: "Email requis" });
      return;
    }
    setPlessBusy(true);
    try {
      const { data } = await api.post("/admin/login/passwordless/request", { email: plessEmail.trim() });
      if (data?.challenge_token) {
        setPlessChallenge(data.challenge_token);
        setPlessStage("verify");
        setPlessMsg({ kind: "ok", text: data.message || "Code envoyé — vérifiez votre boîte email." });
        setPlessResendCooldown(PLESS_RESEND_COOLDOWN_SEC);
      } else {
        // Anti-énumération : la réponse est identique même si l'email n'existe pas
        setPlessMsg({ kind: "ok", text: data?.message || "Si l'email correspond à un compte, un code vient d'être envoyé." });
      }
    } catch (e2) {
      setPlessMsg({ kind: "err", text: e2.response?.data?.detail || "Erreur — réessayez dans un instant." });
    } finally {
      setPlessBusy(false);
    }
  };

  const resendPasswordlessCode = async () => {
    if (plessResendCooldown > 0 || plessBusy) return;
    setPlessMsg({ kind: "", text: "" });
    setPlessBusy(true);
    try {
      const { data } = await api.post("/admin/login/passwordless/request", { email: plessEmail.trim() });
      if (data?.challenge_token) {
        setPlessChallenge(data.challenge_token);
        setPlessCode("");
      }
      setPlessMsg({ kind: "ok", text: "Nouveau code envoyé ✓" });
      setPlessResendCooldown(PLESS_RESEND_COOLDOWN_SEC);
    } catch (e2) {
      setPlessMsg({ kind: "err", text: e2.response?.data?.detail || "Échec du renvoi" });
    } finally {
      setPlessBusy(false);
    }
  };

  const verifyPasswordless = async (e) => {
    if (e) e.preventDefault();
    setPlessMsg({ kind: "", text: "" });
    if (!plessCode.trim() || !plessChallenge) return;
    setPlessBusy(true);
    try {
      const { data } = await api.post("/admin/login/passwordless/verify", {
        challenge_token: plessChallenge,
        code: plessCode.trim(),
      });
      if (data.requires_2fa) {
        // Chaîne vers TOTP 2FA
        setTwofaChallenge({
          stage: "2fa",
          challenge_token: data.challenge_token,
          method: data.method,
          email_masked: data.email_masked,
        });
        cancelPasswordless();
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMe({ email: data.email, role: data.role });
      cancelPasswordless();
    } catch (e2) {
      setPlessMsg({ kind: "err", text: e2.response?.data?.detail || "Code invalide" });
    } finally {
      setPlessBusy(false);
    }
  };

  // ─── Deployment diff banner ────────────────────────────
  const [deployDiff, setDeployDiff] = useState(null); // { preview_version, prod_version, in_sync, prod_reachable, prod_url }
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api.get("/admin/deployment-diff", headers).then(({ data }) => {
      if (!cancelled) setDeployDiff(data);
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadMasterMq5 = async () => {
    try {
      const res = await api.get("/admin/master-mq5", { ...headers, responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `DrTradeScalperPro_v${deployDiff?.preview_version || "latest"}.mq5`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur téléchargement du .mq5 maître : " + (e.response?.data?.detail || e.message));
    }
  };

  const cancelPasswordless = () => {
    setPlessStage(null); setPlessEmail(""); setPlessCode(""); setPlessChallenge("");
    setPlessMsg({ kind: "", text: "" }); setPlessBusy(false); setPlessResendCooldown(0);
  };

  // ─── Password reset (mot de passe oublié) ────────────────
  const requestPasswordReset = async (e) => {
    if (e) e.preventDefault();
    setPwrMsg({ kind: "", text: "" });
    if (!pwrEmail.trim()) {
      setPwrMsg({ kind: "err", text: "Email requis" });
      return;
    }
    setPwrBusy(true);
    try {
      const { data } = await api.post("/admin/password-reset/request", { email: pwrEmail.trim() });
      if (data?.challenge_token) {
        setPwrChallenge(data.challenge_token);
        setPwrStage("verify");
        setPwrMsg({ kind: "ok", text: data.message || "Code envoyé — vérifiez votre boîte email." });
      } else {
        setPwrMsg({ kind: "ok", text: data?.message || "Si l'email correspond à un compte, un code vient d'être envoyé." });
      }
    } catch (e2) {
      setPwrMsg({ kind: "err", text: e2.response?.data?.detail || "Erreur — réessayez dans un instant." });
    } finally {
      setPwrBusy(false);
    }
  };

  const confirmPasswordReset = async (e) => {
    if (e) e.preventDefault();
    setPwrMsg({ kind: "", text: "" });
    if (!pwrCode.trim() || !pwrChallenge) return;
    if (pwrNew.length < 8) {
      setPwrMsg({ kind: "err", text: "Le mot de passe doit faire au moins 8 caractères." });
      return;
    }
    if (pwrNew !== pwrConfirm2) {
      setPwrMsg({ kind: "err", text: "Les 2 mots de passe ne correspondent pas." });
      return;
    }
    setPwrBusy(true);
    try {
      const { data } = await api.post("/admin/password-reset/confirm", {
        challenge_token: pwrChallenge,
        code: pwrCode.trim(),
        new_password: pwrNew,
      });
      setPwrStage("done");
      setPwrMsg({ kind: "ok", text: data?.message || "Mot de passe mis à jour. Reconnectez-vous." });
    } catch (e2) {
      setPwrMsg({ kind: "err", text: e2.response?.data?.detail || "Erreur — code invalide ou expiré." });
    } finally {
      setPwrBusy(false);
    }
  };

  const cancelPasswordReset = () => {
    setPwrStage(null); setPwrEmail(""); setPwrCode(""); setPwrChallenge("");
    setPwrNew(""); setPwrConfirm2(""); setPwrShow(false);
    setPwrMsg({ kind: "", text: "" }); setPwrBusy(false);
  };

  const onEnrollComplete = (data) => {
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setMe({ email: data.email, role: data.role });
    }
    setTwofaChallenge(null);
  };

  const logout = async () => {
    try { await api.post("/admin/logout", null, headers); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    try { localStorage.removeItem("dtsp_admin_last_activity"); } catch {}
    setToken(""); setMe(null);
  };

  // Auto-logout après 60 min d'inactivité (avertissement 60 s avant)
  const {
    showWarning: idleWarn,
    remaining: idleRemaining,
    stayActive: idleStay,
  } = useIdleLogout({
    idleMs: 60 * 60 * 1000,
    warnMs: 60 * 1000,
    onLogout: logout,
  });

  const uploadEx5 = async (orderId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/orders/${orderId}/upload-ex5`, fd, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur upload");
    }
  };

  const setStatus = async (orderId, status) => {
    const fd = new FormData();
    fd.append("status", status);
    try {
      await api.post(`/admin/orders/${orderId}/status`, fd, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdMsg({ kind: "", text: "" });
    if (pwdNew !== pwdConfirm) {
      setPwdMsg({ kind: "error", text: "La confirmation ne correspond pas." });
      return;
    }
    if (pwdNew.length < 8) {
      setPwdMsg({ kind: "error", text: "Le nouveau mot de passe doit faire au moins 8 caractères." });
      return;
    }
    try {
      await api.post(
        "/admin/change-password",
        { current_password: pwdCurrent, new_password: pwdNew, confirm_password: pwdConfirm },
        headers,
      );
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      setPwdMsg({ kind: "ok", text: "Mot de passe mis à jour. Les autres sessions sont déconnectées." });
    } catch (e) {
      setPwdMsg({ kind: "error", text: e.response?.data?.detail || "Erreur lors du changement." });
    }
  };

  const registerTelegramWebhook = async () => {
    setTgMsg({ kind: "", text: "" });
    const base = window.location.origin;
    const fd = new FormData();
    fd.append("base_url", base);
    try {
      const { data } = await api.post("/admin/telegram/register-webhook", fd, headers);
      setTgMsg({ kind: "ok", text: `Webhook enregistré: ${data.webhook_url}` });
    } catch (e) {
      setTgMsg({ kind: "error", text: e.response?.data?.detail || "Échec — vérifiez TELEGRAM_BOT_TOKEN" });
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export.csv", {
        ...headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replaceAll("-", "");
      a.download = `orders_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Échec de l'export CSV");
    }
  };

  const downloadMq5 = async (orderId, filename) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}/download-mq5`, {
        ...headers,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `DrTradeScalperPro_${orderId.slice(0, 8)}.mq5`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.detail || "Échec du téléchargement .mq5");
    }
  };

  const sendDelivery = async (orderId) => {
    if (!window.confirm(
      "Envoyer maintenant .ex5 + Manuel + Guide d'installation (ZIP) au client ?\n" +
      "Le statut passera à 'delivered'."
    )) return;
    setSendingId(orderId);
    try {
      const { data } = await api.post(`/admin/orders/${orderId}/send-delivery`, null, headers);
      alert(`✅ Email envoyé à ${data.sent_to}\nArchive: ${data.zip}`);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Échec d'envoi");
    } finally {
      setSendingId(null);
    }
  };

  // ─── Admin user / invitation actions (superadmin) ─────────────────
  const [newInviteEmail, setNewInviteEmail] = useState("");
  const [newInviteName, setNewInviteName] = useState("");
  const [newInviteBusy, setNewInviteBusy] = useState(false);
  const [newInviteFeedback, setNewInviteFeedback] = useState({ kind: "", text: "" });

  const createInvitation = async () => {
    setNewInviteBusy(true);
    setNewInviteFeedback({ kind: "", text: "" });
    try {
      const { data } = await api.post("/admin/invitations",
        { role: newInviteRole }, headers);
      setLastInviteUrl(data.url);
      const targetEmail = newInviteEmail.trim();
      if (targetEmail) {
        try {
          await api.post(
            `/admin/invitations/${data.invitation.token}/send-email`,
            { email: targetEmail, recipient_name: newInviteName.trim() },
            headers,
          );
          setNewInviteFeedback({
            kind: "ok",
            text: `✓ Lien généré et envoyé à ${targetEmail}`,
          });
          setNewInviteEmail(""); setNewInviteName("");
        } catch (mailErr) {
          setNewInviteFeedback({
            kind: "warn",
            text: `Lien généré, mais l'envoi par email a échoué : ${mailErr.response?.data?.detail || mailErr.message || "erreur"}. Vous pouvez le copier ci-dessous ou réessayer plus bas.`,
          });
        }
      } else {
        setNewInviteFeedback({ kind: "ok", text: "✓ Lien généré (copiez-le ci-dessous)." });
      }
      loadAll();
    } catch (e) {
      setNewInviteFeedback({
        kind: "err",
        text: e.response?.data?.detail || "Échec création invitation",
      });
    } finally { setNewInviteBusy(false); }
  };

  const revokeInvitation = async (tk) => {
    if (!window.confirm("Révoquer cette invitation ?")) return;
    try {
      await api.delete(`/admin/invitations/${tk}`, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleUserActive = async (email, currentActive) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!currentActive));
      await api.post(`/admin/users/${encodeURIComponent(email)}/toggle-active`, fd, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`Supprimer définitivement l'accès de ${email} ?`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  const copyInviteUrl = () => {
    navigator.clipboard.writeText(lastInviteUrl).then(() => {
      alert("Lien copié !");
    });
  };

  // ─── 2FA actions (authenticated) ──────────────────────────
  const disable2FA = async (e) => {
    e.preventDefault();
    setTwofaSettingsMsg({ kind: "", text: "" });
    try {
      await api.post("/admin/2fa/disable", { password: disablePwd }, headers);
      setDisablePwd("");
      setTwofaSettingsMsg({ kind: "ok", text: "2FA désactivée." });
      loadAll();
    } catch (e2) {
      setTwofaSettingsMsg({ kind: "error", text: e2.response?.data?.detail || "Erreur" });
    }
  };

  const onWizardComplete = () => {
    setShowTwofaWizard(false);
    setTwofaSettingsMsg({ kind: "ok", text: "2FA configurée avec succès." });
    loadAll();
  };

  const resetUserTwoFactor = async (email) => {
    if (!window.confirm(`Réinitialiser la 2FA de ${email} ?\nCet utilisateur devra se reconnecter et ré-enrôler une nouvelle méthode.`)) return;
    try {
      await api.post(`/admin/users/${encodeURIComponent(email)}/reset-2fa`, null, headers);
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
  };

  // ─── Affiliate management ──────────────────────────────────
  const loadCommissions = async (status) => {
    try {
      const { data } = await api.get(
        `/admin/affiliates-commissions${status ? `?status=${status}` : ""}`,
        headers,
      );
      setCommissions(data || []);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const markCommissionPaid = async (commId) => {
    const ref = window.prompt("Référence de paiement (hash USDT ou ID mobile money) :");
    if (!ref || !ref.trim()) return;
    try {
      await api.post(`/admin/affiliates-commissions/${commId}/mark-paid`, { paid_ref: ref.trim() }, headers);
      loadCommissions(affStatusFilter);
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const toggleAffiliateActive = async (code, currentActive) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!currentActive));
      await api.post(`/admin/affiliates/${encodeURIComponent(code)}/toggle-active`, fd, headers);
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  // ─── Admin promo codes ─────────────────────────────────────
  const [adminPromos, setAdminPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({
    code: "", discount_percent: 10,
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    max_uses: "",
  });
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoErr, setPromoErr] = useState("");

  const loadAdminPromos = async () => {
    try {
      const { data } = await api.get("/admin/promos", headers);
      setAdminPromos(data || []);
    } catch { /* ignore */ }
  };

  const createAdminPromo = async (e) => {
    e.preventDefault(); setPromoErr("");
    try {
      await api.post("/admin/promos", {
        code: promoForm.code.trim().toUpperCase() || undefined,
        discount_percent: Number(promoForm.discount_percent),
        valid_from: new Date(promoForm.valid_from + "T00:00:00Z").toISOString(),
        valid_to: new Date(promoForm.valid_to + "T23:59:59Z").toISOString(),
        max_uses: promoForm.max_uses ? Number(promoForm.max_uses) : null,
      }, headers);
      setShowPromoForm(false);
      setPromoForm({ ...promoForm, code: "" });
      loadAdminPromos();
    } catch (e2) { setPromoErr(e2.response?.data?.detail || "Erreur"); }
  };

  const toggleAdminPromo = async (code, currentActive) => {
    try {
      const fd = new FormData();
      fd.append("active", String(!currentActive));
      await api.post(`/admin/promos/${encodeURIComponent(code)}/toggle-active`, fd, headers);
      loadAdminPromos();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const deleteAdminPromo = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try {
      await api.delete(`/admin/promos/${encodeURIComponent(code)}`, headers);
      loadAdminPromos();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  const deleteAffiliate = async (code) => {
    const reason = window.prompt(`Supprimer l'affilié ${code} ? Le compte sera notifié par email.\n\nMotif (facultatif) :`);
    if (reason === null) return;
    try {
      await api.delete(`/admin/affiliates/${encodeURIComponent(code)}`, { ...headers, data: { reason } });
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  // ─── Admin payouts ──────────────────────────────────────
  const [payouts, setPayouts] = useState([]);
  const [payoutFilter, setPayoutFilter] = useState("pending");
  const loadPayouts = async (status) => {
    try {
      const { data } = await api.get(`/admin/payouts${status ? `?status=${status}` : ""}`, headers);
      setPayouts(data || []);
    } catch { /* ignore */ }
  };
  const payoutAction = async (id, kind) => {
    try {
      if (kind === "process") {
        await api.post(`/admin/payouts/${id}/process`, null, headers);
      } else if (kind === "pay") {
        const ref = window.prompt("Référence de paiement (hash USDT ou ID mobile) :");
        if (!ref) return;
        await api.post(`/admin/payouts/${id}/mark-paid`, { paid_ref: ref.trim() }, headers);
      } else if (kind === "reject") {
        const reason = window.prompt("Motif du rejet :");
        if (!reason) return;
        await api.post(`/admin/payouts/${id}/reject`, { reason: reason.trim() }, headers);
      }
      loadPayouts(payoutFilter);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  // ─── Email templates ──────────────────────────────────
  const [emailTpls, setEmailTpls] = useState([]);
  const [editingTpl, setEditingTpl] = useState(null);
  const loadEmailTpls = async () => {
    try { const { data } = await api.get("/admin/email-templates", headers); setEmailTpls(data || []); }
    catch { /* ignore */ }
  };
  const saveEmailTpl = async () => {
    if (!editingTpl) return;
    try {
      await api.put(`/admin/email-templates/${encodeURIComponent(editingTpl.key)}`, {
        subject_fr: editingTpl.subject_fr, body_fr: editingTpl.body_fr,
        subject_en: editingTpl.subject_en, body_en: editingTpl.body_en,
      }, headers);
      setEditingTpl(null);
      loadEmailTpls();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };
  const resetEmailTpl = async (key) => {
    if (!window.confirm(`Réinitialiser le modèle ${key} aux valeurs par défaut ?`)) return;
    try { await api.post(`/admin/email-templates/${encodeURIComponent(key)}/reset`, null, headers); loadEmailTpls(); }
    catch (e) { alert(e.response?.data?.detail || "Erreur"); }
  };

  // ─── App settings ─────────────────────────────────────
  const [appSettings, setAppSettings] = useState({ payout_min_xof: 5000 });
  const [settingsDraft, setSettingsDraft] = useState("5000");
  const [settingsMsg, setSettingsMsg] = useState({ kind: "", text: "" });
  const loadAppSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings", headers);
      setAppSettings(data || {});
      setSettingsDraft(String(data?.payout_min_xof ?? 5000));
      setRenewalDraft({
        renewal_reminder_enabled: !!data?.renewal_reminder_enabled,
        renewal_reminder_days_before: data?.renewal_reminder_days_before ?? 7,
        renewal_discount_percent: data?.renewal_discount_percent ?? 20,
        renewal_discount_validity_days: data?.renewal_discount_validity_days ?? 14,
        renewal_email_subject: data?.renewal_email_subject ?? "",
        renewal_email_body: data?.renewal_email_body ?? "",
      });
      setAbandonDraft({
        abandonment_discount_percent: data?.abandonment_discount_percent ?? 10,
        abandonment_validity_days: data?.abandonment_validity_days ?? 7,
        abandonment_interval_1_min: data?.abandonment_interval_1_min ?? 30,
        abandonment_interval_2_min: data?.abandonment_interval_2_min ?? 1440,
        abandonment_interval_3_min: data?.abandonment_interval_3_min ?? 2880,
        abandonment_promo_starts_at_reminder: data?.abandonment_promo_starts_at_reminder ?? 3,
        abandonment_max_age_hours: data?.abandonment_max_age_hours ?? 168,
      });
      setChatTeamDraft({
        chat_assistants: Array.isArray(data?.chat_assistants) ? data.chat_assistants : [],
        chat_group_chat_id: data?.chat_group_chat_id ?? "",
        chat_rotation_enabled: !!data?.chat_rotation_enabled,
        chat_rotation_reclaim_minutes: data?.chat_rotation_reclaim_minutes ?? 2,
        chat_welcome_enabled: data?.chat_welcome_enabled !== false,
        chat_welcome_template: data?.chat_welcome_template ?? "Merci {first_name}, un opérateur va vous prendre en charge sous quelques instants.",
        chat_queue_enabled: data?.chat_queue_enabled !== false,
        chat_queue_interval_minutes: data?.chat_queue_interval_minutes ?? 2,
        chat_queue_template: data?.chat_queue_template ?? "Vous êtes le {position}{ord} dans la file d'attente. Merci de patienter, un conseiller arrive bientôt.",
        chat_assignment_enabled: data?.chat_assignment_enabled !== false,
        chat_assignment_template: data?.chat_assignment_template ?? "Bienvenue {first_name}, je suis {assistant_name}. Comment puis-je vous aider ?",
        chat_away_enabled: data?.chat_away_enabled !== false,
        chat_away_template: data?.chat_away_template ?? "Bonjour {first_name}, notre équipe support est actuellement indisponible. Laissez-nous votre numéro/email et un message — nous reviendrons vers vous dès que possible.",
        chat_inactivity_close_enabled: data?.chat_inactivity_close_enabled !== false,
        chat_inactivity_close_minutes: data?.chat_inactivity_close_minutes ?? 30,
        chat_inactivity_close_template: data?.chat_inactivity_close_template ?? "Conversation fermée pour inactivité ({minutes} min). N'hésitez pas à rouvrir un chat si vous avez besoin d'aide.",
      });
    } catch { /* ignore */ }
  };
  const saveAppSettings = async () => {
    setSettingsMsg({ kind: "", text: "" });
    try {
      const v = parseInt(settingsDraft, 10);
      if (isNaN(v) || v < 0) {
        setSettingsMsg({ kind: "err", text: "Valeur invalide (entier positif attendu)" });
        return;
      }
      const { data } = await api.put("/admin/settings", { payout_min_xof: v }, headers);
      setAppSettings(data.settings || {});
      setSettingsMsg({ kind: "ok", text: "Paramètres enregistrés." });
    } catch (e) {
      setSettingsMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    }
  };

  // ─── Renewal-reminder settings ─────────────────────────
  const [renewalDraft, setRenewalDraft] = useState({});
  const [renewalMsg, setRenewalMsg] = useState({ kind: "", text: "" });
  const [renewalRunMsg, setRenewalRunMsg] = useState({ kind: "", text: "" });
  const [renewalBusy, setRenewalBusy] = useState(false);

  const saveRenewalSettings = async () => {
    setRenewalMsg({ kind: "", text: "" });
    setRenewalBusy(true);
    try {
      const payload = {
        renewal_reminder_enabled: !!renewalDraft.renewal_reminder_enabled,
        renewal_reminder_days_before: parseInt(renewalDraft.renewal_reminder_days_before, 10),
        renewal_discount_percent: parseInt(renewalDraft.renewal_discount_percent, 10),
        renewal_discount_validity_days: parseInt(renewalDraft.renewal_discount_validity_days, 10),
        renewal_email_subject: renewalDraft.renewal_email_subject,
        renewal_email_body: renewalDraft.renewal_email_body,
      };
      const { data } = await api.put("/admin/settings", payload, headers);
      setAppSettings(data.settings || {});
      setRenewalMsg({ kind: "ok", text: "Configuration enregistrée." });
    } catch (e) {
      setRenewalMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setRenewalBusy(false); }
  };

  const runRenewalReminders = async () => {
    if (!window.confirm("Lancer le scan de relance maintenant ? Tous les clients dont la licence expire bientôt recevront un email avec un code de remise.")) return;
    setRenewalRunMsg({ kind: "", text: "" });
    setRenewalBusy(true);
    try {
      const { data } = await api.post("/admin/cron/renewal-reminders", {}, headers);
      if (data.ok) {
        setRenewalRunMsg({
          kind: "ok",
          text: `✓ ${data.sent} email(s) envoyé(s) · ${data.skipped} ignoré(s).`,
        });
      } else {
        setRenewalRunMsg({
          kind: "err",
          text: `Désactivé. Activez le toggle puis sauvegardez avant de relancer.`,
        });
      }
    } catch (e) {
      setRenewalRunMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setRenewalBusy(false); }
  };

  // ─── Abandoned-cart settings ───────────────────────────
  const [abandonDraft, setAbandonDraft] = useState({
    abandonment_discount_percent: 10,
    abandonment_validity_days: 7,
    abandonment_interval_1_min: 30,
    abandonment_interval_2_min: 1440,
    abandonment_interval_3_min: 2880,
    abandonment_promo_starts_at_reminder: 3,
    abandonment_max_age_hours: 168,
  });
  const [abandonMsg, setAbandonMsg] = useState({ kind: "", text: "" });
  const [abandonRunMsg, setAbandonRunMsg] = useState({ kind: "", text: "" });
  const [abandonBusy, setAbandonBusy] = useState(false);

  const saveAbandonSettings = async () => {
    setAbandonMsg({ kind: "", text: "" });
    setAbandonBusy(true);
    try {
      const payload = {
        abandonment_discount_percent: parseInt(abandonDraft.abandonment_discount_percent, 10),
        abandonment_validity_days: parseInt(abandonDraft.abandonment_validity_days, 10),
        abandonment_interval_1_min: parseInt(abandonDraft.abandonment_interval_1_min, 10),
        abandonment_interval_2_min: parseInt(abandonDraft.abandonment_interval_2_min, 10),
        abandonment_interval_3_min: parseInt(abandonDraft.abandonment_interval_3_min, 10),
        abandonment_promo_starts_at_reminder: parseInt(abandonDraft.abandonment_promo_starts_at_reminder, 10),
        abandonment_max_age_hours: parseInt(abandonDraft.abandonment_max_age_hours, 10),
      };
      const { data } = await api.put("/admin/settings", payload, headers);
      setAppSettings(data.settings || {});
      setAbandonMsg({ kind: "ok", text: "Configuration enregistrée." });
    } catch (e) {
      setAbandonMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setAbandonBusy(false); }
  };

  const runAbandonedReminders = async () => {
    if (!window.confirm("Lancer le scan des paniers abandonnés maintenant ? Les clients en attente de paiement depuis ≥ 30 min recevront un email avec un code promo.")) return;
    setAbandonRunMsg({ kind: "", text: "" });
    setAbandonBusy(true);
    try {
      const { data } = await api.post("/admin/cron/abandoned-reminders", {}, headers);
      setAbandonRunMsg({
        kind: "ok",
        text: `✓ ${data.sent || 0} email(s) envoyé(s) · ${data.skipped || 0} ignoré(s)${
          (data.errors && data.errors.length) ? ` · ${data.errors.length} erreur(s)` : ""
        }.`,
      });
    } catch (e) {
      setAbandonRunMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setAbandonBusy(false); }
  };

  // ─── Chat Live monitoring (sessions actives) ───────
  const [chatSessions, setChatSessions] = useState([]);
  const [chatSessionsLoading, setChatSessionsLoading] = useState(false);
  const [chatSessionsFilter, setChatSessionsFilter] = useState("active");
  const [chatVisitors, setChatVisitors] = useState([]);
  const loadChatSessions = async (status = chatSessionsFilter) => {
    setChatSessionsLoading(true);
    try {
      const { data } = await api.get(`/admin/chat/sessions?status=${status}&limit=50`, headers);
      setChatSessions(data.items || []);
    } catch { /* ignore */ }
    finally { setChatSessionsLoading(false); }
  };
  const loadChatVisitors = async () => {
    try {
      const { data } = await api.get("/admin/chat/visitors?limit=50", headers);
      setChatVisitors(data.items || []);
    } catch { /* ignore */ }
  };

  const [chatTestBusy, setChatTestBusy] = useState(false);
  const [chatTestResult, setChatTestResult] = useState(null);
  const testTelegramConfig = async () => {
    setChatTestBusy(true);
    setChatTestResult(null);
    try {
      const { data } = await api.post("/admin/chat/test-telegram", {}, headers);
      setChatTestResult(data);
    } catch (e) {
      setChatTestResult({ ok: false, error: e.response?.data?.detail || "Erreur réseau" });
    } finally { setChatTestBusy(false); }
  };

  const claimSession = async (handoffId) => {
    try {
      await api.post("/admin/chat/claim", { handoff_id: handoffId }, headers);
      await loadChatSessions(chatSessionsFilter);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur");
    }
  };

  const releaseSession = async (handoffId) => {
    if (!window.confirm("Libérer cette session ? Elle redeviendra disponible pour un autre assistant.")) return;
    try {
      await api.post("/admin/chat/release", { handoff_id: handoffId }, headers);
      await loadChatSessions(chatSessionsFilter);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur");
    }
  };

  const closeSession = async (handoffId) => {
    if (!window.confirm("Fermer définitivement cette session ? Le visiteur devra rouvrir un nouveau chat.")) return;
    try {
      await api.post("/admin/chat/close", { handoff_id: handoffId }, headers);
      if (chatReplyOpen === handoffId) closeReplyPanel();
      await loadChatSessions(chatSessionsFilter);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur");
    }
  };

  // ─── BO direct chat panel (admin replies without Telegram) ───
  const [chatReplyOpen, setChatReplyOpen] = useState(null); // handoff_id
  const [chatReplySession, setChatReplySession] = useState(null);
  const [chatReplyMsgs, setChatReplyMsgs] = useState([]);
  const [chatReplyInput, setChatReplyInput] = useState("");
  const [chatReplyBusy, setChatReplyBusy] = useState(false);
  const [chatReplyErr, setChatReplyErr] = useState("");

  const loadChatReplyMessages = async (hid) => {
    try {
      const { data } = await api.get(`/admin/chat/messages?handoff_id=${encodeURIComponent(hid)}`, headers);
      setChatReplyMsgs(data.messages || []);
      setChatReplySession(data.session || null);
    } catch (e) {
      setChatReplyErr(e.response?.data?.detail || "Erreur de chargement");
    }
  };

  const openReplyPanel = async (hid) => {
    setChatReplyOpen(hid);
    setChatReplyMsgs([]);
    setChatReplySession(null);
    setChatReplyInput("");
    setChatReplyErr("");
    await loadChatReplyMessages(hid);
  };

  const closeReplyPanel = () => {
    setChatReplyOpen(null);
    setChatReplySession(null);
    setChatReplyMsgs([]);
    setChatReplyInput("");
    setChatReplyErr("");
  };

  const sendReply = async (extra = {}) => {
    if (!chatReplyOpen) return;
    if (!chatReplyInput.trim() && !extra.attachment && !extra.photo_base64) return;
    if (chatReplyBusy) return;
    setChatReplyBusy(true); setChatReplyErr("");
    try {
      const body = { handoff_id: chatReplyOpen, text: chatReplyInput.trim() };
      if (extra.attachment) body.attachment = extra.attachment;
      if (extra.photo_base64) body.photo_base64 = extra.photo_base64;
      await api.post("/admin/chat/reply", body, headers);
      setChatReplyInput("");
      await loadChatReplyMessages(chatReplyOpen);
      await loadChatSessions(chatSessionsFilter);
    } catch (e) {
      setChatReplyErr(e.response?.data?.detail || "Erreur d'envoi");
    } finally { setChatReplyBusy(false); }
  };

  // ─── BO chat — file attachment picker ───
  const [chatReplyAttachBusy, setChatReplyAttachBusy] = useState(false);
  const onPickAttachment = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setChatReplyErr("Fichier trop volumineux (max 10 MB).");
      return;
    }
    setChatReplyAttachBusy(true); setChatReplyErr("");
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const b64 = (dataUrl.split(",", 2)[1] || "");
      await sendReply({
        attachment: {
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          base64: b64,
        },
      });
    } catch (e) {
      setChatReplyErr("Lecture du fichier impossible.");
    } finally { setChatReplyAttachBusy(false); }
  };

  // ─── Notifications: audio beep + tab title badge for visitor messages ───
  const [unreadByHid, setUnreadByHid] = useState({});
  const baseTitleRef = React.useRef(typeof document !== "undefined" ? document.title : "Admin");

  useEffect(() => {
    const total = Object.values(unreadByHid).reduce((a, b) => a + b, 0);
    if (typeof document !== "undefined") {
      document.title = total > 0 ? `(${total}) ${baseTitleRef.current}` : baseTitleRef.current;
    }
  }, [unreadByHid]);

  // Reset counter for currently opened conversation
  useEffect(() => {
    if (chatReplyOpen && unreadByHid[chatReplyOpen]) {
      setUnreadByHid((m) => { const n = { ...m }; delete n[chatReplyOpen]; return n; });
    }
    // eslint-disable-next-line
  }, [chatReplyOpen]);

  // Reset all when admin tab becomes visible AND user is on chatbot tab AND no modal
  useEffect(() => {
    const onVis = () => {
      if (!document.hidden && tab === "chatbot" && !chatReplyOpen) {
        setUnreadByHid({});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [tab, chatReplyOpen]);

  const beep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.connect(g); g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.36);
      setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 600);
    } catch { /* noop */ }
  };

  // ─── SSE: real-time BO updates ──────────────────────────────
  useEffect(() => {
    if (tab !== "chatbot" || !token) return;
    let es = null;
    try {
      const url = `${API}/admin/chat/stream?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);
      const refresh = (e) => {
        let data = {};
        try { data = JSON.parse(e?.data || "{}"); } catch { /* noop */ }
        loadChatSessions(chatSessionsFilter);
        if (chatReplyOpen) loadChatReplyMessages(chatReplyOpen);
        // Visitor notification : beep + counter when message NOT from us and modal isn't currently focused on that hid
        const inner = data?.data || {};
        if (e?.type === "session.message" && inner.from === "visitor") {
          const hid = inner.handoff_id;
          if (hid && (chatReplyOpen !== hid || document.hidden)) {
            beep();
            setUnreadByHid((m) => ({ ...m, [hid]: (m[hid] || 0) + 1 }));
          }
        }
      };
      ["session.new", "session.update", "session.message",
       "session.claimed", "session.released", "session.closed"].forEach((evt) => {
        es.addEventListener(evt, refresh);
      });
      es.onerror = () => { /* network blip — auto reconnect */ };
    } catch { /* ignore */ }
    return () => { if (es) try { es.close(); } catch { /* noop */ } };
    // eslint-disable-next-line
  }, [tab, token, chatSessionsFilter, chatReplyOpen]);

  const assignSession = async (handoffId) => {
    const list = (chatTeamDraft.chat_assistants || []).filter((a) => a.chat_id);
    if (list.length === 0) { window.alert("Aucun assistant configuré."); return; }
    const choice = window.prompt(
      "À qui assigner cette session ?\n\n" +
      list.map((a, i) => `${i + 1}. ${a.label} (${a.chat_id})${a.on_duty ? "" : " — off duty"}`).join("\n") +
      "\n\nTapez le numéro (1, 2…) :"
    );
    const idx = parseInt(choice, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= list.length) return;
    try {
      await api.post("/admin/chat/assign", { handoff_id: handoffId, chat_id: list[idx].chat_id }, headers);
      await loadChatSessions(chatSessionsFilter);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur");
    }
  };

  // ─── Chat Live Bridge — équipe support Telegram ────────
  const [chatTeamDraft, setChatTeamDraft] = useState({
    chat_assistants: [],
    chat_group_chat_id: "",
    chat_rotation_enabled: false,
    chat_rotation_reclaim_minutes: 2,
  });
  const [chatTeamMsg, setChatTeamMsg] = useState({ kind: "", text: "" });
  const [chatTeamBusy, setChatTeamBusy] = useState(false);

  const addAssistant = () => {
    setChatTeamDraft({
      ...chatTeamDraft,
      chat_assistants: [...(chatTeamDraft.chat_assistants || []), { label: "", chat_id: "", on_duty: true }],
    });
  };
  const updateAssistant = (i, patch) => {
    const arr = [...(chatTeamDraft.chat_assistants || [])];
    arr[i] = { ...arr[i], ...patch };
    setChatTeamDraft({ ...chatTeamDraft, chat_assistants: arr });
  };
  const removeAssistant = (i) => {
    const arr = [...(chatTeamDraft.chat_assistants || [])];
    arr.splice(i, 1);
    setChatTeamDraft({ ...chatTeamDraft, chat_assistants: arr });
  };
  const saveChatTeamSettings = async () => {
    setChatTeamMsg({ kind: "", text: "" });
    setChatTeamBusy(true);
    try {
      const cleaned = (chatTeamDraft.chat_assistants || [])
        .map((a) => ({ label: (a.label || "").trim(), chat_id: (a.chat_id || "").trim() }))
        .filter((a) => a.chat_id);
      const payload = {
        chat_assistants: cleaned,
        chat_group_chat_id: (chatTeamDraft.chat_group_chat_id || "").trim(),
        chat_rotation_enabled: !!chatTeamDraft.chat_rotation_enabled,
        chat_rotation_reclaim_minutes: parseInt(chatTeamDraft.chat_rotation_reclaim_minutes, 10) || 2,
        chat_welcome_enabled: !!chatTeamDraft.chat_welcome_enabled,
        chat_welcome_template: (chatTeamDraft.chat_welcome_template || "").trim(),
        chat_queue_enabled: !!chatTeamDraft.chat_queue_enabled,
        chat_queue_interval_minutes: parseInt(chatTeamDraft.chat_queue_interval_minutes, 10) || 2,
        chat_queue_template: (chatTeamDraft.chat_queue_template || "").trim(),
        chat_assignment_enabled: !!chatTeamDraft.chat_assignment_enabled,
        chat_assignment_template: (chatTeamDraft.chat_assignment_template || "").trim(),
        chat_away_enabled: !!chatTeamDraft.chat_away_enabled,
        chat_away_template: (chatTeamDraft.chat_away_template || "").trim(),
        chat_inactivity_close_enabled: !!chatTeamDraft.chat_inactivity_close_enabled,
        chat_inactivity_close_minutes: parseInt(chatTeamDraft.chat_inactivity_close_minutes, 10) || 30,
        chat_inactivity_close_template: (chatTeamDraft.chat_inactivity_close_template || "").trim(),
      };
      const { data } = await api.put("/admin/settings", payload, headers);
      setAppSettings(data.settings || {});
      const modeLbl = payload.chat_rotation_enabled ? `rotation (${payload.chat_rotation_reclaim_minutes} min)` : "broadcast";
      setChatTeamMsg({ kind: "ok", text: `✓ ${cleaned.length} assistant${cleaned.length > 1 ? "s" : ""}${payload.chat_group_chat_id ? " + groupe" : ""} · Mode: ${modeLbl}` });
    } catch (e) {
      setChatTeamMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setChatTeamBusy(false); }
  };

  // ─── Tickets ─────────────────────────────────────────
  const TICKET_CATS = [
    { id: "complaint", label: "Réclamation" },
    { id: "info",      label: "Demande d'info" },
    { id: "bug",       label: "Bug technique" },
    { id: "refund",    label: "Demande de remboursement" },
    { id: "other",     label: "Autre" },
  ];
  const TICKET_STATUSES = [
    { id: "open",         label: "Ouvert",      color: "amber" },
    { id: "in_progress",  label: "En cours",    color: "cyan" },
    { id: "resolved",     label: "Résolu",      color: "emerald" },
    { id: "closed",       label: "Fermé",       color: "zinc" },
  ];
  const TICKET_PRIOS = [
    { id: "low",     label: "Basse",   color: "zinc" },
    { id: "normal",  label: "Normale", color: "cyan" },
    { id: "high",    label: "Haute",   color: "amber" },
    { id: "urgent",  label: "Urgente", color: "red" },
  ];
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const loadTicketStats = async () => {
    try {
      const { data } = await api.get("/admin/tickets-stats?days=30", headers);
      setTicketStats(data);
    } catch { /* noop */ }
  };
  const [ticketsFilter, setTicketsFilter] = useState({ status: "all", category: "all", priority: "all", q: "" });
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketDraft, setTicketDraft] = useState({ subject: "", description: "", customer_name: "", customer_email: "", customer_phone: "", category: "complaint", priority: "normal", session_handoff_id: "" });
  const [ticketDetail, setTicketDetail] = useState(null); // currently-opened ticket detail
  const [ticketCommentDraft, setTicketCommentDraft] = useState("");
  const [ticketBusy, setTicketBusy] = useState(false);
  const [ticketErr, setTicketErr] = useState("");

  const loadTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (ticketsFilter.status !== "all") params.set("status", ticketsFilter.status);
      if (ticketsFilter.category !== "all") params.set("category", ticketsFilter.category);
      if (ticketsFilter.priority !== "all") params.set("priority", ticketsFilter.priority);
      if (ticketsFilter.q.trim()) params.set("q", ticketsFilter.q.trim());
      const { data } = await api.get(`/admin/tickets?${params.toString()}`, headers);
      setTickets(data.items || []);
    } catch (e) { /* noop */ }
  };

  const createTicket = async () => {
    if (!ticketDraft.subject.trim()) { setTicketErr("Sujet requis"); return; }
    setTicketBusy(true); setTicketErr("");
    try {
      await api.post("/admin/tickets", {
        ...ticketDraft,
        subject: ticketDraft.subject.trim(),
        description: ticketDraft.description.trim(),
      }, headers);
      setTicketModalOpen(false);
      setTicketDraft({ subject: "", description: "", customer_name: "", customer_email: "", customer_phone: "", category: "complaint", priority: "normal", session_handoff_id: "" });
      await loadTickets();
    } catch (e) {
      setTicketErr(e.response?.data?.detail || "Erreur");
    } finally { setTicketBusy(false); }
  };

  const openTicketDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/tickets/${id}`, headers);
      setTicketDetail(data.ticket);
    } catch (e) { /* noop */ }
  };

  const updateTicket = async (id, patch) => {
    setTicketBusy(true);
    try {
      await api.patch(`/admin/tickets/${id}`, patch, headers);
      await openTicketDetail(id);
      await loadTickets();
    } catch (e) {
      setTicketErr(e.response?.data?.detail || "Erreur");
    } finally { setTicketBusy(false); }
  };

  const addTicketComment = async () => {
    if (!ticketDetail || !ticketCommentDraft.trim()) return;
    try {
      await api.post(`/admin/tickets/${ticketDetail.id}/comments`, { text: ticketCommentDraft.trim() }, headers);
      setTicketCommentDraft("");
      await openTicketDetail(ticketDetail.id);
    } catch (e) {
      setTicketErr(e.response?.data?.detail || "Erreur");
    }
  };

  const openTicketFromChat = async (handoff_id) => {
    try {
      const { data } = await api.get(`/admin/tickets/from-chat/${handoff_id}`, headers);
      setTicketDraft({
        subject: data.subject || "",
        description: data.description || "",
        customer_name: data.customer_name || "",
        customer_email: data.customer_email || "",
        customer_phone: "",
        category: "complaint",
        priority: "normal",
        session_handoff_id: data.session_handoff_id || handoff_id,
      });
      setTicketModalOpen(true);
    } catch (e) {
      window.alert(e.response?.data?.detail || "Erreur");
    }
  };


  const [bulkSubject, setBulkSubject] = useState(
    "[Dr Trade Scalper Pro] Nouvelle version disponible — récupérez votre fichier",
  );
  const [bulkBody, setBulkBody] = useState(
    "Bonjour {name},\n\n"
    + "Une nouvelle version de Dr Trade Scalper Pro vient d'être publiée.\n"
    + "Votre fichier .ex5 personnalisé pour les comptes {mt5_accounts} a été mis à jour.\n\n"
    + "Téléchargez-le depuis :\n"
    + "    {download_link}\n\n"
    + "Numéro de commande : {order_short}\n"
    + "Validité : {expiry_date}\n\n"
    + "— L'équipe Dr Trade Bots",
  );
  const [bulkFilter, setBulkFilter] = useState("active");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState({ kind: "", text: "" });

  const runBulkNotify = async (dryRun = true) => {
    setBulkMsg({ kind: "", text: "" });
    if (!dryRun && !window.confirm(
      `Confirmer l'envoi RÉEL d'un email à tous les clients filtrés "${bulkFilter}" ?\n\n`
      + `Cette action est IRRÉVERSIBLE.`
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(
        "/admin/bulk/notify-update",
        { subject: bulkSubject, body: bulkBody, status_filter: bulkFilter, dry_run: dryRun },
        headers,
      );
      if (dryRun) {
        setBulkMsg({
          kind: "ok",
          text: `🔍 Dry-run : ${data.sent} destinataire(s) ciblé(s). Aucun email envoyé.`,
        });
      } else {
        setBulkMsg({
          kind: "ok",
          text: `✓ ${data.sent} email(s) envoyé(s) · ${data.skipped} ignoré(s).`,
        });
      }
    } catch (e) {
      setBulkMsg({ kind: "err", text: e.response?.data?.detail || "Erreur" });
    } finally { setBulkBusy(false); }
  };


  // ─── Filtered orders ─────────────────────────────────────
  const filteredOrders = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${o.id} ${o.full_name || ""} ${o.email || ""} ${o.phone || ""} ${(o.mt5_accounts || []).join(" ")} ${o.payment_reference || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [orders, searchQ, statusFilter]);

  // ─── Custom build (superadmin only) ────────────────────
  const [cbSlug, setCbSlug] = useState("");
  const [cbAccounts, setCbAccounts] = useState("");
  const [cbDuration, setCbDuration] = useState("30");
  const [cbStartDate, setCbStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cbBuilding, setCbBuilding] = useState(false);
  const [cbResult, setCbResult] = useState(null); // { filename, download_url, size }
  const [cbMsg, setCbMsg] = useState({ kind: "", text: "" });

  const [cbEx5File, setCbEx5File] = useState(null);
  const [cbRecipient, setCbRecipient] = useState("");
  const [cbClientName, setCbClientName] = useState("");
  const [cbSubject, setCbSubject] = useState("Votre Dr Trade Scalper Pro est prêt");
  const [cbBody, setCbBody] = useState("");
  const [cbSending, setCbSending] = useState(false);
  const [cbSendMsg, setCbSendMsg] = useState({ kind: "", text: "" });

  const [cbTgChatId, setCbTgChatId] = useState("");
  const [cbTgCaption, setCbTgCaption] = useState("");
  const [cbTgSending, setCbTgSending] = useState(false);
  const [cbTgMsg, setCbTgMsg] = useState({ kind: "", text: "" });

  const [cbHistory, setCbHistory] = useState([]);
  const [cbHistLoading, setCbHistLoading] = useState(false);
  const [cbEaVersion, setCbEaVersion] = useState("?");
  const [cbEmailOpen, setCbEmailOpen] = useState(false);
  const [cbTgOpen, setCbTgOpen] = useState(false);

  const loadCustomBuildVersion = async () => {
    try {
      const { data } = await api.get("/admin/custom-build/version", headers);
      if (data?.ea_version) setCbEaVersion(data.ea_version);
    } catch (e) { /* silent */ }
  };

  const loadCustomBuildHistory = async () => {
    setCbHistLoading(true);
    try {
      const { data } = await api.get("/admin/custom-build/history?limit=50", headers);
      setCbHistory(data.items || []);
    } catch (e) {
      // silent
    } finally {
      setCbHistLoading(false);
    }
  };

  const buildCustomMq5 = async () => {
    setCbMsg({ kind: "", text: "" });
    setCbResult(null);
    if (!cbSlug.trim()) {
      setCbMsg({ kind: "err", text: "Identifiant client (slug) requis." });
      return;
    }
    const dur = parseInt(cbDuration, 10);
    if (Number.isNaN(dur) || (dur < -1)) {
      setCbMsg({ kind: "err", text: "Durée invalide (-1 pour licence à vie, sinon entier positif)." });
      return;
    }
    setCbBuilding(true);
    try {
      const { data } = await api.post(
        "/admin/custom-build/mq5",
        {
          client_slug: cbSlug.trim(),
          mt5_accounts: cbAccounts,
          duration_days: dur,
          start_date: cbStartDate || null,
        },
        headers,
      );
      setCbResult(data);
      setCbMsg({ kind: "ok", text: `✓ Fichier généré : ${data.filename} (${(data.size / 1024).toFixed(1)} Ko)` });
      await loadCustomBuildHistory();
    } catch (e) {
      setCbMsg({ kind: "err", text: e.response?.data?.detail || "Erreur de génération." });
    } finally {
      setCbBuilding(false);
    }
  };

  const downloadCustomMq5 = async () => {
    if (!cbResult?.filename) return;
    try {
      const res = await api.get(
        `/admin/custom-build/download/${encodeURIComponent(cbResult.filename)}`,
        { ...headers, responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cbResult.filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setCbMsg({ kind: "err", text: e.response?.data?.detail || "Téléchargement échoué." });
    }
  };

  const sendCustomEx5 = async () => {
    setCbSendMsg({ kind: "", text: "" });
    if (!cbEx5File) {
      setCbSendMsg({ kind: "err", text: "Sélectionnez un fichier .ex5." });
      return;
    }
    if (!cbRecipient.trim() || !cbRecipient.includes("@")) {
      setCbSendMsg({ kind: "err", text: "Email destinataire invalide." });
      return;
    }
    setCbSending(true);
    try {
      const fd = new FormData();
      fd.append("file", cbEx5File);
      fd.append("recipient_email", cbRecipient.trim());
      fd.append("client_name", cbClientName);
      fd.append("subject", cbSubject);
      fd.append("body", cbBody);
      const { data } = await api.post("/admin/custom-build/send-ex5", fd, {
        ...headers,
        headers: { ...headers.headers, "Content-Type": "multipart/form-data" },
      });
      setCbSendMsg({ kind: "ok", text: `✓ Envoyé à ${data.sent_to} (${data.filename})` });
      await loadCustomBuildHistory();
    } catch (e) {
      setCbSendMsg({ kind: "err", text: e.response?.data?.detail || "Échec d'envoi." });
    } finally {
      setCbSending(false);
    }
  };

  const sendCustomEx5Telegram = async () => {
    setCbTgMsg({ kind: "", text: "" });
    if (!cbEx5File) {
      setCbTgMsg({ kind: "err", text: "Sélectionnez un fichier .ex5 (champ commun ci-dessus)." });
      return;
    }
    if (!cbTgChatId.trim()) {
      setCbTgMsg({ kind: "err", text: "chat_id Telegram requis." });
      return;
    }
    setCbTgSending(true);
    try {
      const fd = new FormData();
      fd.append("file", cbEx5File);
      fd.append("chat_id", cbTgChatId.trim());
      fd.append("caption", cbTgCaption);
      const { data } = await api.post("/admin/custom-build/send-ex5-telegram", fd, {
        ...headers,
        headers: { ...headers.headers, "Content-Type": "multipart/form-data" },
      });
      setCbTgMsg({ kind: "ok", text: `✓ Envoyé via Telegram à ${data.sent_to} (${data.filename})` });
      await loadCustomBuildHistory();
    } catch (e) {
      setCbTgMsg({ kind: "err", text: e.response?.data?.detail || "Échec d'envoi Telegram." });
    } finally {
      setCbTgSending(false);
    }
  };

  // ─── Login screen ─────────────────────────────────────────
  if (!token) {
    // ─── 2FA challenge stage ─────────────────────────────────
    if (twofaChallenge?.stage === "2fa") {
      return (
        <div className="min-h-[70vh] grid place-items-center px-4">
          <form onSubmit={verify2FA} className="glass rounded-2xl p-8 w-full max-w-md" data-testid="admin-2fa-form">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-5 w-5 text-cyan-400" />
              <h1 className="text-xl font-bold">Vérification 2FA</h1>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              {twofaChallenge.method === "email"
                ? <>Un code à 6 chiffres a été envoyé à <span className="font-mono text-zinc-300">{twofaChallenge.email_masked || "votre email"}</span>.</>
                : <>Ouvrez votre application d'authentification et saisissez le code à 6 chiffres.</>}
            </p>
            <input inputMode="numeric" autoComplete="one-time-code" maxLength={10}
              value={twofaCode} onChange={(e) => setTwofaCode(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="123456" className="input w-full text-center text-lg font-mono tracking-[0.4em]"
              data-testid="admin-2fa-code" required />
            {twofaErr && <div className="text-xs text-red-300 mt-3" data-testid="admin-2fa-error">{twofaErr}</div>}
            {twofaResendMsg && <div className="text-xs text-emerald-300 mt-3">{twofaResendMsg}</div>}
            <button type="submit" disabled={twofaBusy || twofaCode.length < 6}
              className="w-full btn-primary py-2.5 rounded-lg text-sm mt-4 inline-flex items-center justify-center gap-2"
              data-testid="admin-2fa-submit">
              {twofaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Valider
            </button>
            <div className="flex items-center justify-between mt-3">
              {twofaChallenge.method === "email" ? (
                <button type="button" onClick={resendEmailCode}
                  className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1"
                  data-testid="admin-2fa-resend">
                  <RefreshCw className="h-3 w-3" /> Renvoyer un code
                </button>
              ) : <span />}
              <button type="button" onClick={cancelTwoFactor}
                className="text-[11px] text-zinc-400 hover:text-zinc-200" data-testid="admin-2fa-cancel">
                Annuler
              </button>
            </div>
          </form>
        </div>
      );
    }

    // ─── First-login mandatory enrollment (assistant) ───────
    if (twofaChallenge?.stage === "enroll") {
      return (
        <div className="min-h-[70vh] grid place-items-center px-4 py-10">
          <div className="w-full max-w-xl">
            <TwoFactorWizard
              enrollToken={twofaChallenge.enroll_token}
              mandatory
              onComplete={onEnrollComplete}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-[70vh] grid place-items-center px-4">
        <form onSubmit={login} className="glass rounded-2xl p-8 w-full max-w-md" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="h-5 w-5 text-cyan-400" />
            <h1 className="text-2xl font-bold">Connexion administrateur</h1>
          </div>
          <div className="space-y-3">
            <input type="email" placeholder="Email admin" value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)} autoComplete="email"
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              data-testid="admin-login-email" required />
            <div className="relative">
              <input
                type={showLoginPwd ? "text" : "password"}
                placeholder="Mot de passe"
                value={loginPwd}
                onChange={(e) => setLoginPwd(e.target.value)}
                autoComplete="current-password"
                className="w-full pl-3 pr-10 py-2.5 rounded-lg text-sm"
                data-testid="admin-login-password" required
              />
              <button
                type="button"
                onClick={() => setShowLoginPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-cyan-300 transition-colors"
                aria-label={showLoginPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                title={showLoginPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                data-testid="admin-login-pwd-toggle"
                tabIndex={-1}
              >
                {showLoginPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {err && <div className="text-xs text-red-400 mt-3">{err}</div>}
          <button className="w-full btn-primary py-2.5 rounded-lg text-sm mt-5" data-testid="admin-login-btn">
            Se connecter
          </button>
          <div className="mt-4 flex flex-col items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => { setPlessStage("request"); setPlessEmail(loginEmail.trim()); setPlessMsg({ kind: "", text: "" }); }}
              className="text-cyan-300 hover:text-cyan-200 underline underline-offset-4 decoration-cyan-300/40"
              data-testid="admin-login-passwordless-btn"
            >
              🔐 Se connecter par code unique
            </button>
            <button
              type="button"
              onClick={() => { setPwrStage("request"); setPwrEmail(loginEmail.trim()); setPwrMsg({ kind: "", text: "" }); }}
              className="text-zinc-400 hover:text-zinc-200 underline underline-offset-4 decoration-zinc-500/40"
              data-testid="admin-login-forgot-password-btn"
            >
              Mot de passe oublié ? Le réinitialiser
            </button>
          </div>
        </form>

        {plessStage && (
          <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm px-4"
            data-testid="passwordless-modal"
            onClick={(e) => { if (e.target === e.currentTarget) cancelPasswordless(); }}
          >
            <div className="glass rounded-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                type="button"
                onClick={cancelPasswordless}
                className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-white"
                data-testid="passwordless-close-btn"
                aria-label="Fermer"
              >✕</button>
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="h-5 w-5 text-cyan-400" />
                <h2 className="text-xl font-bold">Connexion par code email</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-5">
                Recevez un code unique à 6 chiffres sur votre email admin. Ce code
                remplace temporairement votre mot de passe (valable 10 minutes).
              </p>
              {plessStage === "request" && (
                <form onSubmit={requestPasswordless} className="space-y-3" data-testid="passwordless-request-form">
                  <input
                    type="email"
                    placeholder="Email admin"
                    value={plessEmail}
                    onChange={(e) => setPlessEmail(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg text-sm"
                    data-testid="passwordless-email"
                    autoComplete="email"
                    required
                  />
                  <button
                    className="w-full btn-primary py-2.5 rounded-lg text-sm disabled:opacity-50"
                    disabled={plessBusy}
                    data-testid="passwordless-send-code-btn"
                  >
                    {plessBusy ? "Envoi en cours…" : "Envoyer le code"}
                  </button>
                </form>
              )}
              {plessStage === "verify" && (
                <form onSubmit={verifyPasswordless} className="space-y-3" data-testid="passwordless-verify-form">
                  <div className="text-xs text-zinc-400">
                    Saisissez le code reçu à <span className="font-mono text-cyan-300">{plessEmail}</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={10}
                    placeholder="Code à 6 chiffres"
                    value={plessCode}
                    onChange={(e) => setPlessCode(e.target.value.replace(/[^0-9]/g, ""))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm tracking-widest text-center font-mono"
                    data-testid="passwordless-code"
                    required
                  />
                  <button
                    className="w-full btn-primary py-2.5 rounded-lg text-sm disabled:opacity-50"
                    disabled={plessBusy || plessCode.length < 6}
                    data-testid="passwordless-verify-btn"
                  >
                    {plessBusy ? "Vérification…" : "Se connecter"}
                  </button>
                  <button
                    type="button"
                    onClick={resendPasswordlessCode}
                    disabled={plessResendCooldown > 0 || plessBusy}
                    className="w-full btn-ghost py-2 rounded-lg text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="passwordless-resend-btn"
                  >
                    {plessResendCooldown > 0
                      ? `Envoyer un autre code (${plessResendCooldown}s)`
                      : "Envoyer un autre code"}
                  </button>
                </form>
              )}
              {plessMsg.text && (
                <div
                  className={`mt-4 text-xs ${plessMsg.kind === "err" ? "text-red-400" : "text-emerald-300"}`}
                  data-testid="passwordless-message"
                >
                  {plessMsg.text}
                </div>
              )}
            </div>
          </div>
        )}

        {pwrStage && (
          <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm px-4"
            data-testid="password-reset-modal"
            onClick={(e) => { if (e.target === e.currentTarget) cancelPasswordReset(); }}
          >
            <div className="glass rounded-2xl p-6 sm:p-8 w-full max-w-md relative">
              <button
                type="button"
                onClick={cancelPasswordReset}
                className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-white"
                data-testid="password-reset-close-btn"
                aria-label="Fermer"
              >✕</button>
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="h-5 w-5 text-amber-400" />
                <h2 className="text-xl font-bold">Réinitialiser le mot de passe</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-5">
                Recevez un code à 6 chiffres sur votre email admin pour définir
                un nouveau mot de passe (valable 10 minutes). Toutes vos
                sessions actuelles seront déconnectées.
              </p>
              {pwrStage === "request" && (
                <form onSubmit={requestPasswordReset} className="space-y-3" data-testid="password-reset-request-form">
                  <input
                    type="email"
                    placeholder="Email admin"
                    value={pwrEmail}
                    onChange={(e) => setPwrEmail(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg text-sm"
                    data-testid="password-reset-email"
                    autoComplete="email"
                    required
                  />
                  <button
                    className="w-full btn-primary py-2.5 rounded-lg text-sm disabled:opacity-50"
                    disabled={pwrBusy}
                    data-testid="password-reset-send-code-btn"
                  >
                    {pwrBusy ? "Envoi en cours…" : "Envoyer le code"}
                  </button>
                </form>
              )}
              {pwrStage === "verify" && (
                <form onSubmit={confirmPasswordReset} className="space-y-3" data-testid="password-reset-confirm-form">
                  <div className="text-xs text-zinc-400">
                    Code reçu à <span className="font-mono text-amber-300">{pwrEmail}</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={10}
                    placeholder="Code à 6 chiffres"
                    value={pwrCode}
                    onChange={(e) => setPwrCode(e.target.value.replace(/[^0-9]/g, ""))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm tracking-widest text-center font-mono"
                    data-testid="password-reset-code"
                    required
                  />
                  <div className="relative">
                    <input
                      type={pwrShow ? "text" : "password"}
                      placeholder="Nouveau mot de passe (8 caractères min)"
                      value={pwrNew}
                      onChange={(e) => setPwrNew(e.target.value)}
                      className="w-full pl-3 pr-10 py-2.5 rounded-lg text-sm"
                      data-testid="password-reset-new-pwd"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setPwrShow((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-cyan-300"
                      aria-label={pwrShow ? "Masquer" : "Afficher"}
                      data-testid="password-reset-toggle-visibility"
                      tabIndex={-1}
                    >
                      {pwrShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <input
                    type={pwrShow ? "text" : "password"}
                    placeholder="Confirmer le nouveau mot de passe"
                    value={pwrConfirm2}
                    onChange={(e) => setPwrConfirm2(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-lg text-sm"
                    data-testid="password-reset-confirm-pwd"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    className="w-full btn-primary py-2.5 rounded-lg text-sm disabled:opacity-50"
                    disabled={pwrBusy || pwrCode.length < 6 || pwrNew.length < 8}
                    data-testid="password-reset-confirm-btn"
                  >
                    {pwrBusy ? "Mise à jour…" : "Valider le nouveau mot de passe"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPwrStage("request"); setPwrCode(""); setPwrChallenge(""); setPwrNew(""); setPwrConfirm2(""); }}
                    className="w-full btn-ghost py-2 rounded-lg text-xs"
                    data-testid="password-reset-back-btn"
                  >
                    ← Renvoyer un code à une autre adresse
                  </button>
                </form>
              )}
              {pwrStage === "done" && (
                <div className="space-y-3" data-testid="password-reset-done">
                  <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-300 text-sm p-3">
                    ✅ Mot de passe mis à jour. Vous pouvez à présent vous
                    reconnecter avec votre nouveau mot de passe.
                  </div>
                  <button
                    type="button"
                    onClick={cancelPasswordReset}
                    className="w-full btn-primary py-2.5 rounded-lg text-sm"
                    data-testid="password-reset-back-to-login-btn"
                  >
                    Retour à la connexion
                  </button>
                </div>
              )}
              {pwrMsg.text && pwrStage !== "done" && (
                <div
                  className={`mt-4 text-xs ${pwrMsg.kind === "err" ? "text-red-400" : "text-emerald-300"}`}
                  data-testid="password-reset-message"
                >
                  {pwrMsg.text}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Dashboard ─────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="page-admin">
      {deployDiff && !deployDiff.in_sync && (
        <div
          className={`mb-6 rounded-xl border p-4 flex items-start gap-3 ${
            deployDiff.prod_reachable
              ? "border-amber-400/40 bg-amber-400/[0.06] text-amber-100"
              : "border-red-400/40 bg-red-400/[0.06] text-red-100"
          }`}
          data-testid="admin-deploy-diff-banner"
        >
          <div className="mt-0.5">
            {deployDiff.prod_reachable ? "⚠️" : "⛔"}
          </div>
          <div className="flex-1 text-sm">
            {deployDiff.prod_reachable ? (
              <>
                <div className="font-semibold">
                  Redéploiement requis — la version en prod est différente du preview
                </div>
                <div className="text-xs mt-1 text-amber-200/80">
                  Preview (pod) : <span className="font-mono font-semibold">v{deployDiff.preview_version}</span>
                  &nbsp;·&nbsp;
                  Production ({deployDiff.prod_url.replace(/^https?:\/\//, "")}) :&nbsp;
                  <span className="font-mono font-semibold">v{deployDiff.prod_version}</span>
                </div>
                <div className="text-xs mt-1.5">
                  Cliquez « Deploy » dans Emergent pour pousser les changements EA sur la production.
                  Les clients qui commandent aujourd'hui reçoivent encore la v{deployDiff.prod_version}.
                </div>
                <button
                  type="button"
                  onClick={downloadMasterMq5}
                  className="mt-2 px-2.5 py-1 rounded-md border border-amber-300/40 hover:bg-amber-300/10 text-amber-100 text-[11px] font-medium"
                  data-testid="admin-download-master-mq5-btn"
                >
                  📥 Télécharger le .mq5 v{deployDiff.preview_version} (pod actuel)
                </button>
              </>
            ) : (
              <>
                <div className="font-semibold">Production injoignable</div>
                <div className="text-xs mt-1 text-red-200/80">
                  Impossible d'interroger <span className="font-mono">{deployDiff.prod_url}</span>.
                  {deployDiff.prod_error && (<> Erreur : <span className="font-mono">{deployDiff.prod_error}</span></>)}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {deployDiff && deployDiff.in_sync && (
        <div
          className="mb-6 rounded-xl border border-emerald-400/30 bg-emerald-400/[0.05] text-emerald-200 p-3 text-xs flex items-center gap-3 flex-wrap"
          data-testid="admin-deploy-diff-banner"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 shrink-0" />
          <span className="flex-1">
            Preview et production synchronisés sur EA <span className="font-mono font-semibold">v{deployDiff.preview_version}</span>.
          </span>
          <button
            type="button"
            onClick={downloadMasterMq5}
            className="ml-auto px-2.5 py-1 rounded-md border border-emerald-300/40 hover:bg-emerald-300/10 text-emerald-100 text-[11px] font-medium"
            data-testid="admin-download-master-mq5-btn"
          >
            📥 Télécharger le .mq5 v{deployDiff.preview_version}
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold tracking-tighter">Admin Dashboard</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {me && (
            <span className="text-xs font-mono bg-white/[0.04] border border-white/[0.08] rounded px-2.5 py-1.5 flex items-center gap-1.5"
              data-testid="admin-me-badge">
              <span className="text-zinc-400">{me.email}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${
                me.role === "superadmin" ? "bg-cyan-400/20 text-cyan-300" : "bg-amber-400/20 text-amber-300"
              }`}>{me.role}</span>
            </span>
          )}
          <a href="/admin/analytics" className="btn-ghost px-3 py-2 rounded-lg text-sm flex items-center gap-1.5" data-testid="admin-analytics-link">
            📊 Analytics trades
          </a>
          <button onClick={loadAll} className="btn-ghost px-3 py-2 rounded-lg text-sm flex items-center gap-1.5" data-testid="admin-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={logout} className="btn-ghost px-3 py-2 rounded-lg text-sm flex items-center gap-1.5" data-testid="admin-logout-btn">
            <LogOut className="h-3.5 w-3.5" /> {t("admin.logout")}
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/[0.06] flex-wrap">
        {[
          { id: "analytics", label: "Analytics", Icon: TrendingUp },
          { id: "orders",    label: t("admin.orders"), Icon: ShoppingBag },
          { id: "contacts",  label: t("admin.contacts"), Icon: Mail },
          { id: "affiliates", label: "Affiliés", Icon: Users },
          { id: "payouts",   label: "Paiements", Icon: Wallet },
          { id: "licenses",  label: "Licences", Icon: KeyRound },
          { id: "tickets",   label: "Tickets", Icon: LifeBuoy },
          { id: "copytrading", label: "Copytrading", Icon: Cloud },
          ...(me?.role === "superadmin" ? [{ id: "custombuild", label: "Build manuel", Icon: FileCode }] : []),
          ...(me?.role === "superadmin" ? [{ id: "chatbot", label: "Chatbot", Icon: MessageCircle }] : []),
          { id: "settings",  label: "Paramètres", Icon: Settings },
        ].map((it) => (
          <button
            key={it.id}
            onClick={() => {
              setTab(it.id);
              if (it.id === "affiliates") { loadCommissions(affStatusFilter); loadAdminPromos(); }
              if (it.id === "payouts") { loadPayouts(payoutFilter); }
              if (it.id === "settings") { loadEmailTpls(); loadAppSettings(); }
              if (it.id === "chatbot") { loadAppSettings(); loadChatSessions("active"); loadChatVisitors(); }
              if (it.id === "custombuild") { loadCustomBuildHistory(); loadCustomBuildVersion(); }
              if (it.id === "tickets") { loadTickets(); }
              if (it.id === "analytics" && me?.role === "superadmin" && !teamStats) {
                loadTeamStats();
              }
            }}
            className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
              tab === it.id ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-white"
            }`}
            data-testid={`admin-tab-${it.id}`}
          >
            <it.Icon className="h-4 w-4 shrink-0" />
            <span>{it.label}</span>
            {(it.id === "orders" || it.id === "contacts" || it.id === "affiliates") && (
              <span className="text-xs text-zinc-500 font-mono">
                ({it.id === "orders" ? orders.length
                  : it.id === "contacts" ? contacts.length
                  : affiliates.length})
              </span>
            )}
          </button>
        ))}
      </div>

      {err && <div className="text-xs text-red-400 mb-3">{err}</div>}
      {busy && <div className="text-xs text-zinc-500 mb-3 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> {t("common.loading")}</div>}

      {/* ─── ANALYTICS ───────────────────────────────────────── */}
      {tab === "analytics" && analytics && (
        <div className="space-y-6" data-testid="admin-analytics">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard testId="kpi-total-orders" icon={ShoppingBag} label="Commandes totales" value={analytics.total_orders} hint={`${analytics.orders_7d} sur 7j · ${analytics.orders_30d} sur 30j`} />
            <KpiCard testId="kpi-revenue" icon={TrendingUp} label="Revenu vérifié" value={`${analytics.revenue_xof.toLocaleString("fr-FR").replaceAll(",", " ")} FCFA`} hint={`≈ $${analytics.revenue_usd.toLocaleString("en-US")} USD`} />
            <KpiCard testId="kpi-verified" icon={CheckCircle2} label="Paiements vérifiés" value={analytics.verified_orders} hint={`${analytics.verification_rate}% taux validé on-chain`} accent="text-emerald-300" />
            <KpiCard testId="kpi-revenue30d" icon={TrendingUp} label="Revenu 30 derniers jours" value={`${analytics.revenue_xof_30d.toLocaleString("fr-FR").replaceAll(",", " ")} FCFA`} hint="Commandes vérifiées uniquement" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard testId="kpi-affiliates-total" icon={Users} label="Affiliés actifs"
              value={analytics.affiliates_total ?? 0}
              hint="Total comptes affiliés inscrits" accent="text-cyan-300" />
            <KpiCard testId="kpi-affiliates-from-email" icon={Mail} label="Affiliés via email de commande"
              value={analytics.affiliates_from_email ?? 0}
              hint={`Via CTA "Parrainer = Gagner" dans l'email de confirmation`}
              accent="text-amber-300" />
            <KpiCard testId="kpi-affiliates-email-conv" icon={TrendingUp} label="Taux conversion email → affilié"
              value={`${analytics.affiliates_email_conv_rate ?? 0}%`}
              hint="Part des clients payés devenus affiliés via l'email"
              accent="text-emerald-300" />
          </div>

          {/* Cart abandonment block */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard testId="kpi-awaiting-payment" icon={Clock} label="En attente de paiement"
              value={analytics.awaiting_payment_count ?? 0}
              hint="Cinetpay redirigé · pas encore confirmé"
              accent="text-amber-300" />
            <KpiCard testId="kpi-reminders-sent" icon={Mail} label="Relances envoyées"
              value={analytics.abandonment_reminders_sent ?? 0}
              hint="Emails de relance commande non finalisée"
              accent="text-cyan-300" />
            <KpiCard testId="kpi-reminders-recovered" icon={CheckCircle2} label="Relances récupérées"
              value={analytics.abandonment_reminders_recovered ?? 0}
              hint={`${analytics.abandonment_reminders_sent ?? 0} relance(s) envoyée(s) au total`}
              accent="text-emerald-300" />
            <KpiCard testId="kpi-recovery-rate" icon={TrendingUp} label="Taux de récupération"
              value={`${analytics.abandonment_recovery_rate ?? 0}%`}
              hint="Part des relances qui ont finalement payé"
              accent="text-emerald-300" />
            <KpiCard testId="kpi-abandoned" icon={XCircle} label="Définitivement abandonnées"
              value={analytics.abandoned_count ?? 0}
              hint="Plus de 24h sans paiement"
              accent="text-red-300" />
          </div>

          {/* Live bridge stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard testId="kpi-bridge-total" icon={MessageCircle} label="Sessions chat (total)"
              value={analytics.bridge_total ?? 0}
              hint="Demandes humaines historiques"
              accent="text-cyan-300" />
            <KpiCard testId="kpi-bridge-active" icon={Activity} label="Sessions actives"
              value={analytics.bridge_active ?? 0}
              hint="En cours maintenant"
              accent="text-emerald-300" />
            <KpiCard testId="kpi-bridge-reclaim-rate" icon={RefreshCw} label="Taux de reprise"
              value={`${analytics.bridge_reclaim_rate ?? 0}%`}
              hint={`${analytics.bridge_reclaimed ?? 0} demande(s) timeout`}
              accent="text-amber-300" />
            <KpiCard testId="kpi-bridge-closed" icon={CheckCircle2} label="Sessions clôturées"
              value={analytics.bridge_closed ?? 0}
              hint="Conversations terminées"
              accent="text-zinc-300" />
            <KpiCard testId="kpi-chat-visitors" icon={Users} label="Visiteurs CRM"
              value={analytics.chat_visitors_total ?? 0}
              hint="Coordonnées collectées"
              accent="text-cyan-300" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-5">
              <h3 className="text-sm font-semibold mb-4">Top des plans vendus</h3>
              <ul className="space-y-2.5" data-testid="analytics-by-plan">
                {analytics.by_plan.slice(0, 8).map((p) => (
                  <li key={p.plan_id} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300 truncate">{p.plan_label}</span>
                    <span className="font-mono text-xs text-zinc-400">
                      {p.count} cmd · {p.verified} ✓
                    </span>
                  </li>
                ))}
                {analytics.by_plan.length === 0 && <li className="text-zinc-500 text-sm">Aucune donnée</li>}
              </ul>
            </div>
            <div className="glass rounded-2xl p-5">
              <h3 className="text-sm font-semibold mb-4">Méthodes de paiement</h3>
              <ul className="space-y-2.5" data-testid="analytics-by-method">
                {analytics.by_method.map((m) => (
                  <li key={m.method} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300 uppercase tracking-wide text-xs">{m.method}</span>
                    <span className="font-mono text-xs text-zinc-400">
                      {m.count} cmd · {m.verified} ✓ · {m.revenue_xof.toLocaleString("fr-FR").replaceAll(",", " ")} XOF
                    </span>
                  </li>
                ))}
                {analytics.by_method.length === 0 && <li className="text-zinc-500 text-sm">Aucune donnée</li>}
              </ul>
            </div>
            <div className="glass rounded-2xl p-5 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-4">Répartition par statut</h3>
              <div className="flex flex-wrap gap-2" data-testid="analytics-by-status">
                {analytics.by_status.map((s) => (
                  <span
                    key={s.status}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] font-mono"
                  >
                    {s.status} <span className="text-cyan-300 ml-1">{s.count}</span>
                  </span>
                ))}
                {analytics.by_status.length === 0 && <span className="text-zinc-500 text-sm">Aucune donnée</span>}
              </div>
            </div>
          </div>

          {/* ─── Team performance — superadmin only ────────── */}
          {me?.role === "superadmin" && (
            <div className="glass rounded-2xl p-5" data-testid="admin-team-performance">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-cyan-400" />
                  <h3 className="text-sm font-semibold">Top assistants — volume traité</h3>
                </div>
                <div className="flex-1" />
                <select
                  value={teamPeriod}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTeamPeriod(v);
                    if (v !== "custom") loadTeamStats(v);
                  }}
                  className="input text-xs px-2 py-1 w-auto"
                  data-testid="admin-team-period"
                >
                  <option value="today">Aujourd'hui</option>
                  <option value="week">7 derniers jours</option>
                  <option value="month">30 derniers jours</option>
                  <option value="all">Tout l'historique</option>
                  <option value="custom">Période personnalisée…</option>
                </select>
                <button
                  type="button"
                  onClick={() => loadTeamStats()}
                  className="btn-ghost px-2.5 py-1 rounded-md text-xs flex items-center gap-1"
                  data-testid="admin-team-refresh"
                >
                  <RefreshCw className={`h-3 w-3 ${teamBusy ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>

              {teamPeriod === "custom" && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <input type="date" value={teamCustomStart}
                    onChange={(e) => setTeamCustomStart(e.target.value)}
                    className="input text-xs w-auto" data-testid="admin-team-start" />
                  <span className="text-xs text-zinc-500 self-center">→</span>
                  <input type="date" value={teamCustomEnd}
                    onChange={(e) => setTeamCustomEnd(e.target.value)}
                    className="input text-xs w-auto" data-testid="admin-team-end" />
                  <button
                    type="button"
                    onClick={() => loadTeamStats("custom", teamCustomStart, teamCustomEnd)}
                    disabled={!teamCustomStart || !teamCustomEnd}
                    className="btn-primary px-3 py-1 rounded-md text-xs disabled:opacity-40"
                    data-testid="admin-team-apply"
                  >
                    Appliquer
                  </button>
                </div>
              )}

              {teamStats?.team?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-400 text-[11px] uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2">#</th>
                        <th className="text-left px-3 py-2">Assistant</th>
                        <th className="text-left px-3 py-2">Rôle</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-right px-3 py-2">Compiled</th>
                        <th className="text-right px-3 py-2">Delivered</th>
                        <th className="text-right px-3 py-2" title="Commandes incluant un changement de licence">Modif. lic.</th>
                        <th className="text-right px-3 py-2">Revenu vérifié</th>
                        <th className="text-left px-3 py-2">Dernière action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamStats.team.map((r, i) => (
                        <tr key={r.admin_email} className="border-t border-white/[0.04]" data-testid={`admin-team-row-${i}`}>
                          <td className="px-3 py-2 text-zinc-500 font-mono">{i + 1}</td>
                          <td className="px-3 py-2">
                            <div className="text-sm">{r.admin_email}</div>
                            {!r.active && <div className="text-[10px] text-red-300">désactivé</div>}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${r.role === "superadmin" ? "bg-amber-400/15 text-amber-300 border border-amber-400/30" : "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30"}`}>
                              {r.role}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-cyan-300">{r.total}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400">{r.compiled}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-300">{r.delivered}</td>
                          <td className="px-3 py-2 text-right font-mono text-amber-300">{r.license_changes}</td>
                          <td className="px-3 py-2 text-right font-mono text-zinc-400">
                            {r.revenue_xof ? `${r.revenue_xof.toLocaleString("fr-FR").replaceAll(",", " ")}` : "—"}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">
                            {r.last_action_at ? r.last_action_at.slice(0, 16).replace("T", " ") : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-white/[0.06] text-xs text-zinc-500">
                      <tr>
                        <td colSpan={3} className="px-3 py-2">Total période</td>
                        <td className="px-3 py-2 text-right font-mono text-cyan-300 font-semibold">
                          {teamStats.total_processed_orders}
                        </td>
                        <td colSpan={5}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-zinc-500 text-sm py-6 text-center">
                  {teamBusy ? "Chargement…" : "Aucune commande traitée sur cette période."}
                </div>
              )}
            </div>
          )}

          {/* ─── Tickets / Réclamations stats ─── */}
          {ticketStats && (
            <div className="glass rounded-2xl p-5" data-testid="analytics-tickets-block">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4 text-cyan-300" /> Tickets & Satisfaction (30 j)
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Total : <span className="text-cyan-300 font-mono">{ticketStats.total}</span>
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <KpiCard testId="kpi-tickets-total" icon={FileText} label="Tickets créés (30 j)" value={ticketStats.total} hint={`${ticketStats.by_status?.open || 0} ouverts · ${ticketStats.by_status?.in_progress || 0} en cours`} />
                <KpiCard testId="kpi-tickets-resolution" icon={Clock} label="Délai moyen de résolution" value={ticketStats.avg_resolution_hours != null ? `${ticketStats.avg_resolution_hours} h` : "—"} hint="Création → fermeture (sessions closes uniquement)" accent="text-cyan-300" />
                <KpiCard testId="kpi-tickets-rating" icon={Sparkles} label="Note moyenne" value={ticketStats.avg_rating != null ? `${ticketStats.avg_rating} / 5 ★` : "—"} hint={`${ticketStats.ratings_count} avis reçus`} accent="text-amber-300" />
                <KpiCard testId="kpi-tickets-promoters" icon={TrendingUp} label="Promoteurs"
                  value={`${ticketStats.satisfaction_breakdown?.promoters || 0}`}
                  hint={`${ticketStats.satisfaction_breakdown?.passives || 0} neutres · ${ticketStats.satisfaction_breakdown?.detractors || 0} détracteurs`}
                  accent="text-emerald-300" />
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Par catégorie</div>
                  <div className="space-y-2">
                    {[
                      ["complaint", "Réclamation", "red"],
                      ["info", "Demande d'info", "cyan"],
                      ["bug", "Bug technique", "amber"],
                      ["refund", "Demande de remboursement", "violet"],
                      ["other", "Autre", "zinc"],
                    ].map(([id, label, color]) => {
                      const n = ticketStats.by_category?.[id] || 0;
                      const pct = ticketStats.total > 0 ? Math.round((n / ticketStats.total) * 100) : 0;
                      return (
                        <div key={id} className="flex items-center gap-2 text-xs">
                          <div className="w-44 text-zinc-300 truncate">{label}</div>
                          <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                            <div className={`h-full bg-${color}-400/60`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="w-12 text-right font-mono text-zinc-200">{n}</div>
                          <div className="w-10 text-right font-mono text-zinc-500 text-[10px]">{pct}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Par statut</div>
                  <div className="space-y-2">
                    {[
                      ["open", "Ouvert", "amber"],
                      ["in_progress", "En cours", "cyan"],
                      ["resolved", "Résolu", "emerald"],
                      ["closed", "Fermé", "zinc"],
                    ].map(([id, label, color]) => {
                      const n = ticketStats.by_status?.[id] || 0;
                      const pct = ticketStats.total > 0 ? Math.round((n / ticketStats.total) * 100) : 0;
                      return (
                        <div key={id} className="flex items-center gap-2 text-xs">
                          <div className="w-32 text-zinc-300">{label}</div>
                          <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                            <div className={`h-full bg-${color}-400/60`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="w-12 text-right font-mono text-zinc-200">{n}</div>
                          <div className="w-10 text-right font-mono text-zinc-500 text-[10px]">{pct}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── ORDERS ─────────────────────────────────────────── */}
      {tab === "orders" && (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input
                type="search"
                placeholder="Rechercher (id, email, téléphone, hash, compte MT5…)"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                className="input pl-10"
                data-testid="admin-orders-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input sm:w-56"
              data-testid="admin-orders-status-filter"
            >
              <option value="all">Tous les statuts ({orders.length})</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s} ({orders.filter((o) => o.status === s).length})</option>
              ))}
            </select>
            <button
              onClick={downloadCsv}
              className="btn-ghost px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 shrink-0"
              data-testid="admin-orders-export-csv"
            >
              <FileSpreadsheet className="h-4 w-4" /> Export CSV
            </button>
          </div>

          <div className="glass rounded-2xl overflow-hidden" data-testid="admin-orders-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.02] text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-3 w-8"></th>
                    <th className="px-4 py-3 text-left">ID</th>
                    <th className="px-4 py-3 text-left">Plan</th>
                    <th className="px-4 py-3 text-left">Client</th>
                    <th className="px-4 py-3 text-left">Comptes MT5</th>
                    <th className="px-4 py-3 text-left">Paiement</th>
                    <th className="px-4 py-3 text-left">Statut</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const isOpen = expanded.has(o.id);
                    return (
                    <React.Fragment key={o.id}>
                    <tr className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`admin-order-row-${o.id}`}>
                      <td className="px-2 py-3">
                        <button onClick={() => toggleRow(o.id)} className="text-zinc-400 hover:text-cyan-300"
                          data-testid={`admin-order-toggle-${o.id}`}>
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                        {o.id.slice(0, 8)}
                        {(o.license_changes || []).length > 0 && (
                          <div
                            className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] font-semibold not-italic"
                            title={`${o.license_changes.length} changement(s) de numéro — dernier par ${o.license_changes[o.license_changes.length - 1]?.by || "?"}`}
                            data-testid={`admin-order-license-badge-${o.id}`}
                          >
                            <RefreshCw className="h-2.5 w-2.5" />
                            Modif. licence ×{o.license_changes.length}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{o.plan_label}</div>
                        <div className="text-[11px] text-zinc-500">
                          {o.price_xof ? `${o.price_xof.toLocaleString("fr-FR").replaceAll(",", " ")} XOF` : `${o.price_eur || 0}€`} · {o.duration_days}j
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{o.full_name || "—"}</div>
                        <div className="text-[11px] text-zinc-500">{o.email || "—"}</div>
                        {o.phone && <div className="text-[11px] text-zinc-500">{o.phone}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{(o.mt5_accounts || []).join(", ") || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="text-xs uppercase font-semibold">{o.payment_method || "—"}</div>
                        <div className="text-[11px] flex items-center gap-1">
                          {o.payment_verified ? (
                            <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> vérifié</span>
                          ) : (
                            <span className="text-zinc-500 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> non vérifié</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={o.status}
                          onChange={(e) => setStatus(o.id, e.target.value)}
                          className="text-xs rounded-md px-2 py-1"
                          data-testid={`admin-order-status-${o.id}`}
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        {o.processed_by && (o.status === "compiled" || o.status === "delivered") && (
                          <div
                            className="mt-1 text-[10px] text-zinc-500 leading-tight"
                            title={`Traitée par ${o.processed_by}${o.processed_at ? " le " + o.processed_at.slice(0, 16).replace("T", " ") : ""}`}
                            data-testid={`admin-order-processed-by-${o.id}`}
                          >
                            <span className="text-zinc-600">par</span>{" "}
                            <span className="text-cyan-300 font-mono">{o.processed_by.split("@")[0]}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* 1. Download personalized .mq5 */}
                          {o.mq5_filename && (
                            <button
                              onClick={() => downloadMq5(o.id, o.mq5_filename)}
                              title="Télécharger le .mq5 personnalisé à compiler"
                              className="btn-ghost px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1"
                              data-testid={`admin-order-dl-mq5-${o.id}`}
                            >
                              <FileCode className="h-3 w-3" /> .mq5
                            </button>
                          )}
                          {/* 2. Upload compiled .ex5 */}
                          <label className="btn-ghost px-2.5 py-1.5 rounded-md text-xs cursor-pointer flex items-center gap-1" data-testid={`admin-order-upload-label-${o.id}`}>
                            <Upload className="h-3 w-3" /> .ex5
                            <input
                              type="file"
                              accept=".ex5"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadEx5(o.id, e.target.files[0])}
                              data-testid={`admin-order-upload-input-${o.id}`}
                            />
                          </label>
                          {/* 3. Send delivery (ex5 + manual + guide zipped) */}
                          {o.ex5_filename && (
                            <button
                              onClick={() => sendDelivery(o.id)}
                              disabled={sendingId === o.id}
                              title="Envoyer par mail au client : .ex5 + Manuel + Guide d'installation (ZIP)"
                              className={`px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1 font-semibold ${
                                o.status === "delivered"
                                  ? "bg-emerald-400/10 text-emerald-300 border border-emerald-400/30 hover:bg-emerald-400/20"
                                  : "bg-cyan-400 text-[#05050A] hover:bg-cyan-300"
                              } disabled:opacity-60 disabled:cursor-wait`}
                              data-testid={`admin-order-send-${o.id}`}
                            >
                              {sendingId === o.id ? (
                                <><Loader className="h-3 w-3 animate-spin" /> Envoi…</>
                              ) : o.status === "delivered" ? (
                                <><CheckCircle2 className="h-3 w-3" /> Renvoyer</>
                              ) : (
                                <><Mail className="h-3 w-3" /> Envoyer</>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-white/[0.015]" data-testid={`admin-order-timeline-${o.id}`}>
                        <td colSpan={8} className="px-4 py-4">
                          <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-3">
                            Timeline · <a className="text-cyan-300 hover:underline ml-2" href={`/track/${o.id}`} target="_blank" rel="noreferrer">Vue client →</a>
                          </div>
                          {(o.events || []).length === 0 ? (
                            <div className="text-xs text-zinc-500 italic">Aucun événement enregistré.</div>
                          ) : (
                            <ol className="space-y-2 pl-3 border-l border-white/[0.08]">
                              {(o.events || []).map((ev, idx) => (
                                <li key={idx} className="text-xs">
                                  <div className="flex items-baseline gap-3">
                                    <code className="text-zinc-500 font-mono">{new Date(ev.at).toLocaleString()}</code>
                                    <span className="px-2 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-[10px] font-mono">{ev.kind}</span>
                                    <span className="text-zinc-300">{ev.message || ""}</span>
                                  </div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                  {filteredOrders.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">Aucune commande</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ─── CONTACTS ───────────────────────────────────────── */}
      {tab === "contacts" && (
        <div className="glass rounded-2xl p-5 space-y-3" data-testid="admin-contacts-list">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs text-zinc-500">
              {contacts.length} message{contacts.length > 1 ? "s" : ""} · auto-actualisé toutes les 15 s
            </div>
            <button
              onClick={reloadContacts}
              className="text-xs px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] inline-flex items-center gap-1.5"
              data-testid="admin-contacts-refresh"
            >
              <RefreshCw className="h-3 w-3" /> Actualiser
            </button>
          </div>
          {contacts.map((c) => (
            <div key={c.id} className="border border-white/[0.06] rounded-lg p-4" data-testid={`contact-item-${c.id}`}>
              <div className="flex justify-between mb-1.5">
                <div className="text-sm font-medium">
                  {c.name} <span className="text-zinc-500 font-normal">— {c.email}</span>
                  {c.replied && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-400/15 border border-emerald-400/30 text-emerald-300 text-[10px] font-normal">
                      <CheckCircle2 className="h-2.5 w-2.5" /> Répondu
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 font-mono">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <div className="text-xs text-zinc-400 mb-2">{c.subject}</div>
              <div className="text-sm text-zinc-300 whitespace-pre-wrap">{c.message}</div>

              {/* Replies history */}
              {Array.isArray(c.replies) && c.replies.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/[0.04] space-y-2">
                  {c.replies.map((r) => (
                    <div key={r.id} className="bg-emerald-400/[0.04] border border-emerald-400/20 rounded-md p-2.5" data-testid={`contact-reply-${r.id}`}>
                      <div className="text-[10px] text-emerald-300 mb-1 flex items-center gap-1.5">
                        <Send className="h-2.5 w-2.5" />
                        <span>{r.sent_by_name || r.sent_by_email}</span>
                        <span className="text-zinc-500">·</span>
                        <span className="text-zinc-500">{new Date(r.sent_at).toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-zinc-200 whitespace-pre-wrap">{r.body}</div>
                      {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {r.attachments.map((a, i) => (
                            <a
                              key={i}
                              href={`${API}${a.url}`}
                              download={a.filename}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/[0.05] border border-white/10 hover:bg-white/[0.1] text-[10px] no-underline text-zinc-300"
                              data-testid={`contact-reply-att-${i}`}
                            >
                              <Paperclip className="h-2.5 w-2.5" />
                              <span className="truncate max-w-[150px]">{a.filename}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Reply button */}
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => openContactReply(c.id)}
                  className="text-xs px-3 py-1.5 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/25 inline-flex items-center gap-1.5"
                  data-testid={`contact-reply-btn-${c.id}`}
                >
                  <Send className="h-3 w-3" /> Répondre
                </button>
              </div>
            </div>
          ))}
          {contacts.length === 0 && <div className="text-center text-zinc-500 py-8">Aucun message</div>}
        </div>
      )}

      {/* ─── CONTACT REPLY MODAL ─────────────────────────────── */}
      {contactReplyOpen && (() => {
        const c = contacts.find((x) => x.id === contactReplyOpen);
        if (!c) return null;
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeContactReply(); }}
            data-testid="contact-reply-modal"
          >
            <div className="bg-[#0f1115] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-cyan-300" />
                  <div>
                    <div className="text-sm font-semibold">Répondre à {c.name}</div>
                    <div className="text-[10px] text-zinc-500">{c.email} · Sujet : {c.subject}</div>
                  </div>
                </div>
                <button onClick={closeContactReply} disabled={contactReplyBusy} className="p-1.5 rounded-md hover:bg-white/[0.05] disabled:opacity-40" data-testid="contact-reply-close">
                  <XCircle className="h-4 w-4 text-zinc-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
                {/* Original message preview */}
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-md p-3">
                  <div className="text-[10px] text-zinc-500 mb-1">Message original ({new Date(c.created_at).toLocaleString()})</div>
                  <div className="text-xs text-zinc-300 whitespace-pre-wrap line-clamp-6">{c.message}</div>
                </div>
                {/* Reply textarea */}
                <textarea
                  value={contactReplyBody}
                  onChange={(e) => setContactReplyBody(e.target.value)}
                  placeholder="Écrivez votre réponse…"
                  rows={8}
                  className="w-full bg-[#0a0a10] border border-white/10 rounded-md px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-cyan-400/50 resize-none"
                  data-testid="contact-reply-textarea"
                  disabled={contactReplyBusy}
                />
                {/* Attachments preview */}
                {contactReplyAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {contactReplyAttachments.map((a, i) => (
                      <div key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/[0.05] border border-white/10 text-[11px] text-zinc-300" data-testid={`contact-reply-att-prev-${i}`}>
                        <Paperclip className="h-3 w-3 text-cyan-300" />
                        <span className="truncate max-w-[160px]">{a.filename}</span>
                        <span className="text-zinc-500">({Math.round(a.size / 1024)} Ko)</span>
                        <button
                          onClick={() => setContactReplyAttachments((arr) => arr.filter((_, idx) => idx !== i))}
                          disabled={contactReplyBusy}
                          className="ml-1 text-zinc-500 hover:text-rose-300"
                          data-testid={`contact-reply-att-remove-${i}`}
                        >
                          <Trash2 className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {contactReplyMsg.text && (
                  <div className={`text-xs px-3 py-2 rounded-md ${contactReplyMsg.kind === "ok" ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-300" : "bg-rose-500/10 border border-rose-500/30 text-rose-300"}`} data-testid="contact-reply-msg">
                    {contactReplyMsg.text}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-white/[0.06] bg-white/[0.02]">
                <label className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-cyan-300 cursor-pointer" data-testid="contact-reply-attach-label">
                  <Paperclip className="h-3.5 w-3.5" />
                  Joindre fichier
                  <input
                    type="file"
                    multiple
                    onChange={onContactAttach}
                    className="hidden"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                    disabled={contactReplyBusy}
                    data-testid="contact-reply-attach-input"
                  />
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={closeContactReply}
                    disabled={contactReplyBusy}
                    className="text-xs px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] disabled:opacity-40"
                    data-testid="contact-reply-cancel"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={sendContactReply}
                    disabled={contactReplyBusy || !contactReplyBody.trim()}
                    className="text-xs px-3 py-1.5 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/25 disabled:opacity-40 inline-flex items-center gap-1.5"
                    data-testid="contact-reply-send"
                  >
                    {contactReplyBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    {contactReplyBusy ? "Envoi…" : "Envoyer la réponse"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── AFFILIATES ───────────────────────────────────────── */}
      {tab === "affiliates" && (
        <div className="space-y-6" data-testid="admin-affiliates">
          <div className="glass rounded-2xl p-5">
            <h3 className="text-base font-semibold mb-3">Affiliés inscrits ({affiliates.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Nom</th>
                    <th className="px-3 py-2 text-left">Email</th>
                    <th className="px-3 py-2 text-left">Paiement</th>
                    <th className="px-3 py-2 text-right">Clients</th>
                    <th className="px-3 py-2 text-right">En attente</th>
                    <th className="px-3 py-2 text-right">Payé</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {affiliates.map((a) => (
                    <tr key={a.code} className="border-t border-white/[0.04]" data-testid={`admin-affiliate-${a.code}`}>
                      <td className="px-3 py-2 font-mono text-xs text-cyan-300">{a.code}</td>
                      <td className="px-3 py-2 text-xs">{a.full_name}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{a.email}</td>
                      <td className="px-3 py-2 text-xs">
                        <div>{a.payment_method === "usdt" ? "USDT" : "Mobile"}</div>
                        <div className="text-[10px] font-mono text-zinc-500 break-all max-w-[140px]">{a.payment_address}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{a.stats?.unique_clients || 0}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-amber-300">{(a.pending_commission_xof || 0).toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-emerald-300">{(a.paid_commission_xof || 0).toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2">
                        {a.status === "active"
                          ? <span className="text-emerald-400 inline-flex items-center gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />Actif</span>
                          : <span className="text-zinc-500 inline-flex items-center gap-1 text-xs"><XCircle className="h-3 w-3" />Désactivé</span>}
                      </td>
                      <td className="px-3 py-2">
                        {me?.role === "superadmin" && (
                          <div className="flex gap-1">
                            <button onClick={() => toggleAffiliateActive(a.code, a.status === "active")}
                              className="text-xs px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08]"
                              data-testid={`admin-affiliate-toggle-${a.code}`}>
                              {a.status === "active" ? "Désactiver" : "Activer"}
                            </button>
                            <button onClick={() => deleteAffiliate(a.code)}
                              className="text-xs px-2 py-1 rounded bg-red-400/10 hover:bg-red-400/20 text-red-300"
                              data-testid={`admin-affiliate-delete-${a.code}`}>
                              Supprimer
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {affiliates.length === 0 && (
                    <tr><td colSpan={9} className="text-center text-zinc-500 py-6 text-xs italic">Aucun affilié pour le moment</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-base font-semibold">Commissions</h3>
              <div className="flex gap-1">
                {[
                  { id: "pending", label: "En attente" },
                  { id: "paid", label: "Payées" },
                  { id: "", label: "Toutes" },
                ].map((f) => (
                  <button key={f.id} onClick={() => { setAffStatusFilter(f.id); loadCommissions(f.id); }}
                    className={`text-xs px-3 py-1.5 rounded border ${
                      affStatusFilter === f.id
                        ? "bg-cyan-400/10 border-cyan-400/40 text-cyan-200"
                        : "bg-white/[0.02] border-white/10 text-zinc-400"
                    }`}
                    data-testid={`admin-comm-filter-${f.id || "all"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Affilié</th>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-left">Commande</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2 text-center">Taux</th>
                    <th className="px-3 py-2 text-right">Commission</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((c) => (
                    <tr key={c.id} className="border-t border-white/[0.04]" data-testid={`admin-commission-${c.id}`}>
                      <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2 font-mono text-xs text-cyan-300">{c.affiliate_code}</td>
                      <td className="px-3 py-2 text-xs text-zinc-400">{c.client_email}</td>
                      <td className="px-3 py-2 text-xs font-mono">{c.order_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                          c.order_type === "renewal" ? "bg-amber-400/15 text-amber-300" : "bg-cyan-400/15 text-cyan-300"
                        }`}>
                          {c.order_type === "renewal" ? "Renouv." : "Nouveau"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{c.order_amount_xof.toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="text-[10px] uppercase font-mono bg-cyan-400/10 text-cyan-300 px-1.5 py-0.5 rounded">{c.rate_percent}%</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-emerald-300">{c.commission_xof.toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 text-xs">
                        {c.status === "paid"
                          ? <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Payée</span>
                          : <span className="text-amber-300 inline-flex items-center gap-1"><Loader className="h-3 w-3" />En attente</span>}
                      </td>
                      <td className="px-3 py-2">
                        {c.status === "pending" && me?.role === "superadmin" && (
                          <button onClick={() => markCommissionPaid(c.id)}
                            className="text-xs px-2 py-1 rounded bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-300"
                            data-testid={`admin-commission-pay-${c.id}`}>
                            Marquer payée
                          </button>
                        )}
                        {c.status === "paid" && c.paid_ref && (
                          <span className="text-[10px] text-zinc-500 font-mono">{c.paid_ref.slice(0, 12)}…</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {commissions.length === 0 && (
                    <tr><td colSpan={10} className="text-center text-zinc-500 py-6 text-xs italic">Aucune commission</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Admin promo codes */}
          {me?.role === "superadmin" && (
            <div className="glass rounded-2xl p-5" data-testid="admin-promos-section">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-base font-semibold inline-flex items-center gap-2"><Tag className="h-4 w-4 text-cyan-300" /> Codes promo admin</h3>
                <button onClick={() => setShowPromoForm((v) => !v)}
                  className="btn-primary px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1.5"
                  data-testid="admin-promo-new-btn">
                  <Tag className="h-3 w-3" /> {showPromoForm ? "Annuler" : "Nouveau code"}
                </button>
              </div>

              {showPromoForm && (
                <form onSubmit={createAdminPromo} className="grid sm:grid-cols-2 gap-3 mb-4 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg" data-testid="admin-promo-form">
                  <label className="block">
                    <span className="text-xs text-zinc-400 mb-1 block">Code (facultatif)</span>
                    <input type="text" value={promoForm.code}
                      onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                      className="input font-mono uppercase" maxLength={30}
                      placeholder="PROMO-NOEL2026" data-testid="admin-promo-code" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-zinc-400 mb-1 block">Remise (%) *</span>
                    <input type="number" min={1} max={100} required value={promoForm.discount_percent}
                      onChange={(e) => setPromoForm({ ...promoForm, discount_percent: e.target.value })}
                      className="input" data-testid="admin-promo-discount" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-zinc-400 mb-1 block">Valide du *</span>
                    <input type="date" required value={promoForm.valid_from}
                      onChange={(e) => setPromoForm({ ...promoForm, valid_from: e.target.value })}
                      className="input" data-testid="admin-promo-from" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-zinc-400 mb-1 block">Valide jusqu'au *</span>
                    <input type="date" required value={promoForm.valid_to}
                      onChange={(e) => setPromoForm({ ...promoForm, valid_to: e.target.value })}
                      className="input" data-testid="admin-promo-to" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-zinc-400 mb-1 block">Utilisations max (vide = illimité)</span>
                    <input type="number" min={1} value={promoForm.max_uses}
                      onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                      className="input" placeholder="ex: 100" data-testid="admin-promo-maxuses" />
                  </label>
                  <div className="sm:col-span-2 flex items-center justify-between">
                    {promoErr && <div className="text-xs text-red-300">{promoErr}</div>}
                    <button type="submit" className="btn-primary px-4 py-2 rounded-lg text-xs ml-auto inline-flex items-center gap-1.5"
                      data-testid="admin-promo-submit">
                      <Tag className="h-3 w-3" /> Créer
                    </button>
                  </div>
                </form>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Remise</th>
                      <th className="px-3 py-2 text-left">Validité</th>
                      <th className="px-3 py-2 text-right">Utilisations</th>
                      <th className="px-3 py-2 text-left">Statut</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminPromos.map((p) => (
                      <tr key={p.id} className="border-t border-white/[0.04]" data-testid={`admin-promo-${p.code}`}>
                        <td className="px-3 py-2 font-mono text-xs text-cyan-300">{p.code}</td>
                        <td className="px-3 py-2 text-sm font-semibold text-emerald-300">−{p.discount_percent}%</td>
                        <td className="px-3 py-2 text-[11px] text-zinc-500 font-mono">
                          {new Date(p.valid_from).toLocaleDateString()} → {new Date(p.valid_to).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{p.used_count}{p.max_uses ? `/${p.max_uses}` : ""}</td>
                        <td className="px-3 py-2 text-xs">
                          {p.status === "active"
                            ? <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Actif</span>
                            : <span className="text-zinc-500 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Désactivé</span>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1.5">
                            <button onClick={() => toggleAdminPromo(p.code, p.status === "active")}
                              className="text-xs px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08]"
                              data-testid={`admin-promo-toggle-${p.code}`}>
                              {p.status === "active" ? "Désactiver" : "Activer"}
                            </button>
                            <button onClick={() => deleteAdminPromo(p.code)}
                              className="text-xs px-2 py-1 rounded bg-red-400/10 hover:bg-red-400/20 text-red-300"
                              data-testid={`admin-promo-delete-${p.code}`}>
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {adminPromos.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-zinc-500 py-6 text-xs italic">Aucun code promo admin</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── PAYOUTS ───────────────────────────────────────── */}
      {tab === "payouts" && (
        <div className="space-y-5" data-testid="admin-payouts">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-base font-semibold inline-flex items-center gap-2"><Wallet className="h-4 w-4 text-cyan-300" /> Demandes de paiement des affiliés</h3>
              <div className="flex gap-1">
                {[
                  { id: "pending", label: "Non traitées" },
                  { id: "processing", label: "En cours" },
                  { id: "paid", label: "Traitées" },
                  { id: "rejected", label: "Rejetées" },
                  { id: "", label: "Toutes" },
                ].map((f) => (
                  <button key={f.id} onClick={() => { setPayoutFilter(f.id); loadPayouts(f.id); }}
                    className={`text-xs px-3 py-1.5 rounded border ${payoutFilter === f.id
                      ? "bg-cyan-400/10 border-cyan-400/40 text-cyan-200"
                      : "bg-white/[0.02] border-white/10 text-zinc-400"}`}
                    data-testid={`admin-payout-filter-${f.id || "all"}`}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Demande</th>
                    <th className="px-3 py-2 text-left">Affilié</th>
                    <th className="px-3 py-2 text-right">Montant</th>
                    <th className="px-3 py-2 text-left">Méthode / Adresse</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-right">Attente / SLA</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-t border-white/[0.04]" data-testid={`admin-payout-${p.id}`}>
                      <td className="px-3 py-2 text-[11px] font-mono text-zinc-500">
                        <div>{new Date(p.created_at).toLocaleDateString()}</div>
                        <div className="text-zinc-600">{p.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-mono text-cyan-300">{p.affiliate_code}</div>
                        <div className="text-zinc-500 text-[11px]">{p.affiliate_email}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-emerald-300">{p.amount_xof.toLocaleString("fr-FR")}</td>
                      <td className="px-3 py-2 text-xs">
                        <div>{p.payment_method === "usdt" ? "USDT" : "Mobile"}</div>
                        <div className="text-[10px] font-mono text-zinc-500 break-all max-w-[160px]">{p.payment_address_snapshot}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                          p.status === "pending" ? "bg-amber-400/15 text-amber-300" :
                          p.status === "processing" ? "bg-cyan-400/15 text-cyan-300" :
                          p.status === "paid" ? "bg-emerald-400/15 text-emerald-300" :
                          "bg-red-400/15 text-red-300"
                        }`}>{p.status === "pending" ? "Non traitée" : p.status === "processing" ? "En cours" : p.status === "paid" ? "Traitée" : "Rejetée"}</span>
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${p.overdue ? "text-red-300 font-bold" : "text-zinc-400"}`}>
                        {p.days_waiting} / {p.sla_days}j
                        {p.overdue && <div className="text-[10px] text-red-400">⚠ dépassé</div>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {p.status === "pending" && (
                            <button onClick={() => payoutAction(p.id, "process")}
                              className="text-xs px-2 py-1 rounded bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-200"
                              data-testid={`admin-payout-process-${p.id}`}>Traiter</button>
                          )}
                          {["pending", "processing"].includes(p.status) && me?.role === "superadmin" && (
                            <>
                              <button onClick={() => payoutAction(p.id, "pay")}
                                className="text-xs px-2 py-1 rounded bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-300"
                                data-testid={`admin-payout-pay-${p.id}`}>Payer</button>
                              <button onClick={() => payoutAction(p.id, "reject")}
                                className="text-xs px-2 py-1 rounded bg-red-400/10 hover:bg-red-400/20 text-red-300"
                                data-testid={`admin-payout-reject-${p.id}`}>Rejeter</button>
                            </>
                          )}
                          {p.status === "paid" && p.paid_ref && (
                            <span className="text-[10px] font-mono text-zinc-500">{p.paid_ref.slice(0, 16)}…</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {payouts.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-zinc-500 py-6 text-xs italic">Aucune demande</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── LICENSES (account-number change) ──────────────── */}
      {tab === "licenses" && (
        <div className="space-y-6" data-testid="admin-licenses">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Gestion des licences en cours</h3>
            </div>
            <p className="text-xs text-zinc-500 leading-relaxed">
              <b>Étape 1</b> — Saisir un numéro MT5 pour retrouver la commande associée.
              <b className="ml-2">Étape 2</b> — Choisir l'action à effectuer (réémission, changement de numéro…).
              <b className="ml-2">Étape 3</b> — Valider le formulaire correspondant.
              <b className="ml-2">Étape 4</b> — Recompiler le <code>.ex5</code> et l'uploader depuis l'onglet Commandes.
            </p>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">1. Rechercher la licence par numéro MT5</div>
            <div className="flex gap-2">
              <input
                type="text" placeholder="Ancien numéro de compte MT5…"
                value={licLookupNum}
                onChange={(e) => setLicLookupNum(e.target.value)}
                className="input flex-1"
                data-testid="admin-lic-lookup-input"
                onKeyDown={(e) => e.key === "Enter" && lookupLicense()}
              />
              <button
                type="button" onClick={lookupLicense} disabled={licBusy || !licLookupNum.trim()}
                className="btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-40"
                data-testid="admin-lic-lookup-btn"
              >
                {licBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Rechercher
              </button>
            </div>
          </div>

          {licOrder && (
            <div className="glass rounded-2xl p-6" data-testid="admin-lic-order-card">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Commande trouvée</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div><span className="text-zinc-500">Order&nbsp;ID:</span> <code className="text-cyan-300">{licOrder.order_id}</code></div>
                <div><span className="text-zinc-500">Statut:</span> <b>{licOrder.status}</b></div>
                <div><span className="text-zinc-500">Client:</span> {licOrder.full_name || "—"}</div>
                <div><span className="text-zinc-500">Email:</span> {licOrder.email}</div>
                <div><span className="text-zinc-500">Broker:</span> {licOrder.broker || "—"}</div>
                <div><span className="text-zinc-500">Plan:</span> {licOrder.plan_id || "—"}</div>
                <div className="sm:col-span-2">
                  <span className="text-zinc-500">Période de validité:</span>{" "}
                  {licOrder.duration_days ? <span className="font-mono text-xs">{licOrder.duration_days} jours</span> : "—"}
                  {licOrder.license_expiry && (
                    <span className="ml-3 text-xs">
                      <span className="text-zinc-500">expire le</span>{" "}
                      <span className={`font-mono ${licOrder.is_expired ? "text-red-300" : "text-emerald-300"}`}>
                        {licOrder.license_expiry.slice(0, 10)}
                      </span>
                      {licOrder.days_remaining !== null && licOrder.days_remaining !== undefined && (
                        licOrder.is_expired ? (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 text-[10px] font-semibold">
                            EXPIRÉE
                          </span>
                        ) : (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-cyan-400/15 text-cyan-300 border border-cyan-400/30 text-[10px] font-semibold">
                            J−{licOrder.days_remaining}
                          </span>
                        )
                      )}
                    </span>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-zinc-500">Comptes MT5 actuels:</span>{" "}
                  <span className="font-mono text-xs">
                    {(licOrder.mt5_accounts || []).join(", ") || "aucun"}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-zinc-500">Fichier .ex5 existant:</span>{" "}
                  {licOrder.ex5_filename ? (
                    <code className="text-emerald-300 text-xs">{licOrder.ex5_filename}</code>
                  ) : (
                    <span className="text-amber-300 text-xs">aucun fichier compilé</span>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-zinc-500">Changements déjà effectués:</span>{" "}
                  <b>{licOrder.change_count}</b>
                  {licOrder.change_count > 0 && (
                    <span className="text-amber-300 ml-2">(droit gratuit consommé)</span>
                  )}
                </div>
              </div>

              {(licOrder.license_changes || []).length > 0 && (
                <div className="mt-4 border-t border-white/[0.06] pt-4">
                  <div className="text-xs text-zinc-500 uppercase mb-2">Historique des changements</div>
                  <ul className="space-y-2 text-xs">
                    {licOrder.license_changes.map((h, i) => (
                      <li key={i} className="flex flex-wrap gap-x-3 gap-y-1 text-zinc-400">
                        <span className="font-mono text-zinc-500">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                        <code className="text-amber-300">{h.old_account}</code>
                        <span>→</span>
                        <code className="text-emerald-300">{h.new_account}</code>
                        <span className="text-zinc-500">par {h.by}</span>
                        {h.forced && <span className="text-red-300">(forcé)</span>}
                        {h.reason && <span className="italic text-zinc-400">"{h.reason}"</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(licOrder.license_reissues || []).length > 0 && (
                <div className="mt-4 border-t border-white/[0.06] pt-4">
                  <div className="text-xs text-zinc-500 uppercase mb-2">
                    Historique des réémissions ({licOrder.reissue_count})
                  </div>
                  <ul className="space-y-2 text-xs">
                    {licOrder.license_reissues.map((r, i) => (
                      <li key={i} className="flex flex-wrap gap-x-3 gap-y-1 text-zinc-400">
                        <span className="font-mono text-zinc-500">{(r.at || "").slice(0, 16).replace("T", " ")}</span>
                        <span className="text-cyan-300">♻️ réémission</span>
                        <span className="text-zinc-500">par {r.by}</span>
                        {r.expiry_locked && (
                          <span className="text-zinc-500">→ expire {r.expiry_locked.slice(0, 10)}</span>
                        )}
                        {r.reason && <span className="italic text-zinc-400">"{r.reason}"</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ─── 2. Action selector ───────────────────────────── */}
          {licOrder && (
            <div className="glass rounded-2xl p-6">
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                2. Action à effectuer sur cette licence
              </label>
              <select
                value={licAction}
                onChange={(e) => {
                  setLicAction(e.target.value);
                  setLicMsg({ kind: "", text: "" });
                  setLicReissueMsg({ kind: "", text: "" });
                }}
                className="input w-full text-sm"
                data-testid="admin-lic-action-select"
              >
                <option value="">— Sélectionner une action —</option>
                <option value="reissue">♻️ Réémettre la licence (client a perdu son fichier .ex5)</option>
                <option value="change">🔄 Changement de numéro MT5 (1 changement gratuit / licence)</option>
              </select>
              {licAction === "" && (
                <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                  Choisissez l'action à effectuer pour faire apparaître le formulaire correspondant.
                </p>
              )}
            </div>
          )}

          {/* ─── Réémission de licence ──────────────────────── */}
          {licOrder && licAction === "reissue" && (
            <div className="glass rounded-2xl p-6" data-testid="admin-lic-reissue-card">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">♻️ Réémettre la licence</div>
              <p className="text-xs text-zinc-500 leading-relaxed mb-4">
                Cas d'usage : le client a <b>perdu son fichier .ex5</b> (PC en panne, suppression accidentelle…) et veut récupérer son robot.
                Les <b>comptes MT5</b> et la <b>date d'expiration</b> sont préservés à l'identique — c'est juste un renvoi du fichier.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="email" placeholder="Email de livraison (pré-rempli)"
                  value={licReissueEmail} onChange={(e) => setLicReissueEmail(e.target.value)}
                  className="input" data-testid="admin-lic-reissue-email"
                  disabled={licOrder.is_expired}
                />
                <input
                  type="text" placeholder="Motif (optionnel) : perte fichier, changement PC…"
                  value={licReissueReason} onChange={(e) => setLicReissueReason(e.target.value)}
                  className="input" data-testid="admin-lic-reissue-reason"
                  disabled={licOrder.is_expired}
                />
              </div>
              {licReissueMsg.text && (
                <div className={`text-xs mt-3 ${licReissueMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                  {licReissueMsg.text}
                </div>
              )}
              <div className="flex items-center gap-3 mt-4">
                <button
                  type="button" onClick={submitLicenseReissue}
                  disabled={licReissueBusy || licOrder.is_expired}
                  className="btn-primary px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-40"
                  data-testid="admin-lic-reissue-btn"
                >
                  {licReissueBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Réémettre la licence
                </button>
                {licOrder.is_expired && (
                  <div className="text-xs text-red-300">
                    Licence expirée — réémission impossible. Le client doit renouveler son abonnement.
                  </div>
                )}
                {!licOrder.is_expired && licOrder.reissue_count > 0 && (
                  <div className="text-xs text-zinc-500">
                    Cette licence a déjà été réémise {licOrder.reissue_count} fois.
                  </div>
                )}
              </div>
            </div>
          )}

          {licOrder && licAction === "change" && (
            <div className="glass rounded-2xl p-6" data-testid="admin-lic-change-card">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">🔄 Changement de numéro MT5</div>
              <p className="text-xs text-zinc-500 leading-relaxed mb-4">
                Le client a droit à <b>1 changement gratuit</b> de numéro MT5 pendant la durée de sa licence (sous réserve de justification).
                {(licOrder.mt5_accounts || []).length > 1 && (
                  <> Cette licence comporte <b>{licOrder.mt5_accounts.length} comptes</b> — sélectionnez celui à remplacer ci-dessous.</>
                )}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">
                    Ancien numéro à remplacer
                  </label>
                  <select
                    value={licOldNum}
                    onChange={(e) => setLicOldNum(e.target.value)}
                    className="input w-full"
                    data-testid="admin-lic-old-num-select"
                  >
                    <option value="">— Sélectionner le numéro à remplacer —</option>
                    {(licOrder.mt5_accounts || []).map((acc) => (
                      <option key={acc} value={acc}>
                        {acc}{acc === licLookupNum ? "  (numéro recherché)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">
                    Nouveau numéro MT5
                  </label>
                  <input
                    type="text" placeholder="Ex : 12345678"
                    value={licNewNum} onChange={(e) => setLicNewNum(e.target.value)}
                    className="input w-full" data-testid="admin-lic-new-num"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">
                    Email de livraison
                  </label>
                  <input
                    type="email" placeholder="Email pré-rempli depuis la commande"
                    value={licNewEmail} onChange={(e) => setLicNewEmail(e.target.value)}
                    className="input w-full" data-testid="admin-lic-new-email"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">
                    Motif (optionnel)
                  </label>
                  <input
                    type="text" placeholder="Ex : changement de broker, perte d'accès…"
                    value={licReason} onChange={(e) => setLicReason(e.target.value)}
                    className="input w-full" data-testid="admin-lic-reason"
                  />
                </div>
              </div>
              {licOrder.change_count >= 1 && (
                <label className="flex items-center gap-2 mt-3 text-xs text-amber-300 cursor-pointer">
                  <input
                    type="checkbox" checked={licForce}
                    onChange={(e) => setLicForce(e.target.checked)}
                    data-testid="admin-lic-force-chk"
                  />
                  Forcer un changement supplémentaire (droit gratuit déjà consommé)
                </label>
              )}
              {licMsg.text && (
                <div className={`text-xs mt-3 ${licMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                  {licMsg.text}
                </div>
              )}
              <div className="flex items-center gap-3 mt-4">
                <button
                  type="button" onClick={submitLicenseChange}
                  disabled={licBusy || !licNewNum.trim() || !licOldNum.trim()}
                  className="btn-primary px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 disabled:opacity-40"
                  data-testid="admin-lic-submit-btn"
                >
                  {licBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Enregistrer le changement
                </button>
                <div className="text-xs text-zinc-500">
                  Le client recevra un email de confirmation. L'étape 3 (upload du nouveau <code>.ex5</code>)
                  se fait depuis l'onglet <b>Commandes</b>.
                </div>
              </div>
            </div>
          )}

          {!licOrder && !licBusy && licMsg.text && (
            <div className={`glass rounded-2xl p-4 text-sm ${licMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
              {licMsg.text}
            </div>
          )}
        </div>
      )}

      {/* ─── CUSTOM BUILD (superadmin) ───────────────────────── */}
      {tab === "custombuild" && me?.role === "superadmin" && (
        <div className="space-y-6" data-testid="admin-custombuild">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
              <div className="flex items-center gap-2">
                <FileCode className="h-4 w-4 text-cyan-400" />
                <h3 className="text-base font-semibold">Build manuel — hors workflow</h3>
              </div>
              <span
                className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300"
                data-testid="admin-cb-ea-version"
                title="Version actuelle du template EA disponible sur le serveur"
              >
                EA disponible : v{cbEaVersion}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Génère un fichier <code className="text-cyan-300">.mq5</code> personnalisé sans passer par une commande client.
              Réservé au superadmin pour les cas particuliers : démos, partenariats, remplacement express, comptes internes…
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Identifiant client (slug)
                </label>
                <input
                  value={cbSlug}
                  onChange={(e) => setCbSlug(e.target.value)}
                  placeholder="ex : Demo-Partenaire-Forex"
                  className="input font-mono"
                  data-testid="admin-cb-slug"
                />
                <div className="text-[10px] text-zinc-600 mt-1">Utilisé dans le nom du fichier (alphanum + tirets).</div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Comptes MT5 autorisés
                </label>
                <input
                  value={cbAccounts}
                  onChange={(e) => setCbAccounts(e.target.value)}
                  placeholder="ex : 123456, 789012   (vide = illimité *)"
                  className="input font-mono"
                  data-testid="admin-cb-accounts"
                />
                <div className="text-[10px] text-zinc-600 mt-1">Séparés par virgule, espace ou point-virgule. Vide = ClientAccountID = "*"</div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Durée de licence (jours)
                </label>
                <input
                  type="number"
                  value={cbDuration}
                  onChange={(e) => setCbDuration(e.target.value)}
                  placeholder="30"
                  className="input font-mono w-full"
                  data-testid="admin-cb-duration"
                />
                <div className="text-[10px] text-zinc-600 mt-1">
                  Saisir <code>-1</code> pour licence à vie. Plages courantes : 30 / 90 / 180 / 365.
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Date de début (LicenseStartDate)
                </label>
                <input
                  type="date"
                  value={cbStartDate}
                  onChange={(e) => setCbStartDate(e.target.value)}
                  className="input font-mono w-full"
                  data-testid="admin-cb-start-date"
                />
                <div className="text-[10px] text-zinc-600 mt-1">Par défaut : aujourd'hui.</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-5">
              <button
                onClick={buildCustomMq5}
                disabled={cbBuilding}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
                data-testid="admin-cb-build-btn"
              >
                {cbBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCode className="h-4 w-4" />}
                Générer le .mq5 personnalisé
              </button>
              {cbResult && (
                <button
                  onClick={downloadCustomMq5}
                  className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"
                  data-testid="admin-cb-download-btn"
                >
                  <Download className="h-4 w-4" /> Télécharger {cbResult.filename}
                </button>
              )}
            </div>
            {cbMsg.text && (
              <div
                className={`text-xs mt-3 ${cbMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                data-testid="admin-cb-msg"
              >
                {cbMsg.text}
              </div>
            )}
            {cbResult && (
              <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed border-t border-white/[0.06] pt-3">
                Étape suivante : ouvrez ce fichier dans MetaEditor (F7) pour produire le <code>.ex5</code>,
                puis utilisez le bloc « Envoi du .ex5 par email » ci-dessous.
              </div>
            )}
          </div>

          {/* File upload (shared by email + telegram) */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Upload className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Chargement du .ex5 compilé</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              Sélectionnez ici votre <code className="text-cyan-300">.ex5</code> compilé. Le même fichier sera utilisé
              pour les envois par <b>email</b> et par <b>Telegram</b> ci-dessous — pas besoin de le re-charger.
              <br/>
              <span className="text-cyan-300">ℹ️ Le fichier sera automatiquement renommé en <code>Dr Trade Scalper Pro.ex5</code> côté client</span>
              {" "}(le nom affiché sur le graphique MT5 reste donc identique pour tous, peu importe le nom de votre fichier source).
            </p>
            <input
              type="file"
              accept=".ex5"
              onChange={(e) => setCbEx5File(e.target.files?.[0] || null)}
              className="input file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-cyan-500/20 file:text-cyan-300 file:cursor-pointer"
              data-testid="admin-cb-ex5-file"
            />
            {cbEx5File ? (
              <div className="flex items-center gap-2 text-[11px] text-emerald-300 mt-2.5 font-mono">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {cbEx5File.name} · {(cbEx5File.size / 1024).toFixed(1)} Ko · prêt à être envoyé
              </div>
            ) : (
              <div className="text-[11px] text-zinc-500 mt-2.5">
                Aucun fichier sélectionné. Les boutons d'envoi seront désactivés tant qu'aucun .ex5 n'est chargé.
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-6">
            <button
              type="button"
              onClick={() => setCbEmailOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="admin-cb-email-toggle"
              aria-expanded={cbEmailOpen}
            >
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-cyan-400" />
                <h3 className="text-base font-semibold">Envoi du .ex5 par email</h3>
                {cbSendMsg.kind === "ok" && !cbEmailOpen && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                )}
              </div>
              {cbEmailOpen ? (
                <ChevronDown className="h-4 w-4 text-zinc-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-zinc-500" />
              )}
            </button>
            {cbEmailOpen && (
            <>
            <p className="text-xs text-zinc-500 mt-3 mb-5 leading-relaxed">
              Indiquez l'email destinataire et personnalisez le message si besoin. Le fichier <code className="text-cyan-300">.ex5</code>
              utilisé est celui chargé dans le bloc précédent. Aucune trace n'est créée dans la base des commandes.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Email du destinataire *
                </label>
                <input
                  type="email"
                  value={cbRecipient}
                  onChange={(e) => setCbRecipient(e.target.value)}
                  placeholder="client@example.com"
                  className="input font-mono"
                  data-testid="admin-cb-recipient"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Nom du client (optionnel)
                </label>
                <input
                  value={cbClientName}
                  onChange={(e) => setCbClientName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="input"
                  data-testid="admin-cb-client-name"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Sujet de l'email
                </label>
                <input
                  value={cbSubject}
                  onChange={(e) => setCbSubject(e.target.value)}
                  className="input"
                  data-testid="admin-cb-subject"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Corps du message (laisser vide pour utiliser le template par défaut)
                </label>
                <textarea
                  value={cbBody}
                  onChange={(e) => setCbBody(e.target.value)}
                  rows={6}
                  placeholder="Texte personnalisé… ou laisser vide pour le message standard d'installation."
                  className="input w-full font-mono text-xs leading-relaxed"
                  data-testid="admin-cb-body"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={sendCustomEx5}
                disabled={cbSending || !cbEx5File || !cbRecipient}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
                data-testid="admin-cb-send-btn"
              >
                {cbSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer le .ex5 par email
              </button>
            </div>
            {cbSendMsg.text && (
              <div
                className={`text-xs mt-3 ${cbSendMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                data-testid="admin-cb-send-msg"
              >
                {cbSendMsg.text}
              </div>
            )}
            </>
            )}
          </div>

          {/* Telegram delivery */}
          <div className="glass rounded-2xl p-6">
            <button
              type="button"
              onClick={() => setCbTgOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
              data-testid="admin-cb-tg-toggle"
              aria-expanded={cbTgOpen}
            >
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-cyan-400" />
                <h3 className="text-base font-semibold">Envoi du .ex5 via Telegram</h3>
                {cbTgMsg.kind === "ok" && !cbTgOpen && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                )}
              </div>
              {cbTgOpen ? (
                <ChevronDown className="h-4 w-4 text-zinc-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-zinc-500" />
              )}
            </button>
            {cbTgOpen && (
            <>
            <p className="text-xs text-zinc-500 mt-3 mb-5 leading-relaxed">
              Réutilise le fichier <code className="text-cyan-300">.ex5</code> chargé en haut.
              Le client doit avoir démarré une conversation avec le bot, ou être membre d'un groupe où le bot est admin.
              Limite Telegram : 50 MB par fichier.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  chat_id Telegram *
                </label>
                <input
                  value={cbTgChatId}
                  onChange={(e) => setCbTgChatId(e.target.value)}
                  placeholder="123456789  ou  -1001234567890  ou  @username"
                  className="input font-mono"
                  data-testid="admin-cb-tg-chatid"
                />
                <div className="text-[10px] text-zinc-600 mt-1 leading-relaxed">
                  Numérique pour 1-1 · Préfixé <code>-100</code> pour groupes/canaux · <code>@username</code> public.
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500 block mb-1.5">
                  Caption (optionnelle, max 1024 caractères)
                </label>
                <input
                  value={cbTgCaption}
                  onChange={(e) => setCbTgCaption(e.target.value)}
                  placeholder="Texte d'accompagnement… vide = template par défaut"
                  className="input"
                  data-testid="admin-cb-tg-caption"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={sendCustomEx5Telegram}
                disabled={cbTgSending || !cbEx5File || !cbTgChatId}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm disabled:opacity-50"
                data-testid="admin-cb-tg-send-btn"
              >
                {cbTgSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer le .ex5 via Telegram
              </button>
              {!cbEx5File && (
                <span className="text-[11px] text-amber-300">
                  ⚠️ Sélectionnez d'abord un fichier .ex5 dans le bloc « Chargement du .ex5 compilé ».
                </span>
              )}
            </div>
            {cbTgMsg.text && (
              <div
                className={`text-xs mt-3 ${cbTgMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                data-testid="admin-cb-tg-msg"
              >
                {cbTgMsg.text}
              </div>
            )}
            </>
            )}
          </div>

          {/* History */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-cyan-400" />
                <h3 className="text-base font-semibold">Historique des builds manuels</h3>
                <span className="text-xs text-zinc-500 font-mono">({cbHistory.length})</span>
              </div>
              <button
                onClick={loadCustomBuildHistory}
                disabled={cbHistLoading}
                className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs"
                data-testid="admin-cb-history-refresh"
              >
                {cbHistLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Refresh
              </button>
            </div>
            {cbHistory.length === 0 ? (
              <div className="text-xs text-zinc-500 py-6 text-center">
                Aucun build manuel enregistré pour le moment.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="admin-cb-history-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Version</th>
                      <th className="text-left py-2 px-2">Slug</th>
                      <th className="text-left py-2 px-2">Comptes</th>
                      <th className="text-left py-2 px-2">Durée</th>
                      <th className="text-left py-2 px-2">Date début</th>
                      <th className="text-left py-2 px-2">Admin</th>
                      <th className="text-center py-2 px-2">Email</th>
                      <th className="text-center py-2 px-2">Telegram</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cbHistory.map((b) => (
                      <tr key={b.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                        <td className="py-2 px-2 font-mono text-zinc-400">
                          {new Date(b.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td className="py-2 px-2 font-mono">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-cyan-300">
                            v{b.ea_version || "?"}
                          </span>
                        </td>
                        <td className="py-2 px-2 font-mono text-cyan-300">{b.client_slug}</td>
                        <td className="py-2 px-2 font-mono">
                          {b.accounts_count === 0 ? (
                            <span className="text-amber-300">* illimité</span>
                          ) : (
                            <span className="text-zinc-300">
                              {b.mt5_accounts.slice(0, 3).join(", ")}
                              {b.mt5_accounts.length > 3 && ` +${b.mt5_accounts.length - 3}`}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 font-mono">
                          {b.duration_days === -1 ? (
                            <span className="text-emerald-300">à vie</span>
                          ) : (
                            <span>{b.duration_days} j</span>
                          )}
                        </td>
                        <td className="py-2 px-2 font-mono text-zinc-500">{b.start_date || "—"}</td>
                        <td className="py-2 px-2 text-zinc-400">{b.created_by}</td>
                        <td className="py-2 px-2 text-center">
                          {(b.ex5_sent_email || []).length > 0 ? (
                            <span
                              className="text-emerald-300 font-mono"
                              title={(b.ex5_sent_email || []).map((e) => `${e.to} (${e.at?.slice(0,16)})`).join("\n")}
                            >
                              ✓ {(b.ex5_sent_email || []).length}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {(b.ex5_sent_telegram || []).length > 0 ? (
                            <span
                              className="text-emerald-300 font-mono"
                              title={(b.ex5_sent_telegram || []).map((e) => `${e.to} (${e.at?.slice(0,16)})`).join("\n")}
                            >
                              ✓ {(b.ex5_sent_telegram || []).length}
                            </span>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── CHATBOT (superadmin only) ────────────────────── */}
      {tab === "chatbot" && me?.role === "superadmin" && (
        <div className="space-y-6" data-testid="admin-chatbot">
          {/* Mon alias dans le chat visiteur */}
          <div className="glass rounded-2xl p-6" data-testid="admin-alias-card">
            <div className="flex items-center gap-2 mb-1">
              <UserRound className="h-4 w-4 text-violet-300" />
              <h3 className="text-base font-semibold">Mon alias dans le chat visiteur</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              Ce nom apparaît au visiteur quand vous prenez en charge sa conversation
              (« Bienvenue Jean, je suis <span className="text-violet-300">{aliasDraft || "<votre alias>"}</span> ») et en entête de chacun de vos messages.
              Si vide, c'est votre nom complet ou la partie locale de votre email qui est affichée. Chaque administrateur peut définir le sien.
            </p>
            <div className="flex gap-2 max-w-xl">
              <input
                type="text"
                placeholder="Ex: Sophie · Conseiller Dr Trade · Michel"
                value={aliasDraft}
                onChange={(e) => setAliasDraft(e.target.value)}
                maxLength={40}
                className="flex-1 px-3 py-2 rounded-lg text-sm bg-white/[0.03] border border-white/10 focus:border-violet-400/50 outline-none"
                data-testid="admin-alias-input"
              />
              <button
                onClick={saveAlias}
                disabled={aliasBusy}
                className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="admin-alias-save"
              >
                {aliasBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
            </div>
            {aliasMsg.text && (
              <div className={`text-xs mt-3 ${aliasMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                data-testid="admin-alias-msg">
                {aliasMsg.text}
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Équipe support Chat — Telegram</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Configurez les assistants qui reçoivent les messages du chat visiteur sur leur Telegram. Selon
              le mode ci-dessous, les notifications <code>#hdo-XXXXXX</code> sont soit envoyées à
              <b> tous en simultané</b> (broadcast), soit à <b>un seul assistant à la fois</b> (rotation
              round-robin).
            </p>

            <div className="mb-4">
              <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5 block">
                Groupe Telegram dédié (optionnel, pour supervision)
              </label>
              <input
                type="text"
                placeholder="-1001234567890"
                value={chatTeamDraft.chat_group_chat_id || ""}
                onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_group_chat_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                data-testid="chatbot-group-id"
              />
              <div className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                ID du groupe (commence par <code>-100…</code>). Toutes les notifs y sont postées en plus des
                assistants. Pratique pour avoir une visibilité globale.
                Ajoutez <code>@DocTrades_bot</code> comme membre du groupe.
                Trouver l'ID : ajoutez <code>@getidsbot</code> au groupe → tapez <code>/id</code>.
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={testTelegramConfig}
                  disabled={chatTestBusy}
                  className="text-xs px-3 py-1.5 rounded-md bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/25 inline-flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="chatbot-test-telegram"
                  title="Envoyer un message ping aux destinataires actuellement enregistrés"
                >
                  {chatTestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Tester le bot Telegram
                </button>
                <span className="text-[10px] text-zinc-500">Pensez à <b>enregistrer</b> avant le test si vous venez de modifier la config.</span>
              </div>
              {chatTestResult && (
                <div className="mt-2 text-[11px]" data-testid="chatbot-test-result">
                  {chatTestResult.error && (
                    <div className="text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2.5">
                      ❌ {chatTestResult.error}
                    </div>
                  )}
                  {Array.isArray(chatTestResult.results) && chatTestResult.results.length > 0 && (
                    <div className="space-y-1 mt-1">
                      {chatTestResult.results.map((r, i) => (
                        <div key={i} className={`px-2.5 py-1.5 rounded border text-[11px] ${r.ok ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-200" : "bg-red-500/10 border-red-500/30 text-red-200"}`}>
                          <span className="font-mono">{r.chat_id}</span>
                          {r.ok ? <span className="ml-2">✓ Message envoyé</span>
                                : <span className="ml-2">✗ HTTP {r.http || "—"} — {r.error || "Échec"}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-white/[0.06] pt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-[11px] uppercase tracking-wider text-zinc-500">
                  Assistants individuels ({(chatTeamDraft.chat_assistants || []).length})
                </label>
                <button
                  onClick={addAssistant}
                  className="text-xs px-2.5 py-1 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25"
                  data-testid="chatbot-add-btn"
                >
                  + Ajouter
                </button>
              </div>

              {(chatTeamDraft.chat_assistants || []).length === 0 && (
                <div className="text-xs text-zinc-500 italic py-3 text-center">
                  Aucun assistant configuré. Ajoutez au moins 1 assistant ou un groupe ci-dessus.
                </div>
              )}

              <div className="space-y-2">
                {(chatTeamDraft.chat_assistants || []).map((a, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`chatbot-row-${i}`}>
                    <input
                      type="text"
                      placeholder="Nom (ex: Michel)"
                      value={a.label || ""}
                      onChange={(e) => updateAssistant(i, { label: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                    />
                    <input
                      type="text"
                      placeholder="chat_id (ex: 462737224)"
                      value={a.chat_id || ""}
                      onChange={(e) => updateAssistant(i, { chat_id: e.target.value })}
                      className="flex-1 px-3 py-2 rounded-lg text-sm font-mono"
                    />
                    <label className="flex items-center gap-1.5 text-[11px] text-zinc-300 cursor-pointer select-none px-2.5 py-2 rounded-md border border-white/10 hover:border-white/20"
                      title="D'astreinte (reçoit les notifications)">
                      <input
                        type="checkbox"
                        checked={a.on_duty !== false}
                        onChange={(e) => updateAssistant(i, { on_duty: e.target.checked })}
                        className="accent-emerald-400"
                      />
                      Astreinte
                    </label>
                    <button
                      onClick={() => removeAssistant(i)}
                      className="px-2.5 py-2 rounded-md bg-red-400/10 border border-red-400/30 text-red-300 hover:bg-red-400/20"
                      title="Retirer"
                      data-testid={`chatbot-remove-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
                Chaque assistant doit : (1) ouvrir Telegram et démarrer une conversation avec{" "}
                <code>@DocTrades_bot</code> (lui envoyer <code>/start</code>),
                (2) récupérer son <code>chat_id</code> via{" "}
                <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>.
              </div>
            </div>
          </div>

          {/* ─── Rotation round-robin ──────────────────── */}
          <div className="glass rounded-2xl p-6" data-testid="admin-rotation-section">
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Mode d'assignation des demandes</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Choisissez comment les demandes <code>#hdo-XXXXXX</code> sont distribuées entre les assistants
              configurés ci-dessus.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <button
                onClick={() => setChatTeamDraft({ ...chatTeamDraft, chat_rotation_enabled: false })}
                className={`text-left p-4 rounded-xl border transition ${
                  !chatTeamDraft.chat_rotation_enabled
                    ? "border-cyan-400/60 bg-cyan-400/10"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid="rotation-mode-broadcast"
              >
                <div className="text-sm font-semibold mb-1">📢 Broadcast (tous)</div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">
                  Chaque demande est envoyée à <b>tous les assistants</b> en simultané. Le plus rapide répond.
                  Idéal avec 1-2 personnes.
                </div>
              </button>
              <button
                onClick={() => setChatTeamDraft({ ...chatTeamDraft, chat_rotation_enabled: true })}
                className={`text-left p-4 rounded-xl border transition ${
                  chatTeamDraft.chat_rotation_enabled
                    ? "border-cyan-400/60 bg-cyan-400/10"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
                data-testid="rotation-mode-rr"
              >
                <div className="text-sm font-semibold mb-1">🔄 Round-robin (rotation)</div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">
                  Chaque demande est assignée à <b>un seul assistant</b> (rotation équitable). Si pas de
                  réponse dans le délai, les autres sont notifiés pour reprendre.
                </div>
              </button>
            </div>

            {chatTeamDraft.chat_rotation_enabled && (
              <div className="p-4 rounded-xl bg-cyan-400/5 border border-cyan-400/20">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                  <span>Délai de reprise si pas de réponse</span>
                  <span className="text-cyan-300 font-mono text-sm normal-case tracking-normal">
                    {chatTeamDraft.chat_rotation_reclaim_minutes ?? 2} min
                  </span>
                </div>
                <input
                  type="range"
                  min="1" max="15" step="1"
                  value={chatTeamDraft.chat_rotation_reclaim_minutes ?? 2}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_rotation_reclaim_minutes: e.target.value })}
                  className="w-full accent-cyan-400"
                  data-testid="rotation-reclaim-min"
                />
                <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                  <span>1 min</span><span>7 min</span><span>15 min</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
                  Si l'assistant assigné n'a toujours pas ouvert sa conversation Telegram après ce délai,
                  une notification est broadcastée aux autres assistants pour qu'ils prennent le relais.
                  Le premier qui répond devient l'interlocuteur.
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/[0.06]">
              <button onClick={saveChatTeamSettings} disabled={chatTeamBusy}
                className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="chatbot-save">
                <Save className="h-4 w-4" /> Enregistrer tout
              </button>
              {chatTeamMsg.text && (
                <div className={`text-xs ${chatTeamMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                  data-testid="chatbot-msg">
                  {chatTeamMsg.text}
                </div>
              )}
            </div>
          </div>

          {/* ─── Auto-messages côté visiteur ──────────────────── */}
          <div className="glass rounded-2xl p-6" data-testid="admin-automsg-section">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Messages automatiques côté visiteur</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Le bot envoie automatiquement un message de bienvenue dès le démarrage du bridge,
              puis des rappels périodiques tant qu'aucun assistant n'a pris la conversation.
            </p>

            <div className="space-y-4">
              {/* Welcome */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatTeamDraft.chat_welcome_enabled !== false}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_welcome_enabled: e.target.checked })}
                    className="accent-cyan-400"
                  />
                  Message de bienvenue (instantané)
                </label>
                <textarea
                  rows={2}
                  value={chatTeamDraft.chat_welcome_template || ""}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_welcome_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  placeholder="Merci {first_name}, un opérateur va vous prendre en charge sous quelques instants."
                />
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Variables : <code>{"{first_name}"}</code>, <code>{"{last_name}"}</code>, <code>{"{full_name}"}</code>
                </div>
              </div>

              {/* Queue */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatTeamDraft.chat_queue_enabled !== false}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_queue_enabled: e.target.checked })}
                    className="accent-cyan-400"
                  />
                  Message de file d'attente (récurrent)
                </label>
                <textarea
                  rows={2}
                  value={chatTeamDraft.chat_queue_template || ""}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_queue_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  placeholder="Vous êtes le {position}{ord} dans la file d'attente. Merci de patienter, un conseiller arrive bientôt."
                />
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Variables : <code>{"{position}"}</code>, <code>{"{ord}"}</code> (ex: <code>1er</code> / <code>3ème</code>)
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                    <span>Intervalle</span>
                    <span className="text-cyan-300 font-mono text-sm normal-case tracking-normal">
                      toutes les {chatTeamDraft.chat_queue_interval_minutes ?? 2} min
                    </span>
                  </div>
                  <input
                    type="range" min="1" max="10" step="1"
                    value={chatTeamDraft.chat_queue_interval_minutes ?? 2}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_queue_interval_minutes: e.target.value })}
                    className="w-full accent-cyan-400"
                  />
                  <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                    <span>1 min</span><span>5 min</span><span>10 min</span>
                  </div>
                </div>
              </div>
              {/* Assignment auto-message */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="assignment-template-card">
                <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatTeamDraft.chat_assignment_enabled !== false}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_assignment_enabled: e.target.checked })}
                    className="accent-cyan-400"
                    data-testid="assignment-enabled"
                  />
                  Message d'assignation (à la prise en charge)
                </label>
                <textarea
                  rows={2}
                  value={chatTeamDraft.chat_assignment_template || ""}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_assignment_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  placeholder="Bienvenue {first_name}, je suis {assistant_name}. Comment puis-je vous aider ?"
                  data-testid="assignment-template"
                />
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Variables : <code>{"{first_name}"}</code>, <code>{"{last_name}"}</code>, <code>{"{full_name}"}</code>, <code>{"{assistant_name}"}</code>
                  <span className="ml-2 text-zinc-600">(envoyé automatiquement quand un assistant prend la conversation, depuis Telegram ou le BO)</span>
                </div>
              </div>

              {/* Mode "absent" — quand tous les assistants ont on_duty=false */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="away-template-card">
                <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatTeamDraft.chat_away_enabled !== false}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_away_enabled: e.target.checked })}
                    className="accent-amber-400"
                    data-testid="away-enabled"
                  />
                  Mode "Absent" — message d'indisponibilité
                </label>
                <textarea
                  rows={2}
                  value={chatTeamDraft.chat_away_template || ""}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_away_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  placeholder="Bonjour {first_name}, notre équipe est actuellement indisponible…"
                  data-testid="away-template"
                />
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Variables : <code>{"{first_name}"}</code>, <code>{"{last_name}"}</code>, <code>{"{full_name}"}</code>
                  <span className="ml-2 text-amber-300/80">Envoyé automatiquement quand <b>tous</b> les assistants ci-dessus ont leur badge "Astreinte" décoché.</span>
                </div>
              </div>

              {/* Auto-close des sessions inactives */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="inactivity-close-card">
                <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={chatTeamDraft.chat_inactivity_close_enabled !== false}
                    onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_inactivity_close_enabled: e.target.checked })}
                    className="accent-red-400"
                    data-testid="inactivity-close-enabled"
                  />
                  Auto-fermeture des sessions inactives
                </label>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500">Délai d'inactivité</span>
                  <span className="text-red-300 font-mono text-sm">
                    {chatTeamDraft.chat_inactivity_close_minutes ?? 30} min
                  </span>
                </div>
                <input
                  type="range"
                  min="5" max="240" step="5"
                  value={chatTeamDraft.chat_inactivity_close_minutes ?? 30}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_inactivity_close_minutes: parseInt(e.target.value, 10) })}
                  className="w-full accent-red-400 mb-2"
                  data-testid="inactivity-close-min"
                />
                <textarea
                  rows={2}
                  value={chatTeamDraft.chat_inactivity_close_template || ""}
                  onChange={(e) => setChatTeamDraft({ ...chatTeamDraft, chat_inactivity_close_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  placeholder="Conversation fermée pour inactivité ({minutes} min)…"
                  data-testid="inactivity-close-template"
                />
                <div className="text-[10px] text-zinc-500 mt-1.5">
                  Variable : <code>{"{minutes}"}</code>
                  <span className="ml-2 text-zinc-600">(scan toutes les 2 min ; les sessions sans activité depuis le délai sont fermées avec ce message final)</span>
                </div>
              </div>
            </div>
          </div>
          <div className="glass rounded-2xl p-6 border border-cyan-400/20">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-cyan-400" /> Comment ça marche — côté assistant
            </h3>
            <ol className="text-xs text-zinc-400 space-y-1.5 leading-relaxed list-decimal list-inside">
              <li>Un visiteur demande à parler à un humain dans le chat du site.</li>
              <li>Vous recevez <code>🆕 #hdo-XXXXXX</code> sur Telegram (uniquement si vous êtes <b>d'astreinte</b>).</li>
              <li>Soit vous appuyez sur <b>Répondre</b> dans Telegram, soit vous cliquez sur <b>Prendre</b> dans la table ci-dessous.</li>
              <li>Le premier qui agit prend la conversation. Les autres passent en silence (mute).</li>
              <li>Pour clôturer : tapez <code>/end</code> en reply Telegram, ou attendez que le visiteur ferme.</li>
            </ol>
          </div>

          {/* ─── Monitoring live des sessions ─── */}
          <div className="glass rounded-2xl p-6" data-testid="admin-chat-monitoring">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-400" />
                <h3 className="text-base font-semibold">Sessions en direct</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-white/10 overflow-hidden text-xs">
                  {["active", "closed", "all"].map((s) => (
                    <button
                      key={s}
                      onClick={() => { setChatSessionsFilter(s); loadChatSessions(s); }}
                      className={`px-3 py-1.5 ${
                        chatSessionsFilter === s
                          ? "bg-emerald-400/20 text-emerald-200"
                          : "text-zinc-400 hover:bg-white/[0.03]"
                      }`}
                    >
                      {s === "active" ? "Actives" : s === "closed" ? "Fermées" : "Toutes"}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => loadChatSessions(chatSessionsFilter)}
                  disabled={chatSessionsLoading}
                  className="p-1.5 rounded-md hover:bg-white/[0.05] disabled:opacity-40"
                  title="Rafraîchir"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${chatSessionsLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            {chatSessions.length === 0 ? (
              <div className="text-xs text-zinc-500 italic text-center py-6">
                Aucune session {chatSessionsFilter === "active" ? "active" : chatSessionsFilter === "closed" ? "fermée" : ""} pour le moment.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                    <tr>
                      <th className="text-left py-2 pr-2">#hdo</th>
                      <th className="text-left py-2 pr-2">Visiteur</th>
                      <th className="text-left py-2 pr-2">Assignation</th>
                      <th className="text-right py-2 pr-2">File</th>
                      <th className="text-right py-2 pr-2">Âge</th>
                      <th className="text-right py-2 pr-2">Inactif</th>
                      <th className="text-right py-2 pr-2">Msgs</th>
                      <th className="text-center py-2 pr-2">Statut</th>
                      <th className="text-right py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chatSessions.map((s) => {
                      const ageM = Math.floor((s.age_seconds || 0) / 60);
                      const inactM = Math.floor((s.inactive_seconds || 0) / 60);
                      const reclaimed = s.reclaim_broadcasted;
                      return (
                        <tr key={s.handoff_id} className="border-b border-white/[0.04]">
                          <td className="py-2 pr-2 font-mono">#{s.handoff_id}</td>
                          <td className="py-2 pr-2">
                            <div>{s.user_name}</div>
                            <div className="text-[10px] text-zinc-500">{s.email || s.telegram_handle || "-"}</div>
                          </td>
                          <td className="py-2 pr-2">
                            {s.claimed_by ? (
                              <span className="text-emerald-300 text-[11px]">✓ {s.claimed_by.startsWith("bo:") ? `BO ${s.claimed_by.slice(3)}` : <code>{s.claimed_by}</code>}</span>
                            ) : s.assigned_to ? (
                              <span className={reclaimed ? "text-red-300 text-[11px]" : "text-amber-300 text-[11px]"}>
                                {reclaimed ? "⚠️ timeout " : "→ "}<code>{s.assigned_to}</code>
                              </span>
                            ) : (
                              <span className="text-zinc-500 text-[11px]">Broadcast</span>
                            )}
                          </td>
                          <td className="py-2 pr-2 text-right text-cyan-300 font-mono">
                            {s.queue_position > 0 ? `#${s.queue_position}` : "-"}
                          </td>
                          <td className="py-2 pr-2 text-right text-zinc-400">{ageM}min</td>
                          <td className="py-2 pr-2 text-right text-zinc-400">{inactM}min</td>
                          <td className="py-2 pr-2 text-right font-mono">
                            {s.messages_total}
                            {s.messages_photos > 0 && <span className="text-cyan-400"> · 📷{s.messages_photos}</span>}
                          </td>
                          <td className="py-2 pr-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${
                              s.status === "active"
                                ? "bg-emerald-400/15 text-emerald-300"
                                : "bg-zinc-500/15 text-zinc-400"
                            }`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="py-2 text-right">
                            {s.status === "active" && (
                              <div className="flex gap-1 justify-end flex-wrap">
                                {!s.claimed_by && (
                                  <button
                                    onClick={() => claimSession(s.handoff_id)}
                                    className="text-[10px] px-2 py-1 rounded-md bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/25"
                                    title="Prendre en charge cette session"
                                    data-testid={`bo-claim-${s.handoff_id}`}
                                  >
                                    Prendre
                                  </button>
                                )}
                                {!s.claimed_by && me?.role === "superadmin" && (
                                  <button
                                    onClick={() => assignSession(s.handoff_id)}
                                    className="text-[10px] px-2 py-1 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25"
                                    title="Assigner manuellement à un assistant"
                                    data-testid={`bo-assign-${s.handoff_id}`}
                                  >
                                    Assigner
                                  </button>
                                )}
                                {s.claimed_by && (s.claimed_by === `bo:${me?.email}` || me?.role === "superadmin") && (
                                  <button
                                    onClick={() => releaseSession(s.handoff_id)}
                                    className="text-[10px] px-2 py-1 rounded-md bg-amber-400/15 border border-amber-400/40 text-amber-300 hover:bg-amber-400/25"
                                    title="Libérer la session (re-disponible)"
                                    data-testid={`bo-release-${s.handoff_id}`}
                                  >
                                    Libérer
                                  </button>
                                )}
                                {(!s.claimed_by || s.claimed_by === `bo:${me?.email}` || me?.role === "superadmin") && (
                                  <button
                                    onClick={() => closeSession(s.handoff_id)}
                                    className="text-[10px] px-2 py-1 rounded-md bg-red-400/15 border border-red-400/40 text-red-300 hover:bg-red-400/25"
                                    title="Fermer définitivement cette session"
                                    data-testid={`bo-close-${s.handoff_id}`}
                                  >
                                    Fermer
                                  </button>
                                )}
                                <button
                                  onClick={() => openReplyPanel(s.handoff_id)}
                                  className="text-[10px] px-2 py-1 rounded-md bg-violet-400/15 border border-violet-400/40 text-violet-200 hover:bg-violet-400/25 inline-flex items-center gap-1"
                                  title="Ouvrir le chat (répondre depuis le BO)"
                                  data-testid={`bo-reply-${s.handoff_id}`}
                                >
                                  Répondre
                                  {unreadByHid[s.handoff_id] > 0 && (
                                    <span
                                      className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold ml-0.5"
                                      data-testid={`bo-unread-${s.handoff_id}`}
                                    >
                                      {unreadByHid[s.handoff_id]}
                                    </span>
                                  )}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ─── CRM visiteurs ─── */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Visiteurs du chat — CRM</h3>
              <span className="text-[10px] text-zinc-500 ml-auto">{chatVisitors.length} enregistré{chatVisitors.length > 1 ? "s" : ""}</span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
              Coordonnées collectées via la demande de handoff du chat. Rappel/relance manuel possible par email ou Telegram.
            </p>
            {chatVisitors.length === 0 ? (
              <div className="text-xs text-zinc-500 italic text-center py-4">Aucun visiteur enregistré.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                    <tr>
                      <th className="text-left py-2 pr-2">Visiteur</th>
                      <th className="text-left py-2 pr-2">Email</th>
                      <th className="text-left py-2 pr-2">Telegram</th>
                      <th className="text-left py-2 pr-2">Lang</th>
                      <th className="text-right py-2 pr-2">Demandes</th>
                      <th className="text-left py-2">Dernière</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chatVisitors.slice(0, 50).map((v) => (
                      <tr key={v.id || v.email} className="border-b border-white/[0.04]">
                        <td className="py-2 pr-2">{(v.first_name || "") + " " + (v.last_name || "")}</td>
                        <td className="py-2 pr-2">
                          {v.email ? <a href={`mailto:${v.email}`} className="text-cyan-300 hover:underline">{v.email}</a> : "-"}
                        </td>
                        <td className="py-2 pr-2 font-mono text-[11px]">{v.telegram_handle || "-"}</td>
                        <td className="py-2 pr-2 uppercase text-[10px] text-zinc-400">{v.lang || "-"}</td>
                        <td className="py-2 pr-2 text-right font-mono">{v.handoff_count || 0}</td>
                        <td className="py-2 text-[10px] text-zinc-500">{(v.last_seen_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ─── BO Reply Panel (modal) ─── */}
          {chatReplyOpen && (
            <div
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
              onClick={(e) => { if (e.target === e.currentTarget) closeReplyPanel(); }}
              data-testid="bo-reply-modal"
            >
              <div className="bg-[#0f1115] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-violet-300" />
                    <div>
                      <div className="text-sm font-semibold">
                        #{chatReplyOpen} · {chatReplySession?.user_name || "Visiteur"}
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {chatReplySession?.email || chatReplySession?.telegram_handle || ""}
                        {chatReplySession?.claimed_by && (
                          <span className="ml-2 text-emerald-300">
                            ✓ {chatReplySession.claimed_by.startsWith("bo:") ? `BO ${chatReplySession.claimed_by.slice(3)}` : chatReplySession.claimed_by}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button onClick={closeReplyPanel} className="p-1.5 rounded-md hover:bg-white/[0.05]" data-testid="bo-reply-close">
                    <XCircle className="h-4 w-4 text-zinc-400" />
                  </button>
                </div>
                <div className="px-5 py-2 border-b border-white/[0.06] bg-white/[0.02]">
                  <button
                    onClick={() => openTicketFromChat(chatReplyOpen)}
                    className="text-[11px] px-2.5 py-1 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25 inline-flex items-center gap-1.5"
                    title="Créer un ticket à partir de cette conversation"
                    data-testid="bo-reply-make-ticket"
                  >
                    <FileText className="h-3 w-3" /> Convertir en ticket
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2 text-sm" data-testid="bo-reply-thread">
                  {chatReplyMsgs.length === 0 && (
                    <div className="text-xs text-zinc-500 italic text-center py-8">Aucun message pour le moment.</div>
                  )}
                  {chatReplyMsgs.map((m, i) => {
                    const isAdmin = m.from === "admin";
                    const isSystem = m.from === "system";
                    const isVisitor = m.from === "visitor";
                    return (
                      <div key={i} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                          isAdmin ? "bg-violet-500/15 border border-violet-400/30 text-violet-50"
                                  : isSystem ? "bg-zinc-800/50 border border-white/[0.06] text-zinc-300 italic text-[11px]"
                                             : "bg-white/[0.04] border border-white/[0.08] text-zinc-100"
                        }`}>
                          {m.kind === "photo" && (m.photo_url || m.photo_data_url) && (
                            <div className="relative inline-block mb-1">
                              <img src={m.photo_url || m.photo_data_url} alt="" className="rounded-md max-h-56 cursor-zoom-in" onClick={() => window.open(m.photo_url || m.photo_data_url, "_blank")} />
                              <a
                                href={m.photo_url || m.photo_data_url}
                                download={m.filename || `photo-${(m.ts || "img").replace(/[^a-zA-Z0-9]/g, "_")}.jpg`}
                                className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/70 text-white hover:bg-black/90"
                                title="Télécharger"
                                data-testid={`bo-photo-download-${i}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </div>
                          )}
                          {m.kind === "photo" && !m.photo_url && !m.photo_data_url && (
                            <span className="text-[11px] opacity-60">📷 Photo (Telegram, non visible ici)</span>
                          )}
                          {m.kind === "file" && (m.file_url || m.file_data_url) && (
                            <a
                              href={m.file_url || m.file_data_url}
                              download={m.filename || "fichier"}
                              className="flex items-center gap-2 px-2.5 py-2 mb-1 rounded-md bg-white/[0.06] border border-white/10 hover:bg-white/[0.1] no-underline"
                              data-testid={`bo-reply-file-${i}`}
                            >
                              <FileText className="h-4 w-4 text-cyan-300 shrink-0" />
                              <div className="min-w-0">
                                <div className="truncate text-[12px] text-zinc-100">{m.filename || "fichier"}</div>
                                <div className="text-[10px] text-zinc-500 uppercase">{m.mime_type || "fichier"}</div>
                              </div>
                              <Download className="h-3 w-3 text-zinc-400 ml-auto" />
                            </a>
                          )}
                          {m.text || m.caption || (m.kind === "photo" ? "📷" : "")}
                          <div className="text-[9px] opacity-60 mt-1 text-right">
                            {(m.ts || "").slice(11, 16)}
                            {isVisitor && " · visiteur"}
                            {isAdmin && m.admin_chat_id && ` · ${m.admin_chat_id}`}
                            {isSystem && m.kind && ` · ${m.kind}`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {chatReplyErr && (
                  <div className="px-5 py-2 text-xs text-red-300 bg-red-500/10 border-t border-red-500/20">{chatReplyErr}</div>
                )}
                <div className="px-5 py-3 border-t border-white/[0.06] flex items-end gap-2">
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                    id="bo-reply-file-input"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickAttachment(f); e.target.value = ""; }}
                    data-testid="bo-reply-file-picker"
                  />
                  <button
                    onClick={() => document.getElementById("bo-reply-file-input")?.click()}
                    disabled={chatReplyBusy || chatReplyAttachBusy || chatReplySession?.status === "closed"}
                    className="p-2 rounded-lg bg-white/[0.03] border border-white/10 hover:bg-white/[0.07] disabled:opacity-40"
                    title="Joindre une image ou un document (max 10 MB)"
                    data-testid="bo-reply-attach"
                  >
                    {chatReplyAttachBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4 text-zinc-300" />}
                  </button>
                  <textarea
                    rows={2}
                    value={chatReplyInput}
                    onChange={(e) => setChatReplyInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                    }}
                    placeholder={chatReplySession?.status === "closed" ? "Session fermée." : "Votre réponse… (Entrée pour envoyer, Shift+Entrée nouvelle ligne)"}
                    disabled={chatReplyBusy || chatReplySession?.status === "closed"}
                    className="flex-1 px-3 py-2 rounded-lg text-sm bg-white/[0.03] border border-white/10 focus:border-violet-400/50 outline-none resize-none"
                    data-testid="bo-reply-input"
                  />
                  <button
                    onClick={() => sendReply()}
                    disabled={chatReplyBusy || !chatReplyInput.trim() || chatReplySession?.status === "closed"}
                    className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="bo-reply-send"
                  >
                    {chatReplyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Envoyer
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TICKETS ──────────────────────────────────────────── */}
      {tab === "tickets" && (
        <div className="space-y-4" data-testid="admin-tickets">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <FileText className="h-4 w-4 text-cyan-300" /> Tickets / Réclamations
              </h2>
              <button
                onClick={() => { setTicketDraft({ subject: "", description: "", customer_name: "", customer_email: "", customer_phone: "", category: "complaint", priority: "normal", session_handoff_id: "" }); setTicketErr(""); setTicketModalOpen(true); }}
                className="btn-primary px-3 py-2 rounded-lg text-sm inline-flex items-center gap-1.5"
                data-testid="ticket-create-btn"
              >
                <Sparkles className="h-3.5 w-3.5" /> Nouveau ticket
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-3 items-end">
              <select value={ticketsFilter.status} onChange={(e) => setTicketsFilter({ ...ticketsFilter, status: e.target.value })}
                className="px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10 text-xs"
                data-testid="ticket-filter-status">
                <option value="all">Tous statuts</option>
                {TICKET_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <select value={ticketsFilter.category} onChange={(e) => setTicketsFilter({ ...ticketsFilter, category: e.target.value })}
                className="px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10 text-xs">
                <option value="all">Toutes catégories</option>
                {TICKET_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <select value={ticketsFilter.priority} onChange={(e) => setTicketsFilter({ ...ticketsFilter, priority: e.target.value })}
                className="px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10 text-xs">
                <option value="all">Toutes priorités</option>
                {TICKET_PRIOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <input value={ticketsFilter.q} onChange={(e) => setTicketsFilter({ ...ticketsFilter, q: e.target.value })}
                placeholder="Recherche (sujet, email, ID…)"
                className="flex-1 min-w-[200px] px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10 text-xs"
                onKeyDown={(e) => { if (e.key === "Enter") loadTickets(); }} />
              <button onClick={loadTickets} className="btn-ghost px-3 py-1.5 rounded-md text-xs">Filtrer</button>
            </div>

            {tickets.length === 0 ? (
              <div className="text-center text-zinc-500 py-10 text-sm">Aucun ticket.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-zinc-500 uppercase tracking-wider border-b border-white/[0.06]">
                      <th className="py-2 pr-2">ID</th>
                      <th className="py-2 pr-2">Sujet</th>
                      <th className="py-2 pr-2">Client</th>
                      <th className="py-2 pr-2">Catégorie</th>
                      <th className="py-2 pr-2">Priorité</th>
                      <th className="py-2 pr-2">Statut</th>
                      <th className="py-2 pr-2">Avis</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => {
                      const stCfg = TICKET_STATUSES.find((s) => s.id === t.status) || {};
                      const prCfg = TICKET_PRIOS.find((p) => p.id === t.priority) || {};
                      const catLbl = (TICKET_CATS.find((c) => c.id === t.category) || {}).label || t.category;
                      return (
                        <tr key={t.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]" data-testid={`ticket-row-${t.id}`}>
                          <td className="py-2 pr-2 font-mono text-cyan-300">{t.id}</td>
                          <td className="py-2 pr-2 max-w-[260px] truncate">{t.subject}</td>
                          <td className="py-2 pr-2 text-zinc-400">{t.customer_name || "—"}<br/><span className="text-[10px] text-zinc-500">{t.customer_email || ""}</span></td>
                          <td className="py-2 pr-2">{catLbl}</td>
                          <td className="py-2 pr-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase bg-${prCfg.color}-400/15 text-${prCfg.color}-300 border border-${prCfg.color}-400/30`}>{prCfg.label || t.priority}</span>
                          </td>
                          <td className="py-2 pr-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase bg-${stCfg.color}-400/15 text-${stCfg.color}-300 border border-${stCfg.color}-400/30`}>{stCfg.label || t.status}</span>
                          </td>
                          <td className="py-2 pr-2 text-amber-300 font-mono">{t.satisfaction ? `${t.satisfaction.rating}/5 ★` : "—"}</td>
                          <td className="py-2 text-right">
                            <button onClick={() => openTicketDetail(t.id)} className="text-[10px] px-2 py-1 rounded-md bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25" data-testid={`ticket-open-${t.id}`}>
                              Ouvrir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Modal Création */}
          {ticketModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setTicketModalOpen(false); }} data-testid="ticket-create-modal">
              <div className="bg-[#0f1115] border border-white/10 rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                  <h3 className="text-sm font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-cyan-300" /> Nouveau ticket {ticketDraft.session_handoff_id && <span className="text-[10px] text-violet-300">depuis chat #{ticketDraft.session_handoff_id}</span>}</h3>
                  <button onClick={() => setTicketModalOpen(false)} className="p-1.5 rounded-md hover:bg-white/[0.05]"><XCircle className="h-4 w-4 text-zinc-400" /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <select value={ticketDraft.category} onChange={(e) => setTicketDraft({ ...ticketDraft, category: e.target.value })} className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" data-testid="ticket-draft-category">
                      {TICKET_CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <select value={ticketDraft.priority} onChange={(e) => setTicketDraft({ ...ticketDraft, priority: e.target.value })} className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm">
                      {TICKET_PRIOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>
                  <input value={ticketDraft.subject} onChange={(e) => setTicketDraft({ ...ticketDraft, subject: e.target.value })} placeholder="Sujet de la demande *" className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" data-testid="ticket-draft-subject" />
                  <textarea rows={4} value={ticketDraft.description} onChange={(e) => setTicketDraft({ ...ticketDraft, description: e.target.value })} placeholder="Description détaillée…" className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" data-testid="ticket-draft-description" />
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Client</div>
                  <div className="grid grid-cols-2 gap-3">
                    <input value={ticketDraft.customer_name} onChange={(e) => setTicketDraft({ ...ticketDraft, customer_name: e.target.value })} placeholder="Nom" className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" data-testid="ticket-draft-customer-name" />
                    <input type="email" value={ticketDraft.customer_email} onChange={(e) => setTicketDraft({ ...ticketDraft, customer_email: e.target.value })} placeholder="Email *" className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" data-testid="ticket-draft-customer-email" />
                  </div>
                  <input value={ticketDraft.customer_phone} onChange={(e) => setTicketDraft({ ...ticketDraft, customer_phone: e.target.value })} placeholder="Téléphone (optionnel)" className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-sm" />
                  {ticketErr && <div className="text-xs text-red-300">{ticketErr}</div>}
                </div>
                <div className="px-5 py-3 border-t border-white/[0.06] flex justify-end gap-2">
                  <button onClick={() => setTicketModalOpen(false)} className="btn-ghost px-3 py-2 rounded-lg text-sm">Annuler</button>
                  <button onClick={createTicket} disabled={ticketBusy || !ticketDraft.subject.trim()} className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="ticket-draft-save">
                    {ticketBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Créer le ticket
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal Détail */}
          {ticketDetail && (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) setTicketDetail(null); }} data-testid="ticket-detail-modal">
              <div className="bg-[#0f1115] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                  <div>
                    <div className="text-sm font-semibold font-mono text-cyan-300">{ticketDetail.id}</div>
                    <div className="text-xs text-zinc-400 truncate max-w-[400px]">{ticketDetail.subject}</div>
                  </div>
                  <button onClick={() => setTicketDetail(null)} className="p-1.5 rounded-md hover:bg-white/[0.05]"><XCircle className="h-4 w-4 text-zinc-400" /></button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Statut</div>
                      <select value={ticketDetail.status} onChange={(e) => updateTicket(ticketDetail.id, { status: e.target.value })} className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10" data-testid="ticket-detail-status">
                        {TICKET_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Priorité</div>
                      <select value={ticketDetail.priority} onChange={(e) => updateTicket(ticketDetail.id, { priority: e.target.value })} className="w-full px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/10">
                        {TICKET_PRIOS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-400 grid grid-cols-2 gap-2">
                    <div><b>Client :</b> {ticketDetail.customer_name || "—"}</div>
                    <div><b>Email :</b> {ticketDetail.customer_email || "—"}</div>
                    <div><b>Téléphone :</b> {ticketDetail.customer_phone || "—"}</div>
                    <div><b>Créé par :</b> {ticketDetail.created_by}</div>
                    <div><b>Catégorie :</b> {(TICKET_CATS.find((c) => c.id === ticketDetail.category) || {}).label}</div>
                    <div><b>Créé le :</b> {(ticketDetail.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    {ticketDetail.session_handoff_id && <div className="col-span-2"><b>Issue du chat :</b> #{ticketDetail.session_handoff_id}</div>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Description</div>
                    <div className="text-xs bg-white/[0.03] border border-white/[0.06] rounded p-3 whitespace-pre-wrap">{ticketDetail.description || "(aucune)"}</div>
                  </div>
                  {ticketDetail.satisfaction && (
                    <div className="bg-amber-400/5 border border-amber-400/30 rounded-xl p-3" data-testid="ticket-detail-satisfaction">
                      <div className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-1">Avis client</div>
                      <div className="text-sm text-amber-200">⭐ {ticketDetail.satisfaction.rating} / 5</div>
                      {ticketDetail.satisfaction.comment && <div className="text-xs text-zinc-300 mt-1.5 italic">« {ticketDetail.satisfaction.comment} »</div>}
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Commentaires internes ({(ticketDetail.comments || []).length})</div>
                    <div className="space-y-2">
                      {(ticketDetail.comments || []).map((c, i) => (
                        <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded p-2.5 text-xs">
                          <div className="flex justify-between text-[10px] text-zinc-500 mb-1">
                            <span className="text-cyan-300">{c.author}</span>
                            <span>{(c.ts || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                          <div className="whitespace-pre-wrap text-zinc-200">{c.text}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <input value={ticketCommentDraft} onChange={(e) => setTicketCommentDraft(e.target.value)} placeholder="Ajouter un commentaire interne…" className="flex-1 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-xs" onKeyDown={(e) => { if (e.key === "Enter") addTicketComment(); }} data-testid="ticket-comment-input" />
                      <button onClick={addTicketComment} className="btn-ghost px-3 py-2 rounded-lg text-xs" data-testid="ticket-comment-send">Ajouter</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── CLOUD COPYTRADING ─────────────────────────────── */}
      {tab === "copytrading" && (
        <CloudCopytradingTab token={token} />
      )}

      {/* ─── SETTINGS ───────────────────────────────────────── */}
      {tab === "settings" && (
        <div className="space-y-6" data-testid="admin-settings">
          {/* Documentation / Manuel des procédures */}
          <div className="glass rounded-2xl p-6" data-testid="admin-docs-card">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Documentation Back Office</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Manuel officiel des procédures admin (PDF, ~57&nbsp;Ko, 19&nbsp;pages, sommaire cliquable). À lire intégralement avant la première session de traitement.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="/docs/Manuel_Procedures_BackOffice_DrTradeBots_v1.0.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"
                data-testid="admin-docs-manual-download"
              >
                <Download className="h-4 w-4" />
                Télécharger le Manuel des Procédures (v1.0)
              </a>
              <span className="text-[11px] text-zinc-500">
                Couvre&nbsp;: Connexion/2FA · Commandes · Licences · Affiliés · Payouts · Relances auto · Bulk-notify EA · FAQ admin · Règles d'or
              </span>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
          {/* Change password */}
          <form onSubmit={changePassword} className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Changer le mot de passe admin</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Le nouveau mot de passe est persistant en base. Toutes les autres sessions admin seront déconnectées au prochain rafraîchissement.
            </p>
            <div className="space-y-3">
              <input type="password" placeholder="Mot de passe actuel"
                value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)}
                className="input" autoComplete="current-password"
                data-testid="admin-pwd-current" />
              <input type="password" placeholder="Nouveau mot de passe (min. 8 caractères)"
                value={pwdNew} onChange={(e) => setPwdNew(e.target.value)}
                className="input" autoComplete="new-password"
                data-testid="admin-pwd-new" />
              <input type="password" placeholder="Confirmation du nouveau mot de passe"
                value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)}
                className="input" autoComplete="new-password"
                data-testid="admin-pwd-confirm" />
            </div>
            {pwdMsg.text && (
              <div className={`text-xs mt-3 ${pwdMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                {pwdMsg.text}
              </div>
            )}
            <button type="submit" className="btn-primary mt-4 w-full py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
              data-testid="admin-pwd-submit">
              <Save className="h-4 w-4" /> Enregistrer le nouveau mot de passe
            </button>
          </form>

          {/* Telegram bot */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Send className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Changement par Telegram</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Vous pouvez aussi changer le mot de passe en envoyant la commande suivante dans le chat
              Telegram autorisé (TELEGRAM_CHAT_ID configuré côté serveur) :
            </p>
            <code className="block bg-black/30 border border-white/[0.06] rounded-lg px-3 py-2 text-xs font-mono text-cyan-200 mb-4 break-all"
              data-testid="admin-tg-cmd">
              /setadminpwd &lt;mot_de_passe_actuel&gt; &lt;nouveau_mot_de_passe&gt;
            </code>
            <div className="text-[11px] text-zinc-500 leading-relaxed mb-5 space-y-1">
              <div>• Seul le chat_id configuré (TELEGRAM_CHAT_ID) est autorisé.</div>
              <div>• Le bot répond par confirmation ou erreur dans Telegram.</div>
              <div>• Pensez à supprimer le message après usage pour la sécurité.</div>
            </div>
            <button onClick={registerTelegramWebhook}
              className="btn-ghost w-full py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
              data-testid="admin-tg-register-webhook">
              <Send className="h-4 w-4" /> Enregistrer le webhook Telegram
            </button>
            {tgMsg.text && (
              <div className={`text-xs mt-3 break-all ${tgMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                {tgMsg.text}
              </div>
            )}
          </div>

          {/* 2FA configuration */}
          <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-2fa-section">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="h-4 w-4 text-cyan-400" />
              <h3 className="text-base font-semibold">Double authentification (2FA)</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
              Ajoutez un second facteur (application d'authentification ou email) pour protéger votre compte.
              {me?.role === "assistant" && <span className="text-amber-300"> Obligatoire pour les comptes assistants.</span>}
            </p>

            {!showTwofaWizard && (
              <div className="flex items-center gap-3 flex-wrap mb-3">
                {me?.twofa?.enabled ? (
                  <>
                    <span className="text-xs font-mono bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 px-2.5 py-1.5 rounded inline-flex items-center gap-1.5"
                      data-testid="admin-2fa-status-on">
                      <ShieldCheck className="h-3 w-3" />
                      {me.twofa.method === "email" ? (
                        <>Email — {me.twofa.email_masked || "configuré"}</>
                      ) : (
                        <><Smartphone className="h-3 w-3" /> Google Authenticator</>
                      )}
                    </span>
                    <button onClick={() => setShowTwofaWizard(true)}
                      className="btn-ghost px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1.5"
                      data-testid="admin-2fa-change-method">
                      <RefreshCw className="h-3 w-3" /> Changer la méthode
                    </button>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-mono bg-amber-400/10 border border-amber-400/30 text-amber-200 px-2.5 py-1.5 rounded inline-flex items-center gap-1.5"
                      data-testid="admin-2fa-status-off">
                      <ShieldOff className="h-3 w-3" /> Non activée
                    </span>
                    <button onClick={() => setShowTwofaWizard(true)}
                      className="btn-primary px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1.5"
                      data-testid="admin-2fa-enable-btn">
                      <ShieldCheck className="h-3 w-3" /> Activer la 2FA
                    </button>
                  </>
                )}
              </div>
            )}

            {showTwofaWizard && (
              <div className="mt-3">
                <TwoFactorWizard
                  headers={headers}
                  onComplete={onWizardComplete}
                  onCancel={() => setShowTwofaWizard(false)}
                />
              </div>
            )}

            {me?.twofa?.enabled && !me?.twofa?.enforced && !showTwofaWizard && (
              <form onSubmit={disable2FA} className="mt-4 pt-4 border-t border-white/[0.06]">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Désactiver la 2FA</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                    placeholder="Mot de passe actuel (confirmation)"
                    className="input flex-1 min-w-[240px]" data-testid="admin-2fa-disable-pwd" />
                  <button type="submit" disabled={!disablePwd}
                    className="btn-ghost px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1.5"
                    data-testid="admin-2fa-disable-btn">
                    <ShieldOff className="h-3 w-3" /> Désactiver
                  </button>
                </div>
              </form>
            )}

            {twofaSettingsMsg.text && (
              <div className={`text-xs mt-3 ${twofaSettingsMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                {twofaSettingsMsg.text}
              </div>
            )}
          </div>

          {/* Admin users & invitations — superadmin only */}
          {me?.role === "superadmin" && (
            <>
              <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-users-section">
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="h-4 w-4 text-cyan-400" />
                  <h3 className="text-base font-semibold">Accès administrateurs</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                  Liste des comptes admin actifs. Un <b>assistant</b> accède aux commandes mais ne peut pas gérer les autres accès.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Rôle</th>
                        <th className="px-3 py-2 text-left">2FA</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-left">Créé le</th>
                        <th className="px-3 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.email} className="border-t border-white/[0.04]" data-testid={`admin-user-row-${u.email}`}>
                          <td className="px-3 py-2.5 font-mono text-xs">
                            {u.email}
                            {u.email === me.email && <span className="ml-2 text-[10px] text-cyan-300">(vous)</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded ${
                              u.role === "superadmin" ? "bg-cyan-400/15 text-cyan-300" : "bg-amber-400/15 text-amber-300"
                            }`}>{u.role}</span>
                          </td>
                          <td className="px-3 py-2.5 text-xs">
                            {u.twofa?.enabled ? (
                              <span className="text-emerald-300 inline-flex items-center gap-1" data-testid={`admin-user-2fa-on-${u.email}`}>
                                <ShieldCheck className="h-3 w-3" />
                                {u.twofa.method === "email" ? "Email" : "TOTP"}
                              </span>
                            ) : (
                              <span className={u.twofa?.enforced ? "text-red-300 inline-flex items-center gap-1" : "text-zinc-500 inline-flex items-center gap-1"}
                                data-testid={`admin-user-2fa-off-${u.email}`}>
                                <ShieldOff className="h-3 w-3" /> {u.twofa?.enforced ? "requise" : "off"}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs">
                            {u.active ? (
                              <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Actif</span>
                            ) : (
                              <span className="text-zinc-500 inline-flex items-center gap-1"><XCircle className="h-3 w-3" />Désactivé</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-[11px] text-zinc-500 font-mono">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-3 py-2.5">
                            {u.email !== me.email && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={() => toggleUserActive(u.email, u.active)}
                                  className="text-xs px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08]"
                                  data-testid={`admin-user-toggle-${u.email}`}>
                                  {u.active ? "Désactiver" : "Activer"}
                                </button>
                                {u.twofa?.enabled && (
                                  <button onClick={() => resetUserTwoFactor(u.email)}
                                    className="text-xs px-2 py-1 rounded bg-amber-400/10 hover:bg-amber-400/20 text-amber-200"
                                    data-testid={`admin-user-reset2fa-${u.email}`}>
                                    Reset 2FA
                                  </button>
                                )}
                                <button onClick={() => deleteUser(u.email)}
                                  className="text-xs px-2 py-1 rounded bg-red-400/10 hover:bg-red-400/20 text-red-300"
                                  data-testid={`admin-user-delete-${u.email}`}>
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500">Aucun compte</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-invitations-section">
                <div className="flex items-center gap-2 mb-1">
                  <Send className="h-4 w-4 text-cyan-400" />
                  <h3 className="text-base font-semibold">Inviter un nouvel accès</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                  Générez un lien d'invitation (valide 48 h). L'invité choisit son email et son mot de passe.
                  <br />
                  <span className="text-cyan-300/80">💡 Renseignez l'email du destinataire ci-dessous pour <b>générer ET envoyer</b> automatiquement l'invitation en un clic.</span>
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Rôle *</div>
                    <select value={newInviteRole} onChange={(e) => setNewInviteRole(e.target.value)}
                      className="input w-full" data-testid="admin-invite-role"
                      disabled={newInviteBusy}>
                      <option value="assistant">Assistant</option>
                      <option value="superadmin">Superadmin</option>
                    </select>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Email du destinataire <span className="text-zinc-600">(facultatif)</span></div>
                    <input type="email"
                      value={newInviteEmail}
                      onChange={(e) => setNewInviteEmail(e.target.value)}
                      placeholder="prenom@drtradebots.com"
                      className="input w-full"
                      disabled={newInviteBusy}
                      data-testid="admin-invite-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Nom du destinataire <span className="text-zinc-600">(facultatif, utilisé dans le mail)</span></div>
                    <input type="text"
                      value={newInviteName}
                      onChange={(e) => setNewInviteName(e.target.value)}
                      placeholder="Michel Dupont"
                      className="input w-full"
                      disabled={newInviteBusy}
                      data-testid="admin-invite-name" />
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <button onClick={createInvitation}
                    disabled={newInviteBusy}
                    className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="admin-invite-create">
                    {newInviteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {newInviteEmail.trim() ? "Générer et envoyer" : "Générer un lien"}
                  </button>
                  {newInviteFeedback.text && (
                    <div className={`text-xs px-3 py-1.5 rounded ${
                      newInviteFeedback.kind === "ok" ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-300"
                        : newInviteFeedback.kind === "warn" ? "bg-amber-400/10 border border-amber-400/30 text-amber-200"
                        : "bg-rose-500/10 border border-rose-500/30 text-rose-300"
                    }`}
                      data-testid="admin-invite-feedback">
                      {newInviteFeedback.text}
                    </div>
                  )}
                </div>
                {lastInviteUrl && (
                  <div className="mb-4 p-3 bg-emerald-400/5 border border-emerald-400/30 rounded-lg" data-testid="admin-invite-new-url">
                    <div className="text-[11px] text-emerald-300 mb-1.5 uppercase tracking-wider">Lien généré</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono bg-black/30 px-2 py-1.5 rounded overflow-x-auto">
                        {lastInviteUrl}
                      </code>
                      <button onClick={copyInviteUrl}
                        className="shrink-0 px-3 py-1.5 rounded bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-xs inline-flex items-center gap-1"
                        data-testid="admin-invite-copy">
                        <Copy className="h-3 w-3" /> Copier
                      </button>
                    </div>
                  </div>
                )}
                <div className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Invitations en cours</div>
                <div className="space-y-2">
                  {invites.filter((i) => !i.used).map((i) => (
                    <div key={i.token} className="flex items-center gap-2 p-2.5 bg-white/[0.02] border border-white/[0.06] rounded-md text-xs"
                      data-testid={`admin-invite-row-${i.token.slice(0,8)}`}>
                      <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                        i.role === "superadmin" ? "bg-cyan-400/15 text-cyan-300" : "bg-amber-400/15 text-amber-300"
                      }`}>{i.role}</span>
                      <code className="flex-1 text-zinc-400 truncate">{i.token}</code>
                      <span className="text-zinc-500 font-mono">expire {new Date(i.expires_at).toLocaleString()}</span>
                      {i.last_emailed_to && (
                        <span className="text-emerald-300 text-[10px]" title={`Envoyé à ${i.last_emailed_to} le ${new Date(i.last_emailed_at).toLocaleString()}`}>
                          ✉ {i.last_emailed_to}
                        </span>
                      )}
                      <button onClick={() => revokeInvitation(i.token)}
                        className="text-red-300 hover:text-red-200 text-[10px] uppercase"
                        data-testid={`admin-invite-revoke-${i.token.slice(0,8)}`}>
                        Révoquer
                      </button>
                    </div>
                  ))}
                  {invites.filter((i) => !i.used).length === 0 && (
                    <div className="text-xs text-zinc-600 italic">Aucune invitation en attente.</div>
                  )}
                </div>
              </div>

              {/* Payout threshold — superadmin only */}
              <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-payout-threshold-section">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-4 w-4 text-cyan-400" />
                  <h3 className="text-base font-semibold">Seuil minimum de retrait (affiliés)</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                  Montant minimum en XOF qu'un affilié doit avoir accumulé en commissions <b>en attente</b> pour déclencher une demande de paiement.
                  Les renouvellements comptent. Les demandes rejetées remettent les commissions dans le pot.
                </p>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Seuil (XOF)</div>
                    <input type="number" min="0" max="10000000" step="500"
                      value={settingsDraft}
                      onChange={(e) => setSettingsDraft(e.target.value)}
                      className="input w-40 font-mono" data-testid="admin-settings-payout-min-input" />
                  </div>
                  <button onClick={saveAppSettings}
                    className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"
                    data-testid="admin-settings-payout-min-save">
                    <Save className="h-4 w-4" /> Enregistrer
                  </button>
                  <div className="text-[11px] text-zinc-500 font-mono">
                    Actuel : <span className="text-cyan-300">{(appSettings.payout_min_xof ?? 0).toLocaleString("fr-FR").replaceAll(",", " ")} XOF</span>
                  </div>
                </div>
                {settingsMsg.text && (
                  <div className={`text-xs mt-3 ${settingsMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                    data-testid="admin-settings-payout-min-msg">
                    {settingsMsg.text}
                  </div>
                )}
              </div>

              {/* ─── Renewal reminder — superadmin only ─── */}
              {me?.role === "superadmin" && (
                <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-renewal-section">
                  <div className="flex items-center gap-2 mb-1">
                    <RefreshCw className="h-4 w-4 text-amber-400" />
                    <h3 className="text-base font-semibold">Relance de renouvellement automatique</h3>
                  </div>
                  <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                    Email automatique envoyé aux clients <b>N jours avant l'expiration</b> de leur licence,
                    avec un code de remise auto-généré pour les inciter à renouveler. Idempotent : un même
                    client n'est notifié qu'une fois par cycle d'expiration.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer sm:col-span-2">
                      <input type="checkbox"
                        checked={!!renewalDraft.renewal_reminder_enabled}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_reminder_enabled: e.target.checked })}
                        data-testid="admin-renewal-enabled" />
                      <span className="font-semibold">
                        {renewalDraft.renewal_reminder_enabled
                          ? <span className="text-emerald-300">✓ Relance activée</span>
                          : <span className="text-zinc-400">Relance désactivée</span>}
                      </span>
                    </label>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Jours avant expiration
                      </div>
                      <input type="number" min="1" max="60"
                        value={renewalDraft.renewal_reminder_days_before ?? 7}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_reminder_days_before: e.target.value })}
                        className="input w-full font-mono"
                        data-testid="admin-renewal-days-before" />
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Remise (%)
                      </div>
                      <input type="number" min="1" max="90"
                        value={renewalDraft.renewal_discount_percent ?? 20}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_discount_percent: e.target.value })}
                        className="input w-full font-mono"
                        data-testid="admin-renewal-discount-pct" />
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Validité du code (jours)
                      </div>
                      <input type="number" min="1" max="90"
                        value={renewalDraft.renewal_discount_validity_days ?? 14}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_discount_validity_days: e.target.value })}
                        className="input w-full font-mono"
                        data-testid="admin-renewal-validity-days" />
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Sujet de l'email
                      </div>
                      <input type="text"
                        value={renewalDraft.renewal_email_subject ?? ""}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_email_subject: e.target.value })}
                        className="input w-full"
                        data-testid="admin-renewal-subject" />
                    </div>

                    <div className="sm:col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Corps du mail (placeholders : {"{name}"}, {"{expiry_date}"}, {"{days_left}"}, {"{discount_percent}"}, {"{promo_code}"}, {"{promo_valid_until}"})
                      </div>
                      <textarea rows="9"
                        value={renewalDraft.renewal_email_body ?? ""}
                        onChange={(e) => setRenewalDraft({ ...renewalDraft, renewal_email_body: e.target.value })}
                        className="input w-full font-mono text-xs"
                        data-testid="admin-renewal-body" />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <button onClick={saveRenewalSettings} disabled={renewalBusy}
                      className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="admin-renewal-save">
                      <Save className="h-4 w-4" /> Enregistrer
                    </button>
                    <button onClick={runRenewalReminders} disabled={renewalBusy || !appSettings?.renewal_reminder_enabled}
                      className="px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 bg-amber-400/15 border border-amber-400/40 text-amber-300 hover:bg-amber-400/25 disabled:opacity-40"
                      data-testid="admin-renewal-run">
                      <RefreshCw className={`h-4 w-4 ${renewalBusy ? "animate-spin" : ""}`} />
                      Lancer le scan maintenant
                    </button>
                  </div>
                  {renewalMsg.text && (
                    <div className={`text-xs mt-3 ${renewalMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                      {renewalMsg.text}
                    </div>
                  )}
                  {renewalRunMsg.text && (
                    <div className={`text-xs mt-2 ${renewalRunMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                      {renewalRunMsg.text}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Paniers abandonnés — superadmin only ─── */}
              {me?.role === "superadmin" && (
                <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-abandon-section">
                  <div className="flex items-center gap-2 mb-1">
                    <RefreshCw className="h-4 w-4 text-orange-400" />
                    <h3 className="text-base font-semibold">Relance paniers abandonnés</h3>
                  </div>
                  <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                    Email envoyé automatiquement aux clients dont le paiement (CinetPay/USDT) est resté en attente
                    (statut <code>awaiting_payment</code>). <b>Jusqu'à 3 relances</b> espacées selon les intervalles
                    configurés ci-dessous. Un <b>code promo unique</b> n'est généré qu'à partir de la relance numérotée
                    « Promo à partir de la relance n° X » (par défaut <code>3</code>, soit la dernière).
                  </p>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        <span>Remise offerte (%)</span>
                        <span className="text-orange-300 font-mono text-sm normal-case tracking-normal">
                          -{abandonDraft.abandonment_discount_percent}%
                        </span>
                      </div>
                      <input type="range" min="0" max="90" step="1"
                        value={abandonDraft.abandonment_discount_percent ?? 10}
                        onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_discount_percent: e.target.value })}
                        className="w-full accent-orange-400"
                        data-testid="admin-abandon-discount-pct" />
                      <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                        <span>0 %</span><span>45 %</span><span>90 %</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        <span>Validité du code (jours)</span>
                        <span className="text-orange-300 font-mono text-sm normal-case tracking-normal">
                          {abandonDraft.abandonment_validity_days} j
                        </span>
                      </div>
                      <input type="range" min="1" max="60" step="1"
                        value={abandonDraft.abandonment_validity_days ?? 7}
                        onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_validity_days: e.target.value })}
                        className="w-full accent-orange-400"
                        data-testid="admin-abandon-validity-days" />
                      <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
                        <span>1 j</span><span>30 j</span><span>60 j</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Promo à partir de la relance n°</div>
                      <select
                        value={abandonDraft.abandonment_promo_starts_at_reminder ?? 3}
                        onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_promo_starts_at_reminder: e.target.value })}
                        className="input w-full"
                        data-testid="admin-abandon-promo-starts">
                        <option value="1">1 — Dès la 1ère relance</option>
                        <option value="2">2 — À partir de la 2ème</option>
                        <option value="3">3 — Uniquement la dernière (recommandé)</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
                      Intervalles entre les relances (en minutes)
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <label className="block">
                        <span className="text-[10px] text-zinc-500">Avant relance n°1 (depuis création)</span>
                        <input type="number" min="5" max="10080" step="5"
                          value={abandonDraft.abandonment_interval_1_min ?? 30}
                          onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_interval_1_min: e.target.value })}
                          className="input w-full mt-1"
                          data-testid="admin-abandon-interval-1" />
                        <span className="text-[10px] text-zinc-500 mt-0.5 block">défaut : 30 min</span>
                      </label>
                      <label className="block">
                        <span className="text-[10px] text-zinc-500">Entre n°1 et n°2</span>
                        <input type="number" min="5" max="10080" step="5"
                          value={abandonDraft.abandonment_interval_2_min ?? 1440}
                          onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_interval_2_min: e.target.value })}
                          className="input w-full mt-1"
                          data-testid="admin-abandon-interval-2" />
                        <span className="text-[10px] text-zinc-500 mt-0.5 block">défaut : 1 440 min (24 h)</span>
                      </label>
                      <label className="block">
                        <span className="text-[10px] text-zinc-500">Entre n°2 et n°3</span>
                        <input type="number" min="5" max="20160" step="5"
                          value={abandonDraft.abandonment_interval_3_min ?? 2880}
                          onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_interval_3_min: e.target.value })}
                          className="input w-full mt-1"
                          data-testid="admin-abandon-interval-3" />
                        <span className="text-[10px] text-zinc-500 mt-0.5 block">défaut : 2 880 min (48 h)</span>
                      </label>
                    </div>
                    <label className="block mt-3">
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500">Âge maximum d'une commande (en heures) — au-delà, marquée « abandoned »</span>
                      <input type="number" min="1" max="720" step="1"
                        value={abandonDraft.abandonment_max_age_hours ?? 168}
                        onChange={(e) => setAbandonDraft({ ...abandonDraft, abandonment_max_age_hours: e.target.value })}
                        className="input w-full mt-1 max-w-[200px]"
                        data-testid="admin-abandon-max-age" />
                      <span className="text-[10px] text-zinc-500 mt-0.5 block">défaut : 168 h (7 jours)</span>
                    </label>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <button onClick={saveAbandonSettings} disabled={abandonBusy}
                      className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="admin-abandon-save">
                      <Save className="h-4 w-4" /> Enregistrer
                    </button>
                    <button onClick={runAbandonedReminders} disabled={abandonBusy}
                      className="px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 bg-orange-400/15 border border-orange-400/40 text-orange-300 hover:bg-orange-400/25 disabled:opacity-40"
                      data-testid="admin-abandon-run">
                      <RefreshCw className={`h-4 w-4 ${abandonBusy ? "animate-spin" : ""}`} />
                      Lancer le scan maintenant
                    </button>
                  </div>
                  {abandonMsg.text && (
                    <div className={`text-xs mt-3 ${abandonMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                      data-testid="admin-abandon-save-msg">
                      {abandonMsg.text}
                    </div>
                  )}
                  {abandonRunMsg.text && (
                    <div className={`text-xs mt-2 ${abandonRunMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}
                      data-testid="admin-abandon-run-msg">
                      {abandonRunMsg.text}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Bulk update notification — superadmin only ─── */}
              {me?.role === "superadmin" && (
                <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-bulk-update-section">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-cyan-400" />
                    <h3 className="text-base font-semibold">Notification de mise à jour groupée</h3>
                  </div>
                  <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                    Quand vous publiez une <b>nouvelle version de l'EA</b> et que les nouveaux <code>.ex5</code>
                    ont été uploadés via l'onglet Commandes (1 par client), utilisez ce formulaire pour
                    notifier tous les clients en une fois. Lien de téléchargement personnalisé inclus
                    automatiquement.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Sujet
                      </div>
                      <input type="text"
                        value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)}
                        className="input w-full" data-testid="admin-bulk-subject" />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Corps (placeholders : {"{name}"}, {"{email}"}, {"{order_short}"}, {"{download_link}"}, {"{mt5_accounts}"}, {"{expiry_date}"})
                      </div>
                      <textarea rows="9"
                        value={bulkBody} onChange={(e) => setBulkBody(e.target.value)}
                        className="input w-full font-mono text-xs"
                        data-testid="admin-bulk-body" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
                        Cible
                      </div>
                      <select value={bulkFilter} onChange={(e) => setBulkFilter(e.target.value)}
                        className="input w-full" data-testid="admin-bulk-filter">
                        <option value="active">Actifs (delivered + compiled, non expirés)</option>
                        <option value="delivered">Delivered uniquement</option>
                        <option value="compiled">Compiled uniquement</option>
                        <option value="all">Tous (sans filtre d'expiration)</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4">
                    <button onClick={() => runBulkNotify(true)} disabled={bulkBusy}
                      className="px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40"
                      data-testid="admin-bulk-dryrun">
                      🔍 Aperçu (dry-run)
                    </button>
                    <button onClick={() => runBulkNotify(false)} disabled={bulkBusy}
                      className="btn-primary px-4 py-2.5 rounded-lg text-sm inline-flex items-center gap-2 disabled:opacity-40"
                      data-testid="admin-bulk-send">
                      📨 Envoyer pour de vrai
                    </button>
                  </div>
                  {bulkMsg.text && (
                    <div className={`text-xs mt-3 ${bulkMsg.kind === "ok" ? "text-emerald-300" : "text-red-300"}`}>
                      {bulkMsg.text}
                    </div>
                  )}
                </div>
              )}

              {/* Auto-reply contact form — superadmin only */}
              {me?.role === "superadmin" && (
                <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-contact-autoreply-section">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-4 w-4 text-emerald-400" />
                    <h3 className="text-base font-semibold">Accusé de réception automatique (formulaire de contact)</h3>
                  </div>
                  <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                    Quand un visiteur soumet un message via la page <code className="text-cyan-300">/contact</code>,
                    un email de remerciement automatique lui est envoyé immédiatement (« Nous avons bien reçu votre message,
                    réponse sous 24 h »). Le contenu est <b>éditable plus bas dans « Modèles d'emails »</b> sous la clé
                    <code className="text-cyan-300 mx-1">contact.autoreply</code> (FR + EN).
                  </p>
                  <label className="inline-flex items-center gap-3 cursor-pointer select-none" data-testid="admin-contact-autoreply-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!appSettings.contact_autoreply_enabled}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        // Optimistic update
                        setAppSettings((s) => ({ ...s, contact_autoreply_enabled: newVal }));
                        try {
                          const { data } = await api.put("/admin/settings", { contact_autoreply_enabled: newVal }, headers);
                          setAppSettings(data.settings || {});
                        } catch (err) {
                          setAppSettings((s) => ({ ...s, contact_autoreply_enabled: !newVal }));
                          alert(err.response?.data?.detail || "Erreur lors de la mise à jour.");
                        }
                      }}
                      className="h-4 w-4 accent-emerald-400"
                      data-testid="admin-contact-autoreply-toggle"
                    />
                    <span className="text-sm">
                      {appSettings.contact_autoreply_enabled ? (
                        <span className="text-emerald-300">✓ Auto-reply activé</span>
                      ) : (
                        <span className="text-zinc-400">Auto-reply désactivé</span>
                      )}
                    </span>
                  </label>
                  <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                    💡 Variables disponibles dans le template : <code className="text-cyan-300">{"{{name}}"}</code>,
                    <code className="text-cyan-300 mx-1">{"{{subject}}"}</code>,
                    <code className="text-cyan-300">{"{{date}}"}</code>,
                    <code className="text-cyan-300 mx-1">{"{{email}}"}</code>.
                  </div>
                </div>
              )}

              {/* Email templates editor — superadmin only */}
              <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="admin-email-templates-section">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4 text-cyan-400" />
                  <h3 className="text-base font-semibold">Modèles d'emails transactionnels</h3>
                </div>
                <p className="text-xs text-zinc-500 mb-5 leading-relaxed">
                  Personnalisez les sujets et contenus des emails envoyés automatiquement (confirmation de commande, demandes de paiement, affiliation…). Les variables entre accolades <code className="text-cyan-300">{"{ ... }"}</code> sont remplacées dynamiquement.
                </p>

                {!editingTpl ? (
                  <div className="space-y-2" data-testid="admin-email-templates-list">
                    {emailTpls.map((tpl) => (
                      <div key={tpl.key}
                        className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg"
                        data-testid={`admin-email-tpl-row-${tpl.key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{tpl.label || tpl.key}</div>
                          <div className="text-[11px] text-zinc-500 font-mono truncate">{tpl.key} · FR: {tpl.subject_fr || "—"}</div>
                        </div>
                        <button onClick={() => setEditingTpl({ ...tpl })}
                          className="text-xs px-3 py-1.5 rounded bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-300 border border-cyan-400/30"
                          data-testid={`admin-email-tpl-edit-${tpl.key}`}>
                          Modifier
                        </button>
                        <button onClick={() => resetEmailTpl(tpl.key)}
                          className="text-xs px-3 py-1.5 rounded bg-amber-400/10 hover:bg-amber-400/20 text-amber-200 border border-amber-400/30"
                          data-testid={`admin-email-tpl-reset-${tpl.key}`}>
                          Reset
                        </button>
                      </div>
                    ))}
                    {emailTpls.length === 0 && (
                      <div className="text-xs text-zinc-600 italic">Aucun modèle chargé.</div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="admin-email-tpl-editor">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] uppercase tracking-wider text-zinc-500">Modèle</span>
                      <code className="text-xs font-mono px-2 py-1 rounded bg-black/30 text-cyan-300">{editingTpl.key}</code>
                      <span className="text-xs text-zinc-400">— {editingTpl.label || ""}</span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Sujet (FR)</div>
                        <input value={editingTpl.subject_fr || ""}
                          onChange={(e) => setEditingTpl({ ...editingTpl, subject_fr: e.target.value })}
                          className="input w-full" data-testid="admin-email-tpl-subject-fr" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Sujet (EN)</div>
                        <input value={editingTpl.subject_en || ""}
                          onChange={(e) => setEditingTpl({ ...editingTpl, subject_en: e.target.value })}
                          className="input w-full" data-testid="admin-email-tpl-subject-en" />
                      </div>
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Corps (FR)</div>
                      <textarea value={editingTpl.body_fr || ""}
                        onChange={(e) => setEditingTpl({ ...editingTpl, body_fr: e.target.value })}
                        rows={10}
                        className="input w-full font-mono text-xs leading-relaxed"
                        data-testid="admin-email-tpl-body-fr" />
                    </div>

                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">Corps (EN)</div>
                      <textarea value={editingTpl.body_en || ""}
                        onChange={(e) => setEditingTpl({ ...editingTpl, body_en: e.target.value })}
                        rows={10}
                        className="input w-full font-mono text-xs leading-relaxed"
                        data-testid="admin-email-tpl-body-en" />
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t border-white/[0.06]">
                      <button onClick={saveEmailTpl}
                        className="btn-primary px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2"
                        data-testid="admin-email-tpl-save">
                        <Save className="h-4 w-4" /> Enregistrer
                      </button>
                      <button onClick={() => setEditingTpl(null)}
                        className="btn-ghost px-4 py-2 rounded-lg text-sm"
                        data-testid="admin-email-tpl-cancel">
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </div>
      )}
      <IdleLogoutWarning
        open={idleWarn}
        remainingMs={idleRemaining}
        onStay={idleStay}
        onLogoutNow={logout}
      />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, accent = "text-cyan-300", testId }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="text-2xl font-bold tracking-tighter font-mono">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">{hint}</div>}
    </div>
  );
}
